import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { useCRM } from '../context/CRMContext';
import { useAuth } from '../context/AuthContext';
import { subscribeLeadNotes, addLeadNote, deleteLeadNote } from '../firebase/firestore';
import PhoneInput from '../components/PhoneInput';
import {
  onSnapshot, doc, collection, query, where, orderBy,
  getDocs, writeBatch, updateDoc, getDoc, setDoc,
  addDoc, deleteDoc, serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase/config';

const SOURCE_ICONS = {
  facebook:  '📘', instagram: '📸', whatsapp:  '💬',
  website:   '🌐', referral:  '🤝', manual:    '✍️',
  walkin:    '🚶', google:    '🔍', linkedin:  '💼',
  youtube:   '▶️',  email:     '📧', organic:   '🌿',
  event:     '🎪', agent:     '👤',
};

const ALL_SOURCES = [
  { value: 'facebook',  label: 'Facebook'   },
  { value: 'instagram', label: 'Instagram'  },
  { value: 'whatsapp',  label: 'WhatsApp'   },
  { value: 'website',   label: 'Website'    },
  { value: 'google',    label: 'Google Ads' },
  { value: 'linkedin',  label: 'LinkedIn'   },
  { value: 'youtube',   label: 'YouTube'    },
  { value: 'email',     label: 'Email'      },
  { value: 'organic',   label: 'Organic'    },
  { value: 'referral',  label: 'Referral'   },
  { value: 'walkin',    label: 'Walk-in'    },
  { value: 'event',     label: 'Event'      },
  { value: 'agent',     label: 'Agent'      },
  { value: 'manual',    label: 'Manual'     },
];

const COUNTRIES = [
  'UK','USA','Canada','Australia','Germany','Ireland',
  'New Zealand','Singapore','Dubai','France','Netherlands',
  'Italy','Spain','Sweden','Norway','Denmark','Japan','Malta',
];

const ALL_INTAKES = [
  'Jan 2025','May 2025','Sep 2025',
  'Jan 2026','May 2026','Sep 2026',
  'Jan 2027','May 2027','Sep 2027',
];

const PAGE_SIZE_OPTIONS = [20, 50, 100, 200, 500];

// ─── COLUMN DEFINITIONS ───────────────────────────────────────────────────────
const ALL_COLUMNS = [
  // Contact Info
  { key: 'firstName',      label: 'First Name',          group: 'contact' },
  { key: 'lastName',       label: 'Last Name',           group: 'contact' },
  { key: 'email',          label: 'Email',               group: 'contact' },
  { key: 'phone',          label: 'Phone',               group: 'contact' },
  { key: 'whatsapp',       label: 'WhatsApp',            group: 'contact' },
  { key: 'city',           label: 'City',                group: 'contact' },
  { key: 'state',          label: 'State / Province',    group: 'contact' },
  { key: 'address',        label: 'Address',             group: 'contact' },
  // Pipeline
  { key: 'stage',          label: 'Stage',               group: 'pipeline' },
  { key: 'source',         label: 'Source',              group: 'pipeline' },
  { key: 'countries',      label: 'Countries',           group: 'pipeline' },
  { key: 'assigned',       label: 'Assigned To',         group: 'pipeline' },
  { key: 'priority',       label: 'Priority',            group: 'pipeline' },
  { key: 'score',          label: 'Score',               group: 'pipeline' },
  // Academic
  { key: 'course',         label: 'Course',              group: 'academic' },
  { key: 'intake',         label: 'Intake',              group: 'academic' },
  { key: 'budget',         label: 'Budget',              group: 'academic' },
  { key: 'qualification',  label: 'Qualification',       group: 'academic' },
  { key: 'school',         label: 'School / College',    group: 'academic' },
  { key: 'grade',          label: 'Grade / Class',       group: 'academic' },
  { key: 'board',          label: 'Board / Curriculum',  group: 'academic' },
  { key: 'graduationYear', label: 'Graduation Year',     group: 'academic' },
  // Personal
  { key: 'dob',            label: 'Date of Birth',       group: 'personal' },
  { key: 'gender',         label: 'Gender',              group: 'personal' },
  { key: 'nationality',    label: 'Nationality',         group: 'personal' },
  { key: 'passport',       label: 'Passport No.',        group: 'personal' },
  // Test Scores
  { key: 'ielts',          label: 'IELTS',               group: 'scores' },
  { key: 'toefl',          label: 'TOEFL',               group: 'scores' },
  { key: 'pte',            label: 'PTE',                 group: 'scores' },
  { key: 'gre',            label: 'GRE',                 group: 'scores' },
  { key: 'gmat',           label: 'GMAT',                group: 'scores' },
  { key: 'sat',            label: 'SAT',                 group: 'scores' },
  { key: 'act',            label: 'ACT',                 group: 'scores' },
  { key: 'gpa',            label: 'GPA',                 group: 'scores' },
  // Parents
  { key: 'parentName',     label: 'Parent Name',         group: 'parents' },
  { key: 'parentPhone',    label: 'Parent Phone',        group: 'parents' },
  { key: 'parentEmail',    label: 'Parent Email',        group: 'parents' },
  { key: 'parentRelation', label: 'Parent Relation',     group: 'parents' },
  // Other
  { key: 'message',        label: 'Message',             group: 'other' },
  // Activity
  { key: 'leadAge',        label: 'Lead Age',            group: 'activity' },
  { key: 'lastActivity',   label: 'Last Activity',       group: 'activity' },
  { key: 'added',          label: 'Date Added',          group: 'activity' },
];
const DEFAULT_COLUMNS = ['stage', 'source', 'countries', 'assigned', 'score', 'priority', 'added'];

// Helper: get display name from lead (handles firstName/lastName split or legacy name)
const getLeadName = (lead) => {
  if (lead.firstName || lead.lastName) {
    return [lead.firstName, lead.lastName].filter(Boolean).join(' ');
  }
  return lead.name || '?';
};

// ─── LEAD SCORE (computed from real data, not random) ────────────────────────
const computeLeadScore = (lead, stages = []) => {
  let s = 0;
  // Profile completeness (40 pts)
  if (lead.email)                                    s += 10;
  if (lead.course)                                   s += 8;
  if (lead.countries?.length || lead.country)        s += 7;
  if (lead.budget)                                   s += 5;
  if (lead.intake)                                   s += 5;
  if (lead.ielts || lead.toefl || lead.pte || lead.qualification) s += 5;
  // Priority (20 pts)
  if (lead.priority === 'hot')       s += 20;
  else if (lead.priority === 'warm') s += 12;
  else                               s += 4;
  // Stage progress (20 pts)
  const idx = stages.findIndex(st => st.id === lead.status);
  if (idx >= 0 && stages.length > 1)
    s += Math.round((idx / (stages.length - 1)) * 20);
  // Recency (20 pts)
  const age = getLeadAge(lead);
  if (age !== null) {
    if (age <= 3)        s += 20;
    else if (age <= 7)   s += 15;
    else if (age <= 30)  s += 10;
    else if (age <= 90)  s += 5;
    else                 s += 2;
  }
  return Math.min(100, s);
};

// Lead age helpers
const getLeadAge = (lead) => {
  const d = getDate(lead.createdAt);
  if (!d) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
};
const leadAgeLabel = (days) => {
  if (days === null || days === undefined) return '—';
  if (days === 0) return 'Today';
  if (days === 1) return '1d';
  if (days < 30) return days + 'd';
  if (days < 365) return Math.floor(days / 30) + 'mo';
  return Math.floor(days / 365) + 'yr';
};
const leadAgeColor = (days) => {
  if (days === null) return '#94a3b8';
  if (days <= 7)  return '#10b981';
  if (days <= 30) return '#f59e0b';
  return '#ef4444';
};

// CSV export utility
const exportToCSV = (rows, pipelineStages, getUserById) => {
  const headers = [
    'Full Name','First Name','Last Name','Phone','WhatsApp','Email',
    'Stage','Source','Countries','Course','Intake','Qualification','Budget','Priority','Score',
    'IELTS','TOEFL','PTE','GRE','GMAT','SAT','ACT','GPA',
    'Date of Birth','Gender','Nationality','Passport No.',
    'City','State','Address',
    'School / College','Grade / Class','Board / Curriculum','Graduation Year',
    'Parent Name','Parent Phone','Parent Email','Parent Relation',
    'Message','Assigned To','Date Added',
  ];
  const data = rows.map(l => {
    const stage    = pipelineStages.find(s => s.id === l.status);
    const user     = getUserById(l.assignedToId);
    const fullName = l.firstName || l.lastName
      ? [l.firstName, l.lastName].filter(Boolean).join(' ')
      : (l.name || '');
    return [
      fullName,
      l.firstName || l.name?.split(' ')[0] || '',
      l.lastName  || l.name?.split(' ').slice(1).join(' ') || '',
      l.phone || '', l.whatsapp || '', l.email || '',
      stage?.name || l.status || '', l.source || '',
      (l.countries || (l.country ? [l.country] : [])).join(';'),
      l.course || '', l.intake || '', l.qualification || '', l.budget || '',
      l.priority || '', l.score || '',
      l.ielts || '', l.toefl || '', l.pte || '', l.gre || '',
      l.gmat || '', l.sat || '', l.act || '', l.gpa || '',
      l.dob || '', l.gender || '', l.nationality || '', l.passport || '',
      l.city || '', l.state || '', l.address || '',
      l.school || '', l.grade || '', l.board || '', l.graduationYear || '',
      l.parentName || '', l.parentPhone || '', l.parentEmail || '', l.parentRelation || '',
      l.message || '',
      user?.name || l.assignedTo || '',
      getDate(l.createdAt)?.toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' }) || '',
    ];
  });
  const csv = [headers, ...data]
    .map(row => row.map(v => '"' + String(v ?? '').replace(/"/g, '""') + '"').join(','))
    .join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }); // BOM for Excel
  const url  = URL.createObjectURL(blob);
  const a    = Object.assign(document.createElement('a'), {
    href: url,
    download: 'leads_' + new Date().toISOString().slice(0, 10) + '.csv',
  });
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Safely convert Firestore Timestamp or string to JS Date
const getDate = (ts) => {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  const d = new Date(ts);
  return isNaN(d) ? null : d;
};

const timeDiff = (ts) => {
  const date = getDate(ts);
  if (!date || date.getTime() < 1000000) return 'just now';
  const diff = Math.round((Date.now() - date.getTime()) / 60000);
  if (diff < 1) return 'just now';
  if (diff < 60) return `${diff}m ago`;
  if (diff < 1440) return `${Math.round(diff / 60)}h ago`;
  if (diff < 43200) return `${Math.round(diff / 1440)}d ago`;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

// ─── DEFAULT VIEW TEMPLATE ────────────────────────────────────────────────────
// Filters are arrays now — empty [] = "all", non-empty = selected subset
const defaultFilters = () => ({
  status: [], source: [], user: [],
  priority: [], campaign: [], search: '', sort: 'newest',
});

// Converts old single-value string format to array (backward compat)
const toArr = (val) => {
  if (!val || val === 'all') return [];
  if (Array.isArray(val)) return val;
  return [val];
};

const loadSavedViews = () => {
  try {
    const raw = localStorage.getItem('crm_saved_views');
    const views = raw ? JSON.parse(raw) : null;
    if (Array.isArray(views) && views.length > 0) return views;
  } catch {}
  return [{ id: 'default', name: 'All Leads', filters: defaultFilters(), columns: DEFAULT_COLUMNS }];
};

const loadActiveViewId = () => {
  try { return localStorage.getItem('crm_active_view_id') || 'default'; } catch { return 'default'; }
};

const loadViewMode = () => {
  try { return localStorage.getItem('crm_view_mode') || 'table'; } catch { return 'table'; }
};

// Returns the saved filters for the currently active view (used to init state)
const loadActiveFilters = () => {
  try {
    const views = loadSavedViews();
    const id    = loadActiveViewId();
    const view  = views.find(v => v.id === id);
    const f     = view?.filters || defaultFilters();
    // Normalise: old saved views used strings ('all' / single id), new format uses arrays
    return {
      status:   toArr(f.status),
      source:   toArr(f.source),
      user:     toArr(f.user),
      priority: toArr(f.priority),
      campaign: toArr(f.campaign),
      search:   f.search || '',
      sort:     f.sort   || 'newest',
    };
  } catch { return defaultFilters(); }
};

// ─── BULK UPLOAD MODAL (with field mapping) ──────────────────────────────────
const CRM_FIELDS = [
  { key: '',              label: '— Skip this column —' },
  { key: 'firstName',     label: 'First Name' },
  { key: 'lastName',      label: 'Last Name' },
  { key: 'name',          label: 'Full Name (legacy)' },
  { key: 'phone',         label: 'Phone' },
  { key: 'email',         label: 'Email' },
  { key: 'source',        label: 'Source' },
  { key: 'country',       label: 'Country' },
  { key: 'course',        label: 'Course / Program' },
  { key: 'priority',      label: 'Priority' },
  { key: 'intake',        label: 'Intake' },
  { key: 'budget',        label: 'Budget' },
  { key: 'ielts',         label: 'IELTS Score' },
  { key: 'toefl',         label: 'TOEFL Score' },
  { key: 'pte',           label: 'PTE Score' },
  { key: 'gre',           label: 'GRE Score' },
  { key: 'gmat',          label: 'GMAT Score' },
  { key: 'sat',           label: 'SAT Score' },
  { key: 'qualification', label: 'Qualification' },
  { key: 'notes',         label: 'Notes' },
  { key: 'status',        label: 'Status / Pipeline Stage' },
  { key: 'assignedTo',   label: 'Assigned Counselor (from file)' },
];

const autoDetectField = (header) => {
  const h = header.toLowerCase().replace(/[^a-z0-9]/g, '');
  const map = {
    firstName:    ['firstname','first','givenname','fname'],
    lastName:     ['lastname','last','surname','familyname','lname'],
    name:         ['name','fullname','studentname','leadname','candidatename'],
    phone:        ['phone','mobile','contact','mobileno','phoneno','contactno','phonenumber','mobilenumber'],
    email:        ['email','emailid','emailaddress','mail'],
    source:       ['source','leadsource','channel'],
    country:      ['country','targetcountry','destination','preferredcountry'],
    course:       ['course','program','courseinterest','preferredcourse','subject','degree'],
    priority:     ['priority','leadpriority'],
    intake:       ['intake','targetintake','session','semester'],
    budget:       ['budget','budgetinusd','budgetusd'],
    ielts:        ['ielts','ieltsscore','ieltsband'],
    toefl:        ['toefl','toeflscore'],
    pte:          ['pte','ptescore'],
    gre:          ['gre','grescore'],
    gmat:         ['gmat','gmatscore'],
    sat:          ['sat','satscore'],
    qualification:['qualification','education','degree','highestqualification'],
    notes:        ['notes','remarks','comment','comments','note','description'],
    status:       ['status','stage','pipelinestage','leadstage','leadstatus','enquirystatus','currentstatus','enquirystage'],
    assignedTo:   ['assignedto','counselor','counsellor','assignedcounselor','assignedcounsellor','adviser','advisor','agent','assignee','owner'],
  };
  for (const [key, patterns] of Object.entries(map)) {
    if (patterns.includes(h)) return key;
  }
  return '';
};

// Parse a CSV line respecting quoted fields
const parseCSVLine = (line) => {
  const result = [];
  let cur = '', inQ = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') { inQ = !inQ; }
    else if (c === ',' && !inQ) { result.push(cur.trim()); cur = ''; }
    else { cur += c; }
  }
  result.push(cur.trim());
  return result;
};

function BulkUploadModal({ onClose, onStartImport, customSources, saveCustomSources }) {
  const { users, pipelineStages, addStage, customFields, addCustomField } = useCRM();
  const counselors = useMemo(() => (users || []).filter(u => u.role === 'counsellor' || u.role === 'counselor' || u.role === 'admin'), [users]);

  // Fields created inline during this import session (appear immediately before Firestore snapshot fires)
  const [newlyCreatedFields, setNewlyCreatedFields] = useState([]);

  // Per-column inline creation state: { [csvHeader]: { label, type, saving } }
  const [creatingFieldFor, setCreatingFieldFor] = useState({});

  // Merge hardcoded CRM fields + org custom fields + any created this session
  const allCrmFields = useMemo(() => [
    ...CRM_FIELDS,
    ...(customFields || []).map(cf => ({ key: `cf_${cf.id}`, label: `${cf.label} (Custom)` })),
    ...newlyCreatedFields.filter(nf => !(customFields || []).find(cf => `cf_${cf.id}` === nf.key)),
  ], [customFields, newlyCreatedFields]);

  const [step, setStep]           = useState(1);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [csvRows, setCsvRows]       = useState([]);
  const [mapping, setMapping]       = useState({});
  const fileRef = React.useRef();
  const [dragging, setDragging]     = useState(false);
  const [creatingNewSources, setCreatingNewSources] = useState(false);
  // Status mapping: { rawValue: stageId | '__new__' }
  const [statusOverrides, setStatusOverrides] = useState({});

  // Assignment config
  const [assignMode, setAssignMode] = useState('roundrobin'); // 'csv' | 'roundrobin' | 'single' | 'none'
  const [assignSingle, setAssignSingle] = useState('');       // userId for single mode
  const [assignPool, setAssignPool] = useState([]);           // userIds for round robin pool
  const rrPointer = useRef(0);

  // All known sources (built-in + custom)
  const allKnownSources = useMemo(() => {
    const custom = (customSources || []).filter(cs => !ALL_SOURCES.find(s => s.value === cs.value));
    return [...ALL_SOURCES, ...custom];
  }, [customSources]);

  // Fuzzy-match a raw source string from CSV to a known source value
  const matchSource = useCallback((rawSource) => {
    if (!rawSource) return null;
    const raw = rawSource.toLowerCase().trim();
    const exactVal = allKnownSources.find(s => s.value.toLowerCase() === raw);
    if (exactVal) return exactVal.value;
    const exactLbl = allKnownSources.find(s => s.label.toLowerCase() === raw);
    if (exactLbl) return exactLbl.value;
    const partial = allKnownSources.find(s =>
      s.label.toLowerCase().includes(raw) || raw.includes(s.label.toLowerCase())
    );
    if (partial) return partial.value;
    return null; // no match → new source
  }, [allKnownSources]);

  // Fuzzy-match a raw status string from CSV to an existing pipeline stage id
  const matchStatus = useCallback((rawStatus) => {
    if (!rawStatus || !pipelineStages?.length) return null;
    const raw = rawStatus.toLowerCase().trim();
    // Exact id match
    const exactId = pipelineStages.find(s => (s.id || '').toLowerCase() === raw);
    if (exactId) return exactId.id;
    // Exact name match
    const exactName = pipelineStages.find(s => (s.name || '').toLowerCase() === raw);
    if (exactName) return exactName.id;
    // Partial name match
    const partial = pipelineStages.find(s =>
      (s.name || '').toLowerCase().includes(raw) || raw.includes((s.name || '').toLowerCase())
    );
    if (partial) return partial.id;
    return null; // no match → new stage
  }, [pipelineStages]);

  const handleFile = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const lines = ev.target.result.split(/\r?\n/).filter(l => l.trim());
      if (lines.length < 2) return;
      const headers = parseCSVLine(lines[0]);
      const rows = lines.slice(1).map(line => {
        const vals = parseCSVLine(line);
        const obj = {};
        headers.forEach((h, i) => { obj[h] = (vals[i] || '').trim(); });
        return obj;
      }).filter(r => Object.values(r).some(v => v));
      setCsvHeaders(headers);
      setCsvRows(rows);
      const auto = {};
      headers.forEach(h => { auto[h] = autoDetectField(h); });
      setMapping(auto);
      setStep(2);
    };
    reader.readAsText(file);
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  // Which CSV column is mapped to "source" / "status" / "assignedTo"?
  const sourceCol = useMemo(() =>
    Object.entries(mapping).find(([, v]) => v === 'source')?.[0],
  [mapping]);
  const statusCol = useMemo(() =>
    Object.entries(mapping).find(([, v]) => v === 'status')?.[0],
  [mapping]);
  const counselorCol = useMemo(() =>
    Object.entries(mapping).find(([, v]) => v === 'assignedTo')?.[0],
  [mapping]);

  // Unique raw source values from the CSV
  const rawSourceValues = useMemo(() => {
    if (!sourceCol) return [];
    const vals = new Set(csvRows.map(r => (r[sourceCol] || '').trim()).filter(Boolean));
    return [...vals];
  }, [csvRows, sourceCol]);

  // Classify each raw source value
  const sourceAnalysis = useMemo(() => {
    return rawSourceValues.map(raw => {
      const matched = matchSource(raw);
      return { raw, matched, isNew: !matched };
    });
  }, [rawSourceValues, matchSource]);

  const newSources = sourceAnalysis.filter(s => s.isNew);

  // Unique raw status values from the CSV
  const rawStatusValues = useMemo(() => {
    if (!statusCol) return [];
    const vals = new Set(csvRows.map(r => (r[statusCol] || '').trim()).filter(Boolean));
    return [...vals];
  }, [csvRows, statusCol]);

  // Classify each raw status value — respects manual overrides
  const statusAnalysis = useMemo(() => {
    return rawStatusValues.map(raw => {
      const autoMatch = matchStatus(raw);
      const override  = statusOverrides[raw]; // '__new__' | stageId | undefined
      // Resolved: manual override wins; fallback to auto match; then 'new'
      const resolved  = override !== undefined ? override : (autoMatch || '__new__');
      const isNew     = resolved === '__new__';
      const matchedStage = isNew ? null : pipelineStages.find(s => s.id === resolved);
      return { raw, resolved, isNew, matchedStage, autoMatch };
    });
  }, [rawStatusValues, matchStatus, statusOverrides, pipelineStages]);

  const newStatuses = statusAnalysis.filter(s => s.isNew);

  // Fuzzy match a raw counselor name to a user object
  const matchCounselor = useCallback((rawName) => {
    if (!rawName) return null;
    const raw = rawName.toLowerCase().trim();
    return counselors.find(u =>
      (u.name || '').toLowerCase() === raw ||
      (u.email || '').toLowerCase() === raw ||
      (u.name || '').toLowerCase().includes(raw) ||
      raw.includes((u.name || '').toLowerCase())
    ) || null;
  }, [counselors]);

  // Create a new custom field inline and map this CSV column to it
  const handleCreateFieldForColumn = async (header) => {
    const state = creatingFieldFor[header];
    if (!state?.label?.trim()) return;
    setCreatingFieldFor(prev => ({ ...prev, [header]: { ...prev[header], saving: true } }));
    try {
      const options = state.type === 'select' && state.options
        ? state.options.split(',').map(s => s.trim()).filter(Boolean)
        : [];
      const docRef = await addCustomField({ label: state.label.trim(), type: state.type || 'text', options });
      const newKey   = `cf_${docRef.id}`;
      const newLabel = `${state.label.trim()} (Custom)`;
      setNewlyCreatedFields(prev => [...prev, { key: newKey, label: newLabel }]);
      setMapping(prev => ({ ...prev, [header]: newKey }));
      setCreatingFieldFor(prev => { const { [header]: _, ...rest } = prev; return rest; });
    } catch (e) {
      console.error('Failed to create field', e);
      setCreatingFieldFor(prev => ({ ...prev, [header]: { ...prev[header], saving: false } }));
    }
  };

  const buildLead = useCallback((row, resolvedSourceMap) => {
    const lead = {};
    Object.entries(mapping).forEach(([csvCol, crmKey]) => {
      if (!crmKey) return;
      if (crmKey.startsWith('cf_')) {
        // Custom field — store with cf_ prefix so lead panel reads it correctly
        lead[crmKey] = row[csvCol] || '';
      } else {
        lead[crmKey] = row[csvCol] || '';
      }
    });
    // Compute name from firstName/lastName if present
    if (lead.firstName || lead.lastName) {
      lead.name = [lead.firstName, lead.lastName].filter(Boolean).join(' ');
    }
    if (!lead.name || !lead.phone) return null;
    lead.countries = lead.country ? [lead.country] : [];
    // Resolve source
    if (lead.source) {
      lead.source = resolvedSourceMap[lead.source.trim()] || matchSource(lead.source) || 'manual';
    } else {
      lead.source = 'manual';
    }
    lead.priority = lead.priority || 'warm';
    // Keep raw status for resolution in handleImport
    lead._rawStatus = lead.status || '';
    delete lead.status;
    // Keep raw counselor name for assignment resolution later
    lead._rawCounselor = lead.assignedTo || '';
    delete lead.assignedTo;
    return lead;
  }, [mapping, matchSource]);

  // Counselor analysis (for step 3 preview when mode = 'csv')
  const rawCounselorValues = useMemo(() => {
    if (!counselorCol) return [];
    const vals = new Set(csvRows.map(r => (r[counselorCol] || '').trim()).filter(Boolean));
    return [...vals];
  }, [csvRows, counselorCol]);

  const counselorAnalysis = useMemo(() =>
    rawCounselorValues.map(raw => {
      const matched = matchCounselor(raw);
      return { raw, matched, isUnknown: !matched };
    }),
  [rawCounselorValues, matchCounselor]);

  // Toggle counselor in pool
  const togglePool = (uid) => setAssignPool(prev =>
    prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]
  );

  const mappedCrmKeys = Object.values(mapping).filter(Boolean);
  const hasPhone = mappedCrmKeys.includes('phone');
  const hasName  = mappedCrmKeys.includes('name') || mappedCrmKeys.includes('firstName');

  // Build preview rows using current source analysis
  const previewResolvedMap = useMemo(() => {
    const map = {};
    sourceAnalysis.forEach(s => {
      map[s.raw] = s.matched || s.raw.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    });
    return map;
  }, [sourceAnalysis]);

  const validRows = useMemo(() =>
    csvRows.map(r => buildLead(r, previewResolvedMap)).filter(Boolean),
  [csvRows, buildLead, previewResolvedMap]);

  const handleImport = async () => {
    setCreatingNewSources(true);

    // 1. Create any new sources in Firestore first
    let updatedCustomSources = [...(customSources || [])];
    if (newSources.length > 0 && saveCustomSources) {
      newSources.forEach(s => {
        const value = s.raw.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        const label = s.raw.trim();
        if (!updatedCustomSources.find(cs => cs.value === value) && !ALL_SOURCES.find(as => as.value === value)) {
          updatedCustomSources.push({ value, label, icon: '🏷️', builtIn: false });
        }
      });
      await saveCustomSources(updatedCustomSources);
    }

    // 2. Create any new pipeline stages in Firestore
    const resolvedStatusMap = {}; // rawValue → stageId
    for (const s of statusAnalysis) {
      if (s.isNew) {
        // Create the new stage
        const stageName = s.raw.trim();
        const stageId   = s.raw.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        // Only create if doesn't already exist by name or id
        const exists = pipelineStages.find(st =>
          st.id === stageId || (st.name || '').toLowerCase() === stageName.toLowerCase()
        );
        if (!exists && addStage) {
          await addStage({ name: stageName, color: '#64748b' });
        }
        resolvedStatusMap[s.raw] = stageId;
      } else {
        resolvedStatusMap[s.raw] = s.resolved; // existing stage id
      }
    }

    // 3. Build final source resolved map
    const resolvedSourceMap = {};
    sourceAnalysis.forEach(s => {
      resolvedSourceMap[s.raw] = s.matched || s.raw.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
    });

    // 4. Build final leads with resolved sources
    const firstStageId = pipelineStages[0]?.id || 'new';
    const builtRows = csvRows.map(r => buildLead(r, resolvedSourceMap)).filter(Boolean);

    // 5. Resolve counselor assignment + status per lead
    const pool = assignPool.length > 0 ? assignPool : counselors.map(c => c.id);
    rrPointer.current = 0;

    const finalRows = builtRows.map(lead => {
      // ── Counselor ──
      let assignedToId = '';
      let assignedTo   = '';

      if (assignMode === 'csv') {
        const rawCounselor = lead._rawCounselor || '';
        const matched = matchCounselor(rawCounselor);
        if (matched) {
          assignedToId = matched.id;
          assignedTo   = matched.name || matched.email || '';
        }
      } else if (assignMode === 'roundrobin') {
        const activePool = pool.filter(uid => counselors.find(c => c.id === uid));
        if (activePool.length > 0) {
          const uid  = activePool[rrPointer.current % activePool.length];
          rrPointer.current++;
          const user = counselors.find(c => c.id === uid);
          assignedToId = uid;
          assignedTo   = user?.name || user?.email || '';
        }
      } else if (assignMode === 'single') {
        const user = counselors.find(c => c.id === assignSingle);
        if (user) {
          assignedToId = user.id;
          assignedTo   = user.name || user.email || '';
        }
      }

      // ── Status ──
      let status = firstStageId;
      if (lead._rawStatus && resolvedStatusMap[lead._rawStatus.trim()]) {
        status = resolvedStatusMap[lead._rawStatus.trim()];
      } else if (lead._rawStatus) {
        // fallback: use first stage
        status = firstStageId;
      }

      const { _rawCounselor, _rawStatus, ...cleanLead } = lead;
      return { ...cleanLead, status, assignedToId, assignedTo };
    });

    setCreatingNewSources(false);
    onStartImport(finalRows);
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 720, width: '95vw' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>Bulk Import Leads</h2>
            <p style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
              {step === 1 ? 'Upload a CSV file to get started' : 'Match your CSV columns to CRM fields'}
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>✕</button>
        </div>

        {/* Step indicator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 24 }}>
          {['Upload File', 'Map Fields', 'Assign Counselors'].map((label, i) => (
            <React.Fragment key={i}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{
                  width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 11, fontWeight: 700,
                  background: step > i + 1 ? '#10b981' : step === i + 1 ? '#7c3aed' : 'rgba(0,0,0,0.08)',
                  color: step >= i + 1 ? '#fff' : '#64748b',
                }}>
                  {step > i + 1 ? '✓' : i + 1}
                </div>
                <span style={{ fontSize: 12, fontWeight: 500, color: step === i + 1 ? '#7c3aed' : step > i + 1 ? '#10b981' : '#94a3b8' }}>{label}</span>
              </div>
              {i < 2 && <div style={{ flex: 1, height: 1, background: step > i + 1 ? '#10b981' : 'rgba(0,0,0,0.08)', margin: '0 8px' }} />}
            </React.Fragment>
          ))}
        </div>

        {/* ── Step 1: Upload ── */}
        {step === 1 && (
          <div>
            <div
              onDragOver={e => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
              style={{
                border: `2px dashed ${dragging ? 'rgba(124,58,237,0.7)' : 'rgba(124,58,237,0.3)'}`,
                borderRadius: 14, padding: '52px 24px', textAlign: 'center', cursor: 'pointer',
                background: dragging ? 'rgba(124,58,237,0.08)' : 'rgba(124,58,237,0.03)',
                transition: 'all 0.15s', marginBottom: 16,
              }}
            >
              <div style={{ fontSize: 40, marginBottom: 10 }}>📂</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginBottom: 4 }}>
                {dragging ? 'Drop your file here' : 'Click or drag & drop a CSV file'}
              </div>
              <div style={{ fontSize: 12, color: '#64748b' }}>
                Supports .csv files — any column layout is fine, you'll map the fields next
              </div>
            </div>
            <input ref={fileRef} type="file" accept=".csv,.txt" onChange={e => handleFile(e.target.files[0])} style={{ display: 'none' }} />
            <div style={{ background: 'rgba(0,0,0,0.04)', borderRadius: 9, padding: '12px 14px', fontSize: 11.5, color: '#475569', fontFamily: 'monospace' }}>
              <div style={{ fontFamily: 'Inter,sans-serif', fontWeight: 600, color: '#374151', fontSize: 12, marginBottom: 6 }}>Example CSV:</div>
              first name,last name,mobile,email,country,source<br/>
              Rahul,Sharma,9876543210,rahul@gmail.com,UK,Facebook<br/>
              Priya,Nair,9123456789,,Canada,Instagram
            </div>
          </div>
        )}

        {/* ── Step 2: Map Fields ── */}
        {step === 2 && (
          <div>
            {(!hasName || !hasPhone) && (
              <div style={{ padding: '10px 14px', background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.25)', borderRadius: 9, fontSize: 12.5, color: '#d97706', marginBottom: 14 }}>
                ⚠️ Map at least <strong>First Name</strong> (or Full Name) and <strong>Phone</strong> — other rows will be skipped.
              </div>
            )}
            <div style={{ maxHeight: 360, overflowY: 'auto', marginBottom: 16 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#64748b', padding: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.5px', width: '38%' }}>CSV Column</th>
                    <th style={{ textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#64748b', padding: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.5px', width: '28%' }}>Maps to CRM Field</th>
                    <th style={{ textAlign: 'left', fontSize: 11, fontWeight: 600, color: '#64748b', padding: '0 0 10px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Sample Data</th>
                  </tr>
                </thead>
                <tbody>
                  {csvHeaders.map(header => (
                    <tr key={header}>
                      <td style={{ padding: '7px 12px 7px 0', verticalAlign: 'middle' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', background: 'rgba(0,0,0,0.04)', padding: '5px 10px', borderRadius: 7, display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {header}
                        </div>
                      </td>
                      <td style={{ padding: '7px 12px 7px 0', verticalAlign: 'middle' }}>
                        {creatingFieldFor[header] ? (
                          /* ── Inline "Create New Field" form ── */
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                            <input
                              autoFocus
                              placeholder="Field label (e.g. Referral Code)"
                              value={creatingFieldFor[header].label}
                              onChange={e => setCreatingFieldFor(prev => ({ ...prev, [header]: { ...prev[header], label: e.target.value } }))}
                              onKeyDown={e => e.key === 'Enter' && handleCreateFieldForColumn(header)}
                              style={{ width: '100%', boxSizing: 'border-box', fontSize: 12, padding: '5px 9px', borderRadius: 7, border: '1.5px solid #7c3aed', outline: 'none', fontFamily: 'inherit' }}
                            />
                            <div style={{ display: 'flex', gap: 5 }}>
                              <select
                                value={creatingFieldFor[header].type}
                                onChange={e => setCreatingFieldFor(prev => ({ ...prev, [header]: { ...prev[header], type: e.target.value } }))}
                                className="crm-select"
                                style={{ flex: 1, fontSize: 11 }}
                              >
                                <option value="text">Text</option>
                                <option value="number">Number</option>
                                <option value="select">Dropdown</option>
                                <option value="date">Date</option>
                              </select>
                              <button
                                onClick={() => handleCreateFieldForColumn(header)}
                                disabled={creatingFieldFor[header].saving || !creatingFieldFor[header].label?.trim()}
                                style={{ fontSize: 11, padding: '4px 10px', background: '#7c3aed', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', whiteSpace: 'nowrap', opacity: (creatingFieldFor[header].saving || !creatingFieldFor[header].label?.trim()) ? 0.6 : 1 }}
                              >
                                {creatingFieldFor[header].saving ? '…' : '✓ Save'}
                              </button>
                              <button
                                onClick={() => setCreatingFieldFor(prev => { const { [header]: _, ...rest } = prev; return rest; })}
                                style={{ fontSize: 11, padding: '4px 8px', background: 'rgba(0,0,0,0.07)', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#475569' }}
                              >✕</button>
                            </div>
                          </div>
                        ) : (
                          /* ── Normal field picker ── */
                          <select
                            value={mapping[header] || ''}
                            onChange={e => {
                              if (e.target.value === '__create_new__') {
                                setCreatingFieldFor(prev => ({ ...prev, [header]: { label: header, type: 'text', saving: false } }));
                              } else {
                                setMapping(p => ({ ...p, [header]: e.target.value }));
                              }
                            }}
                            className="crm-select"
                            style={{ width: '100%', fontSize: 12 }}
                          >
                            {allCrmFields.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
                            <option value="__create_new__">➕ Create New Field…</option>
                          </select>
                        )}
                      </td>
                      <td style={{ padding: '7px 0', verticalAlign: 'middle' }}>
                        <span style={{ fontSize: 11.5, color: '#475569', background: 'rgba(0,0,0,0.03)', padding: '4px 8px', borderRadius: 5, display: 'inline-block', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {csvRows[0]?.[header] || <span style={{ color: '#94a3b8' }}>—</span>}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Source mapping preview */}
            {sourceCol && sourceAnalysis.length > 0 && (
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>
                  📊 Source Mapping — {sourceAnalysis.length} unique value{sourceAnalysis.length !== 1 ? 's' : ''} found
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {sourceAnalysis.map(s => (
                    <div key={s.raw} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 8, background: '#fff', border: `1px solid ${s.isNew ? '#fde68a' : '#d1fae5'}` }}>
                      <span style={{ fontSize: 13 }}>{s.isNew ? '🆕' : '✅'}</span>
                      <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: '#0f172a' }}>"{s.raw}"</span>
                      <span style={{ fontSize: 11, color: '#94a3b8', margin: '0 4px' }}>→</span>
                      {s.isNew ? (
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#d97706', background: '#fef3c7', padding: '2px 10px', borderRadius: 20 }}>
                          New source will be created: "{s.raw}"
                        </span>
                      ) : (
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#065f46', background: '#d1fae5', padding: '2px 10px', borderRadius: 20 }}>
                          Matched: {allKnownSources.find(k => k.value === s.matched)?.label || s.matched}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                {newSources.length > 0 && (
                  <div style={{ fontSize: 11, color: '#d97706', marginTop: 8, padding: '6px 10px', background: '#fef3c7', borderRadius: 7 }}>
                    ⚡ {newSources.length} new source{newSources.length !== 1 ? 's' : ''} will be automatically added to your Sources list.
                  </div>
                )}
              </div>
            )}

            {/* Status mapping — interactive dropdowns */}
            {statusCol && statusAnalysis.length > 0 && (
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px', marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 4 }}>
                  🔀 Status Mapping — {statusAnalysis.length} unique value{statusAnalysis.length !== 1 ? 's' : ''} found
                </div>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 12 }}>
                  Map each CSV status value to an existing CRM pipeline stage, or leave as "Create new stage" to auto-create it.
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {statusAnalysis.map(s => (
                    <div key={s.raw} style={{
                      display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 9,
                      background: '#fff', border: `1px solid ${s.isNew ? '#fde68a' : '#d1fae5'}`,
                    }}>
                      {/* CSV value */}
                      <div style={{ minWidth: 0, flex: '0 0 180px' }}>
                        <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.4px' }}>From file</div>
                        <div style={{ fontSize: 12.5, fontWeight: 700, color: '#0f172a', background: 'rgba(0,0,0,0.05)', padding: '4px 9px', borderRadius: 6, display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.raw}
                        </div>
                      </div>
                      <span style={{ color: '#c4c4c4', fontSize: 16, flexShrink: 0 }}>→</span>
                      {/* Mapping dropdown */}
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 10, color: '#94a3b8', marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Map to CRM stage</div>
                        <select
                          value={s.resolved}
                          onChange={e => setStatusOverrides(prev => ({ ...prev, [s.raw]: e.target.value }))}
                          className="crm-select"
                          style={{ width: '100%', fontSize: 12, borderColor: s.isNew ? '#fbbf24' : '#86efac' }}
                        >
                          <option value="__new__">🆕 Create new stage: "{s.raw}"</option>
                          {(pipelineStages || []).map(stage => (
                            <option key={stage.id} value={stage.id}>{stage.name}</option>
                          ))}
                        </select>
                      </div>
                      {/* Status badge */}
                      <div style={{ flexShrink: 0 }}>
                        {s.isNew ? (
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#d97706', background: '#fef3c7', padding: '3px 10px', borderRadius: 20, whiteSpace: 'nowrap' }}>
                            Will create
                          </span>
                        ) : (
                          <span style={{ fontSize: 10, fontWeight: 700, color: '#065f46', background: '#d1fae5', padding: '3px 10px', borderRadius: 20, whiteSpace: 'nowrap' }}>
                            ✓ Mapped
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {newStatuses.length > 0 && (
                  <div style={{ fontSize: 11, color: '#d97706', marginTop: 10, padding: '7px 12px', background: '#fef3c7', borderRadius: 7 }}>
                    ⚡ {newStatuses.length} new stage{newStatuses.length !== 1 ? 's' : ''} will be created in your pipeline: {newStatuses.map(s => `"${s.raw}"`).join(', ')}
                  </div>
                )}
                {statusAnalysis.every(s => !s.isNew) && (
                  <div style={{ fontSize: 11, color: '#065f46', marginTop: 10, padding: '7px 12px', background: '#d1fae5', borderRadius: 7 }}>
                    ✅ All status values matched to existing stages — no new stages will be created.
                  </div>
                )}
              </div>
            )}

            <div style={{ background: 'rgba(124,58,237,0.04)', border: '1px solid rgba(124,58,237,0.15)', borderRadius: 10, padding: '12px 14px', marginBottom: 16, fontSize: 12.5, color: '#475569' }}>
              <span style={{ fontWeight: 600, color: '#7c3aed' }}>{validRows.length}</span> of {csvRows.length} rows will be imported
              {csvRows.length - validRows.length > 0 && <span style={{ color: '#f59e0b', marginLeft: 6 }}>({csvRows.length - validRows.length} skipped — missing Name or Phone)</span>}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn-ghost" onClick={() => { setStep(1); setCsvHeaders([]); setCsvRows([]); }}>← Re-upload</button>
              <button className="btn-ghost" onClick={onClose}>Cancel</button>
              <button className="btn-primary" onClick={() => setStep(3)} disabled={!hasName || !hasPhone || validRows.length === 0}>
                Next: Assign Counselors →
              </button>
            </div>
          </div>
        )}

        {/* ── Step 3: Assign Counselors ── */}
        {step === 3 && (
          <div>
            <div style={{ fontSize: 13, color: '#64748b', marginBottom: 18 }}>
              Choose how <strong>{validRows.length} leads</strong> should be assigned to counselors after import.
            </div>

            {/* Mode selector */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
              {[
                { id: 'roundrobin', icon: '🔄', title: 'Round Robin', desc: 'Distribute evenly among selected counselors' },
                { id: 'csv',        icon: '📋', title: 'From File',   desc: counselorCol ? `Use "${counselorCol}" column from CSV` : 'Map a counselor column in step 2 first', disabled: !counselorCol },
                { id: 'single',     icon: '👤', title: 'Assign All to One', desc: 'Every lead goes to one specific counselor' },
                { id: 'none',       icon: '⏳', title: 'Leave Unassigned', desc: 'Import without assignment — assign manually later' },
              ].map(m => (
                <div key={m.id} onClick={() => !m.disabled && setAssignMode(m.id)} style={{
                  padding: '14px 16px', borderRadius: 11, cursor: m.disabled ? 'not-allowed' : 'pointer',
                  border: assignMode === m.id ? '2px solid #7c3aed' : '1px solid #e2e8f0',
                  background: assignMode === m.id ? 'rgba(124,58,237,0.04)' : m.disabled ? '#f8fafc' : '#fff',
                  opacity: m.disabled ? 0.5 : 1, transition: 'all 0.15s',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 18 }}>{m.icon}</span>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{m.title}</span>
                    {assignMode === m.id && <span style={{ marginLeft: 'auto', color: '#7c3aed', fontWeight: 800 }}>✓</span>}
                  </div>
                  <div style={{ fontSize: 11, color: '#64748b' }}>{m.desc}</div>
                </div>
              ))}
            </div>

            {/* Round robin — pool selector */}
            {assignMode === 'roundrobin' && (
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 11, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>
                  Select counselors for round robin
                  <span style={{ fontSize: 11, fontWeight: 400, color: '#64748b', marginLeft: 6 }}>(leave all unchecked to include everyone)</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {counselors.map(u => {
                    const inPool = assignPool.includes(u.id);
                    return (
                      <div key={u.id} onClick={() => togglePool(u.id)} style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 20,
                        cursor: 'pointer', border: `1px solid ${inPool ? '#7c3aed' : '#e2e8f0'}`,
                        background: inPool ? 'rgba(124,58,237,0.08)' : '#fff', transition: 'all 0.15s',
                      }}>
                        <div style={{
                          width: 22, height: 22, borderRadius: '50%', fontSize: 9, fontWeight: 700,
                          background: u.avatar?.bg || '#7c3aed', color: u.avatar?.color || '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>{(u.name || u.email || 'U').slice(0, 2).toUpperCase()}</div>
                        <span style={{ fontSize: 12, fontWeight: 600, color: inPool ? '#7c3aed' : '#374151' }}>{u.name || u.email}</span>
                        {inPool && <span style={{ fontSize: 10, color: '#7c3aed' }}>✓</span>}
                      </div>
                    );
                  })}
                </div>
                {counselors.length === 0 && <div style={{ fontSize: 12, color: '#94a3b8' }}>No counselors found. Add team members in Settings → Team.</div>}
                {assignPool.length > 0 && (
                  <div style={{ marginTop: 10, fontSize: 11, color: '#7c3aed' }}>
                    {validRows.length} leads will be split across {assignPool.length} counselor{assignPool.length !== 1 ? 's' : ''} (~{Math.ceil(validRows.length / assignPool.length)} each)
                  </div>
                )}
                {assignPool.length === 0 && counselors.length > 0 && (
                  <div style={{ marginTop: 10, fontSize: 11, color: '#64748b' }}>
                    All {counselors.length} counselors selected · ~{Math.ceil(validRows.length / counselors.length)} leads each
                  </div>
                )}
              </div>
            )}

            {/* Single counselor picker */}
            {assignMode === 'single' && (
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 11, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>Select counselor</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {counselors.map(u => {
                    const selected = assignSingle === u.id;
                    return (
                      <div key={u.id} onClick={() => setAssignSingle(u.id)} style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 20,
                        cursor: 'pointer', border: `1px solid ${selected ? '#7c3aed' : '#e2e8f0'}`,
                        background: selected ? 'rgba(124,58,237,0.08)' : '#fff',
                      }}>
                        <div style={{
                          width: 22, height: 22, borderRadius: '50%', fontSize: 9, fontWeight: 700,
                          background: u.avatar?.bg || '#7c3aed', color: u.avatar?.color || '#fff',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>{(u.name || u.email || 'U').slice(0, 2).toUpperCase()}</div>
                        <span style={{ fontSize: 12, fontWeight: 600, color: selected ? '#7c3aed' : '#374151' }}>{u.name || u.email}</span>
                        {selected && <span style={{ fontSize: 10, color: '#7c3aed' }}>✓</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* CSV counselor analysis */}
            {assignMode === 'csv' && counselorCol && counselorAnalysis.length > 0 && (
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 11, padding: 16, marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a', marginBottom: 10 }}>Counselor mapping from file</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {counselorAnalysis.map(s => (
                    <div key={s.raw} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 8, background: '#fff', border: `1px solid ${s.isUnknown ? '#fde68a' : '#d1fae5'}` }}>
                      <span style={{ fontSize: 13 }}>{s.isUnknown ? '⚠️' : '✅'}</span>
                      <span style={{ flex: 1, fontSize: 12, fontWeight: 600, color: '#0f172a' }}>"{s.raw}"</span>
                      <span style={{ fontSize: 11, color: '#94a3b8', margin: '0 4px' }}>→</span>
                      {s.isUnknown ? (
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#d97706', background: '#fef3c7', padding: '2px 10px', borderRadius: 20 }}>
                          No match — will be unassigned
                        </span>
                      ) : (
                        <span style={{ fontSize: 11, fontWeight: 700, color: '#065f46', background: '#d1fae5', padding: '2px 10px', borderRadius: 20 }}>
                          {s.matched?.name || s.matched?.email}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
                {counselorAnalysis.some(s => s.isUnknown) && (
                  <div style={{ fontSize: 11, color: '#d97706', marginTop: 8, padding: '6px 10px', background: '#fef3c7', borderRadius: 7 }}>
                    ⚠️ Unmatched counselors: leads will be imported unassigned. You can reassign them after import.
                  </div>
                )}
              </div>
            )}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn-ghost" onClick={() => setStep(2)}>← Back</button>
              <button className="btn-ghost" onClick={onClose}>Cancel</button>
              <button className="btn-primary"
                onClick={handleImport}
                disabled={creatingNewSources || (assignMode === 'single' && !assignSingle)}>
                {creatingNewSources ? '⏳ Preparing…' : `🚀 Import ${validRows.length} Leads`}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ADD LEAD MODAL ───────────────────────────────────────────────────────────
function AddLeadModal({ onClose }) {
  const { addLead, customFields, customSources, priorities } = useCRM();
  const mergedSources = useMemo(() => {
    const custom = (customSources || []).filter(cs => !ALL_SOURCES.find(s => s.value === cs.value));
    return [...ALL_SOURCES, ...custom];
  }, [customSources]);
  const emptyForm = {
    firstName: '', lastName: '', phone: '', email: '', source: 'manual', priority: 'warm',
    countries: [], course: '', intake: '', budget: '',
    ielts: '', toefl: '', pte: '', gre: '', gmat: '', sat: '',
    qualification: '', notes: '',
  };
  const [form, setForm] = useState(emptyForm);
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [countryOpen, setCountryOpen] = useState(false);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const toggleCountry = (c) => setForm(p => ({
    ...p,
    countries: p.countries.includes(c)
      ? p.countries.filter(x => x !== c)
      : [...p.countries, c],
  }));

  const handleSubmit = async (addMore = false) => {
    if (!form.firstName.trim() || !form.phone.trim()) {
      setSaveError('First name and phone number are required.');
      return;
    }
    setSaving(true);
    setSaveError('');
    try {
      const name = [form.firstName, form.lastName].filter(Boolean).join(' ');
      await addLead({
        ...form,
        name,
        country: form.countries[0] || '',
      });
      if (addMore) {
        setForm({ ...emptyForm, source: form.source, priority: form.priority });
        setStep(1);
      } else {
        onClose();
      }
    } catch (err) {
      console.error('Add lead failed:', err);
      setSaveError('Failed to save — please check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  const testScoreFields = [
    { key: 'ielts', label: 'IELTS', placeholder: '7.0', step: '0.5' },
    { key: 'toefl', label: 'TOEFL', placeholder: '100' },
    { key: 'pte',   label: 'PTE',   placeholder: '65' },
    { key: 'gre',   label: 'GRE',   placeholder: '320' },
    { key: 'gmat',  label: 'GMAT',  placeholder: '700' },
    { key: 'sat',   label: 'SAT',   placeholder: '1400' },
  ];

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box modal-wide">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700, color: '#0f172a' }}>Add New Lead</h2>
            <p style={{ fontSize: 13, color: '#475569', marginTop: 2 }}>Step {step} of 2 — {step === 1 ? 'Basic Info' : 'Academic Profile'}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>

        <div className="progress-bar" style={{ marginBottom: 20 }}>
          <div className="progress-fill" style={{ width: step === 1 ? '50%' : '100%', background: 'linear-gradient(90deg,#7c3aed,#3b82f6)' }} />
        </div>

        {step === 1 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>First Name *</label>
              <input className="crm-input" placeholder="e.g. Arjun (type full name to auto-split)" value={form.firstName} autoFocus
                onChange={e => {
                  const val = e.target.value;
                  if (val.includes(' ') && !form.lastName.trim()) {
                    const idx = val.indexOf(' ');
                    set('firstName', val.slice(0, idx));
                    set('lastName', val.slice(idx + 1));
                  } else {
                    set('firstName', val);
                  }
                }} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Last Name</label>
              <input className="crm-input" placeholder="e.g. Mehta" value={form.lastName} onChange={e => set('lastName', e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Phone *</label>
              <PhoneInput value={form.phone} onChange={v => set('phone', v)} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Email</label>
              <input className="crm-input" placeholder="arjun@gmail.com" type="email" value={form.email} onChange={e => set('email', e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Lead Source</label>
              <select className="crm-select" style={{ width: '100%' }} value={form.source} onChange={e => set('source', e.target.value)}>
                {mergedSources.map(s => (
                  <option key={s.value} value={s.value}>{s.icon || SOURCE_ICONS[s.value]} {s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Priority</label>
              <select className="crm-select" style={{ width: '100%' }} value={form.priority} onChange={e => set('priority', e.target.value)}>
                {(priorities || [{ value: 'hot', label: 'Hot', emoji: '🔥' }, { value: 'warm', label: 'Warm', emoji: '☀️' }, { value: 'cold', label: 'Cold', emoji: '❄️' }]).map(p => (
                  <option key={p.value} value={p.value}>{p.emoji} {p.label}</option>
                ))}
              </select>
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Initial Notes</label>
              <textarea className="crm-textarea" placeholder="Any initial notes about this lead..." value={form.notes} onChange={e => set('notes', e.target.value)} style={{ minHeight: 64 }} />
            </div>
          </div>
        )}

        {step === 2 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {/* Multi-country selector */}
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Target Countries</label>
              <div style={{ position: 'relative' }}>
                <div
                  onClick={() => setCountryOpen(v => !v)}
                  className="crm-input"
                  style={{ cursor: 'pointer', display: 'flex', flexWrap: 'wrap', gap: 4, minHeight: 40, alignItems: 'center', padding: '6px 10px' }}
                >
                  {form.countries.length === 0
                    ? <span style={{ color: '#94a3b8', fontSize: 13 }}>Select countries…</span>
                    : form.countries.map(c => (
                        <span key={c} style={{ background: 'rgba(124,58,237,0.15)', color: '#7c3aed', padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                          {c}
                          <span onClick={ev => { ev.stopPropagation(); toggleCountry(c); }} style={{ cursor: 'pointer', fontSize: 13, lineHeight: 1 }}>×</span>
                        </span>
                      ))
                  }
                  <span style={{ marginLeft: 'auto', color: '#94a3b8', fontSize: 12 }}>{countryOpen ? '▴' : '▾'}</span>
                </div>
                {countryOpen && (
                  <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, background: '#fff', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 200, padding: 10, display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, maxHeight: 220, overflowY: 'auto' }}>
                    {COUNTRIES.map(c => (
                      <label key={c} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 12, color: form.countries.includes(c) ? '#7c3aed' : '#374151', background: form.countries.includes(c) ? 'rgba(124,58,237,0.08)' : 'transparent', transition: 'all 0.1s' }}>
                        <input type="checkbox" checked={form.countries.includes(c)} onChange={() => toggleCountry(c)} style={{ accentColor: '#7c3aed' }} />
                        {c}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Target Course</label>
              <input className="crm-input" placeholder="e.g. MBA, MS Computer Science" value={form.course} onChange={e => set('course', e.target.value)} />
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Preferred Intake</label>
              <select className="crm-select" style={{ width: '100%' }} value={form.intake} onChange={e => set('intake', e.target.value)}>
                <option value="">Select intake</option>
                {ALL_INTAKES.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Qualification</label>
              <select className="crm-select" style={{ width: '100%' }} value={form.qualification} onChange={e => set('qualification', e.target.value)}>
                <option value="">Select</option>
                {['10th','12th','Diploma','Graduate','Post Graduate'].map(q => <option key={q} value={q}>{q}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>Budget (INR)</label>
              <input className="crm-input" placeholder="e.g. 3500000" type="number" value={form.budget} onChange={e => set('budget', e.target.value)} />
            </div>

            {/* Test Scores */}
            <div style={{ gridColumn: '1 / -1' }}>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 600, letterSpacing: '0.4px', marginBottom: 8 }}>TEST SCORES (leave blank if not given)</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                {testScoreFields.map(({ key, label, placeholder, step: s }) => (
                  <div key={key}>
                    <label style={{ fontSize: 11, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 4 }}>{label}</label>
                    <input className="crm-input" placeholder={placeholder} type="number" step={s} value={form[key]} onChange={e => set(key, e.target.value)} style={{ height: 36, fontSize: 13 }} />
                  </div>
                ))}
              </div>
            </div>

            {/* Custom fields */}
            {customFields.filter(f => f.type !== 'checkbox').map(cf => (
              <div key={cf.id}>
                <label style={{ fontSize: 12, color: '#64748b', fontWeight: 500, display: 'block', marginBottom: 6 }}>{cf.label}{cf.required ? ' *' : ''}</label>
                {cf.type === 'select' ? (
                  <select className="crm-select" style={{ width: '100%' }} value={form[cf.label] || ''} onChange={e => set(cf.label, e.target.value)}>
                    <option value="">Select</option>
                    {(cf.options || []).map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <input className="crm-input" type={cf.type === 'number' ? 'number' : 'text'} value={form[cf.label] || ''} onChange={e => set(cf.label, e.target.value)} />
                )}
              </div>
            ))}
          </div>
        )}

        {saveError && (
          <div style={{ marginTop: 14, padding: '10px 14px', background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 8, fontSize: 13, color: '#dc2626' }}>
            ⚠️ {saveError}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, marginTop: 16, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {step === 2 && <button className="btn-ghost" onClick={() => setStep(1)} disabled={saving}>← Back</button>}
          <button className="btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          {step === 1 ? (
            <button className="btn-primary" onClick={() => { setSaveError(''); setCountryOpen(false); setStep(2); }} disabled={!form.firstName.trim() || !form.phone.trim()}>
              Next →
            </button>
          ) : (
            <>
              <button className="btn-ghost" onClick={() => handleSubmit(true)} disabled={saving} style={{ fontWeight: 600 }}>
                {saving ? 'Saving…' : '+ Save & Add More'}
              </button>
              <button className="btn-primary" onClick={() => handleSubmit(false)} disabled={saving} style={{ minWidth: 120 }}>
                {saving ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 0.7s linear infinite' }}><path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/></svg>
                    Saving…
                  </span>
                ) : (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                    Create Lead
                  </span>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── WHATSAPP CHAT TAB ────────────────────────────────────────────────────────
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

function WAChatTab({ lead, currentUser }) {
  const [messages,      setMessages]      = useState([]);
  const [waSession,     setWaSession]     = useState(null);
  const [templates,     setTemplates]     = useState([]);
  const [text,          setText]          = useState('');
  const [sending,       setSending]       = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [connecting,    setConnecting]    = useState(false);
  const msgEndRef = useRef(null);

  const phoneNorm = (lead.phone || '').replace(/\D/g, '');

  // Subscribe to this counselor's WA session status
  useEffect(() => {
    if (!currentUser?.id) return;
    return onSnapshot(doc(db, 'waSessions', currentUser.id), snap => {
      setWaSession(snap.exists() ? snap.data() : null);
    }, () => setWaSession(null));
  }, [currentUser?.id]);

  // Subscribe to WA messages for this lead — no orderBy to avoid composite index
  useEffect(() => {
    if (!lead.id) return;
    const q = query(collection(db, 'waMessages'), where('leadId', '==', lead.id));
    return onSnapshot(
      q,
      snap => {
        const msgs = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        msgs.sort((a, b) => (a.timestamp?.seconds || 0) - (b.timestamp?.seconds || 0));
        setMessages(msgs);
      },
      () => setMessages([])
    );
  }, [lead.id]);

  // Subscribe to templates — no orderBy to avoid composite index requirement
  useEffect(() => {
    const companyId = currentUser?.companyId || 'default';
    const q = query(collection(db, 'waTemplates'), where('companyId', '==', companyId));
    return onSnapshot(
      q,
      snap => {
        const tmpl = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        tmpl.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));
        setTemplates(tmpl);
      },
      () => setTemplates([])
    );
  }, [currentUser?.companyId]);

  // Mark incoming messages read when tab opens
  useEffect(() => {
    if (!lead.id || !currentUser?.id) return;
    const markRead = async () => {
      const q = query(collection(db, 'waMessages'), where('leadId', '==', lead.id), where('fromMe', '==', false), where('read', '==', false));
      const snap = await getDocs(q);
      if (snap.empty) return;
      const batch = writeBatch(db);
      snap.docs.forEach(d => batch.update(d.ref, { read: true }));
      await batch.commit();
      await updateDoc(doc(db, 'leads', lead.id), { waUnread: 0 });
    };
    markRead().catch(() => {});
  }, [lead.id, currentUser?.id]);

  // Scroll to bottom when messages change
  useEffect(() => {
    msgEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      await fetch(`${BACKEND_URL}/api/wa/connect`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ counselorId: currentUser.id }),
      });
    } catch (e) {
      alert('Could not reach the backend server. Make sure it is running.');
    } finally {
      setConnecting(false);
    }
  };

  const handleSend = async () => {
    const trimmed = text.trim();
    if (!trimmed || !phoneNorm) return;
    setSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/wa/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ counselorId: currentUser.id, phone: phoneNorm, text: trimmed, leadId: lead.id }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.error || 'Failed to send message'); return; }
      setText('');
    } catch (e) {
      alert('Send failed — is the backend running?');
    } finally {
      setSending(false);
    }
  };

  const handleFileAttach = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !phoneNorm) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('counselorId', currentUser.id);
    fd.append('phone', phoneNorm);
    fd.append('caption', file.name);
    fd.append('leadId', lead.id);
    setSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/wa/send-media`, { method: 'POST', body: fd });
      if (!res.ok) { const d = await res.json(); alert(d.error || 'Upload failed'); }
    } catch (e) { alert('Upload failed — is the backend running?'); }
    finally { setSending(false); e.target.value = ''; }
  };

  const getTimestamp = (ts) => {
    if (!ts) return '';
    const d = ts?.toDate?.() || (ts?.seconds ? new Date(ts.seconds * 1000) : null);
    if (!d) return '';
    const now = new Date();
    const diffH = (now - d) / 3600000;
    if (diffH < 24) return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) + ' ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  // Session not connected
  if (!waSession || waSession.status === 'disconnected') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 16, padding: 24, textAlign: 'center' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(37,211,102,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32 }}>💬</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>Connect Your WhatsApp</div>
        <div style={{ fontSize: 13, color: '#64748b', maxWidth: 280 }}>
          Scan a QR code once to link your personal WhatsApp number. Messages will sync here in real time.
        </div>
        {connecting ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, border: '3px solid rgba(37,211,102,0.3)', borderTop: '3px solid #25d366', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            <div style={{ fontSize: 13, color: '#25d366', fontWeight: 600 }}>Starting session… QR code will appear shortly</div>
            <div style={{ fontSize: 12, color: '#94a3b8' }}>This can take up to 15 seconds</div>
          </div>
        ) : (
          <button onClick={handleConnect}
            style={{ background: 'linear-gradient(135deg, #25d366, #128c7e)', color: '#fff', border: 'none', borderRadius: 10, padding: '12px 28px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
            📱 Connect WhatsApp
          </button>
        )}
        <div style={{ fontSize: 12, color: '#94a3b8' }}>Or go to Settings → WhatsApp to manage your connection</div>
      </div>
    );
  }

  // QR scanning in progress (connecting state but no QR yet)
  if (waSession.status === 'connecting' && !waSession.qrCode) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 16, padding: 24, textAlign: 'center' }}>
        <div style={{ width: 36, height: 36, border: '3px solid rgba(37,211,102,0.3)', borderTop: '3px solid #25d366', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        <div style={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>Generating QR Code…</div>
        <div style={{ fontSize: 13, color: '#64748b' }}>Almost there — QR will appear in a moment</div>
      </div>
    );
  }

  // QR scanning in progress
  if (waSession.status === 'connecting' && waSession.qrCode) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, padding: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>Scan with WhatsApp</div>
        <div style={{ fontSize: 13, color: '#64748b', maxWidth: 280 }}>Open WhatsApp on your phone → ⋮ → Linked Devices → Link a Device</div>
        <img src={waSession.qrCode} alt="QR Code" style={{ width: 220, height: 220, borderRadius: 12, border: '1px solid rgba(0,0,0,0.08)' }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#f59e0b' }}>
          <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: '#f59e0b', animation: 'pulse 1.5s infinite' }} />
          Waiting for scan…
        </div>
      </div>
    );
  }

  // Connected — show chat
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 400 }}>
      {/* Session badge */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, padding: '8px 12px', background: 'rgba(37,211,102,0.07)', borderRadius: 10, border: '1px solid rgba(37,211,102,0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#25d366', display: 'inline-block' }} />
          <span style={{ fontSize: 12, color: '#128c7e', fontWeight: 600 }}>Connected as +{waSession.phone}</span>
        </div>
        <span style={{ fontSize: 11, color: '#64748b' }}>{lead.phone}</span>
      </div>

      {/* Message list */}
      <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12, paddingRight: 2, minHeight: 200, maxHeight: 360 }}>
        {messages.length === 0 && (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: 13 }}>
            No messages yet. Say hello! 👋
          </div>
        )}
        {messages.map(msg => {
          const isMe = msg.fromMe;
          return (
            <div key={msg.id} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
              <div style={{
                maxWidth: '80%', padding: '9px 13px', borderRadius: isMe ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                background: isMe ? 'linear-gradient(135deg, #dcf8c6, #c5f0a4)' : '#fff',
                border: '1px solid',
                borderColor: isMe ? 'rgba(37,211,102,0.3)' : 'rgba(0,0,0,0.08)',
                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              }}>
                {msg.type === 'image' && msg.mediaUrl && (
                  <img src={msg.mediaUrl} alt="attachment" style={{ width: '100%', borderRadius: 8, marginBottom: 4 }} />
                )}
                {msg.type === 'document' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, fontSize: 12, color: '#3b82f6' }}>
                    📎 {msg.body}
                  </div>
                )}
                {(msg.type === 'text' || !msg.type) && (
                  <div style={{ fontSize: 13, color: '#0f172a', lineHeight: 1.5, wordBreak: 'break-word' }}>{msg.body}</div>
                )}
                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4, textAlign: 'right' }}>
                  {getTimestamp(msg.timestamp)}{isMe && ' ✓'}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={msgEndRef} />
      </div>

      {/* Quick reply templates */}
      {showTemplates && templates.length > 0 && (
        <div style={{ marginBottom: 8, background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 10, overflow: 'hidden', maxHeight: 180, overflowY: 'auto', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}>
          {templates.map(t => (
            <button key={t.id} onClick={() => { setText(t.body); setShowTemplates(false); }}
              style={{ width: '100%', textAlign: 'left', padding: '10px 14px', background: 'none', border: 'none', borderBottom: '1px solid rgba(0,0,0,0.05)', cursor: 'pointer', fontSize: 12, color: '#0f172a' }}>
              <div style={{ fontWeight: 600, color: '#7c3aed', marginBottom: 2 }}>{t.title}</div>
              <div style={{ color: '#64748b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.body}</div>
            </button>
          ))}
        </div>
      )}

      {/* Input bar */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'flex-end' }}>
        {/* Template button */}
        <button onClick={() => setShowTemplates(p => !p)} title="Quick replies"
          style={{ width: 36, height: 36, borderRadius: 9, background: showTemplates ? 'rgba(124,58,237,0.1)' : 'rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.08)', color: '#7c3aed', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          ⚡
        </button>
        {/* Attach button */}
        <label title="Attach file" style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.08)', color: '#64748b', cursor: 'pointer', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          📎
          <input type="file" accept="image/*,application/pdf,.doc,.docx" onChange={handleFileAttach} style={{ display: 'none' }} />
        </label>
        {/* Text input */}
        <textarea value={text} onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
          placeholder={`Message ${lead.firstName || lead.phone}…`}
          rows={1}
          style={{ flex: 1, resize: 'none', padding: '9px 13px', borderRadius: 10, border: '1px solid rgba(0,0,0,0.12)', fontSize: 13, outline: 'none', fontFamily: 'inherit', lineHeight: 1.5, maxHeight: 80, overflowY: 'auto' }}
        />
        {/* Send button */}
        <button onClick={handleSend} disabled={!text.trim() || sending}
          style={{ width: 36, height: 36, borderRadius: 9, background: text.trim() ? 'linear-gradient(135deg, #25d366, #128c7e)' : 'rgba(0,0,0,0.05)', border: 'none', color: text.trim() ? '#fff' : '#94a3b8', cursor: text.trim() ? 'pointer' : 'not-allowed', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          {sending ? '…' : '➤'}
        </button>
      </div>
    </div>
  );
}

// ─── LEAD MEETING TAB ─────────────────────────────────────────────────────────
const QUICK_PLATFORMS = [
  { id: 'google_meet', label: 'Google Meet', icon: '🎥', color: '#4285f4', autoLink: true },
  { id: 'teams',       label: 'Teams',       icon: '🟦', color: '#5059c9', autoLink: true },
  { id: 'zoom',        label: 'Zoom',        icon: '🔵', color: '#2d8cff', autoLink: true },
  { id: 'whatsapp',    label: 'WhatsApp',    icon: '📞', color: '#25d366', autoLink: false },
  { id: 'in_person',   label: 'In Person',   icon: '🤝', color: '#f59e0b', autoLink: false },
  { id: 'phone',       label: 'Phone',       icon: '☎️', color: '#64748b', autoLink: false },
];

const PLATFORM_TO_PROVIDER_QUICK = { google_meet: 'google', teams: 'microsoft', zoom: 'zoom' };
const PROVIDER_LABEL_QUICK = { google: 'Google', microsoft: 'Microsoft', zoom: 'Zoom' };
const LEAD_BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

function generateJitsiLink() {
  const r = () => Math.random().toString(36).substring(2, 7);
  return `https://meet.jit.si/${r()}-${r()}-${r()}`;
}

function LeadMeetingTab({ lead, currentUser, company }) {
  const [meetings, setMeetings] = React.useState([]);
  const [form, setForm] = React.useState({
    platform: 'google_meet', date: '', time: '', title: '', duration: 30, meetLink: '', agenda: '',
  });
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [oauthStatus, setOauthStatus] = React.useState(null);
  const [creatingLink, setCreatingLink] = React.useState(false);
  const [linkError, setLinkError] = React.useState('');

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const today = new Date().toISOString().split('T')[0];

  // Fetch which accounts this user has connected
  React.useEffect(() => {
    if (!company?.id || !currentUser?.id) return;
    fetch(`${LEAD_BACKEND_URL}/api/oauth/status?${new URLSearchParams({ companyId: company.id, userId: currentUser.id })}`)
      .then(r => r.json()).then(d => setOauthStatus(d || {})).catch(() => {});
  }, [company?.id, currentUser?.id]); // eslint-disable-line

  // Load meetings for this lead
  React.useEffect(() => {
    if (!company?.id) return;
    let unsub = null;
    Promise.all([import('firebase/firestore'), import('../firebase/config')]).then(([ff, fc]) => {
      const { collection, onSnapshot, query, where } = ff;
      const { db: fdb } = fc;
      const ref = query(collection(fdb, 'companies', company.id, 'meetings'), where('leadId', '==', lead.id));
      unsub = onSnapshot(ref, snap => {
        const items = snap.docs.map(d => ({ ...d.data(), id: d.id }));
        items.sort((a, b) => (b.date + (b.time || '')).localeCompare(a.date + (a.time || '')));
        setMeetings(items);
      });
    });
    return () => { if (unsub) unsub(); };
  }, [company?.id, lead.id]); // eslint-disable-line

  const handleSchedule = async () => {
    if (!form.date) return;
    setSaving(true);
    try {
      const { collection, addDoc, serverTimestamp } = await import('firebase/firestore');
      const { db: fdb } = await import('../firebase/config');
      await addDoc(collection(fdb, 'companies', company.id, 'meetings'), {
        ...form,
        leadId: lead.id, leadName: lead.name, leadPhone: lead.phone,
        status: 'scheduled', createdBy: currentUser?.id, createdByName: currentUser?.name,
        companyId: company.id, createdAt: serverTimestamp(),
      });
      setForm({ platform: 'google_meet', date: '', time: '', title: '', duration: 30, meetLink: '', agenda: '' });
      setSaved(true); setTimeout(() => setSaved(false), 3000);
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const handleCreateRealLink = async () => {
    if (!form.date) { setLinkError('Pick a date first.'); return; }
    setLinkError(''); setCreatingLink(true);
    try {
      const startTime = new Date(form.date + 'T' + (form.time || '09:00')).toISOString();
      const resp = await fetch(`${LEAD_BACKEND_URL}/api/meetings/create-link`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId: company.id, userId: currentUser.id, platform: form.platform,
          title: form.title || `${currentPlatform.label} with ${lead.name}`, startTime,
          durationMinutes: form.duration, attendeeEmails: lead.email ? [lead.email] : [],
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (data.success && data.link) {
        set('meetLink', data.link);
      } else {
        setLinkError(data.error || 'Could not create meeting link.');
      }
    } catch { setLinkError('Could not reach the server.'); }
    setCreatingLink(false);
  };

  const currentPlatform = QUICK_PLATFORMS.find(p => p.id === form.platform) || QUICK_PLATFORMS[0];
  const requiredProvider = PLATFORM_TO_PROVIDER_QUICK[form.platform];
  const isConnected = !!(requiredProvider && oauthStatus?.[requiredProvider]?.connected);

  return (
    <div>
      {/* Schedule form */}
      <div style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.06), rgba(59,130,246,0.06))', borderRadius: 14, border: '1px solid rgba(124,58,237,0.15)', padding: 16, marginBottom: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>📅 Schedule a Meeting</div>

        {/* Platform dropdown */}
        <select
          value={form.platform}
          onChange={e => { set('platform', e.target.value); set('meetLink', ''); setLinkError(''); }}
          className="crm-select"
          style={{ width: '100%', marginBottom: 10, fontSize: 12 }}
        >
          {QUICK_PLATFORMS.map(p => <option key={p.id} value={p.id}>{p.icon}  {p.label}</option>)}
        </select>

        {/* Connected status chip */}
        {currentPlatform.autoLink && requiredProvider && (
          <div style={{ marginBottom: 10 }}>
            {isConnected ? (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 20, background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.2)' }}>
                <span style={{ fontSize: 10 }}>✅</span>
                <span style={{ fontSize: 10, color: '#059669', fontWeight: 700 }}>
                  {PROVIDER_LABEL_QUICK[requiredProvider]} connected{oauthStatus?.[requiredProvider]?.email ? ` · ${oauthStatus[requiredProvider].email.split('@')[0]}` : ''}
                </span>
              </div>
            ) : (
              <div style={{ fontSize: 10, color: '#64748b', background: 'rgba(100,116,139,0.08)', padding: '4px 10px', borderRadius: 20, display: 'inline-block' }}>
                ○ Connect {PROVIDER_LABEL_QUICK[requiredProvider]} in Meetings page for real links
              </div>
            )}
          </div>
        )}

        {/* Title */}
        <input className="crm-input" value={form.title} onChange={e => set('title', e.target.value)}
          placeholder={`${currentPlatform.label} with ${lead.name}`} style={{ width: '100%', marginBottom: 10, fontSize: 12 }} />

        {/* Date / Time / Duration */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
          <input type="date" className="crm-input" min={today} value={form.date} onChange={e => set('date', e.target.value)} style={{ fontSize: 11 }} />
          <input type="time" className="crm-input" value={form.time} onChange={e => set('time', e.target.value)} style={{ fontSize: 11 }} />
          <select className="crm-select" value={form.duration} onChange={e => set('duration', Number(e.target.value))} style={{ fontSize: 11 }}>
            {[15,30,45,60,90,120].map(d => <option key={d} value={d}>{d}m</option>)}
          </select>
        </div>

        {/* Meeting link row */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          <input className="crm-input" value={form.meetLink} onChange={e => set('meetLink', e.target.value)}
            placeholder={currentPlatform.autoLink ? 'Generate link below or paste one' : 'Location / link (optional)'}
            style={{ flex: 1, fontSize: 11 }} />
          {form.meetLink && (
            <button onClick={() => navigator.clipboard?.writeText(form.meetLink)} style={{
              padding: '0 10px', borderRadius: 7, border: '1px solid rgba(0,0,0,0.1)', background: 'none', cursor: 'pointer', fontSize: 13,
            }}>📋</button>
          )}
        </div>

        {/* Generate buttons */}
        {currentPlatform.autoLink && !form.meetLink && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
            {isConnected && (
              <button onClick={handleCreateRealLink} disabled={creatingLink || !form.date} style={{
                padding: '7px 12px', borderRadius: 8, border: 'none', cursor: (!form.date || creatingLink) ? 'not-allowed' : 'pointer',
                background: `linear-gradient(135deg, ${currentPlatform.color}, ${currentPlatform.color}cc)`,
                color: '#fff', fontSize: 11, fontWeight: 700, opacity: !form.date ? 0.5 : 1,
              }}>{creatingLink ? '⏳ Creating…' : `${currentPlatform.icon} Real ${currentPlatform.label} Link`}</button>
            )}
            <button onClick={() => set('meetLink', generateJitsiLink())} style={{
              padding: '7px 12px', borderRadius: 8,
              border: `1px solid ${currentPlatform.color}40`,
              background: isConnected ? 'transparent' : currentPlatform.color + '15',
              color: currentPlatform.color, cursor: 'pointer', fontSize: 11, fontWeight: 700,
            }}>⚡ {isConnected ? 'Quick Jitsi' : 'Generate Link'}</button>
          </div>
        )}

        {linkError && <div style={{ fontSize: 11, color: '#ef4444', marginBottom: 8 }}>⚠ {linkError}</div>}

        {form.meetLink && (
          <div style={{ fontSize: 11, color: '#10b981', fontWeight: 600, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
            ✅ Link ready
            {lead.phone && (
              <a href={`https://wa.me/${lead.phone.replace(/\D/g,'')}?text=${encodeURIComponent('Hi! Join our meeting: ' + form.meetLink)}`}
                target="_blank" rel="noopener noreferrer" style={{ color: '#25d366', textDecoration: 'none', fontWeight: 700 }}>
                Share on WhatsApp →
              </a>
            )}
          </div>
        )}

        <button onClick={handleSchedule} disabled={!form.date || saving} style={{
          width: '100%', padding: '10px', borderRadius: 10, border: 'none',
          background: form.date ? 'linear-gradient(135deg, #7c3aed, #3b82f6)' : '#e2e8f0',
          color: form.date ? '#fff' : '#94a3b8', cursor: form.date ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 700,
        }}>{saving ? 'Scheduling…' : saved ? '✅ Scheduled!' : 'Schedule Meeting'}</button>
      </div>

      {/* Past meetings */}
      {meetings.length > 0 && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 800, color: '#64748b', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: 10 }}>Meetings with {lead.name}</div>
          {meetings.map(m => {
            const p = QUICK_PLATFORMS.find(pl => pl.id === m.platform) || QUICK_PLATFORMS[0];
            const statusColor = m.status === 'completed' ? '#10b981' : m.status === 'cancelled' ? '#ef4444' : '#0ea5e9';
            return (
              <div key={m.id} style={{ background: 'rgba(0,0,0,0.02)', borderRadius: 10, border: '1px solid rgba(0,0,0,0.06)', padding: '12px 14px', marginBottom: 8, borderLeft: `3px solid ${p.color}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 16 }}>{p.icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>{m.title || m.meetingType || p.label + ' meeting'}</div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>{m.date}{m.time ? ' · ' + m.time : ''} · {m.duration || 30} min</div>
                  </div>
                  <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20, fontWeight: 700, background: statusColor + '15', color: statusColor, flexShrink: 0 }}>{m.status}</span>
                </div>
                {m.meetLink && (
                  <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                    <a href={m.meetLink} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: p.color, textDecoration: 'none', fontWeight: 700, padding: '4px 10px', borderRadius: 6, background: p.color + '10', border: `1px solid ${p.color}30` }}>
                      🔗 Join →
                    </a>
                    <button onClick={() => navigator.clipboard?.writeText(m.meetLink)} style={{ background: 'none', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 6, cursor: 'pointer', fontSize: 11, padding: '4px 8px', color: '#64748b' }}>📋</button>
                    {lead.phone && (
                      <a href={`https://wa.me/${lead.phone.replace(/\D/g,'')}?text=${encodeURIComponent('Meeting link: ' + m.meetLink)}`}
                        target="_blank" rel="noopener noreferrer" style={{ fontSize: 11, color: '#25d366', textDecoration: 'none', fontWeight: 700, padding: '4px 10px', borderRadius: 6, background: 'rgba(37,211,102,0.08)', border: '1px solid rgba(37,211,102,0.25)' }}>📱</a>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {meetings.length === 0 && (
        <div style={{ textAlign: 'center', padding: '20px 0', color: '#94a3b8', fontSize: 13 }}>No meetings scheduled yet</div>
      )}
    </div>
  );
}

// ─── IN-APP EMAIL COMPOSER ────────────────────────────────────────────────────
function EmailComposerModal({ lead, currentUser, onClose }) {
  const { firebaseUser } = useAuth();
  const [subject, setSubject]   = useState(`Re: ${lead.name || lead.email || ''}`);
  const [body,    setBody]      = useState('');
  const [sending, setSending]   = useState(false);
  const [result,  setResult]    = useState(null); // {ok, msg}
  const [oauthEmail, setOauthEmail] = useState('');

  // Detect which work email to show as "From"
  useEffect(() => {
    const provider = firebaseUser?.providerData?.find(
      p => p.providerId === 'microsoft.com' || p.providerId === 'google.com'
    );
    if (provider?.email) { setOauthEmail(provider.email); return; }
    // Try fetching oauthStatus from backend
    if (!currentUser?.companyId || !currentUser?.id) return;
    fetch(`${BACKEND_URL}/api/oauth/status?companyId=${encodeURIComponent(currentUser.companyId)}&userId=${encodeURIComponent(currentUser.id)}`)
      .then(r => r.json()).then(s => {
        const email = s?.microsoft?.email || s?.google?.email || firebaseUser?.email || '';
        setOauthEmail(email);
      }).catch(() => setOauthEmail(firebaseUser?.email || ''));
  }, [currentUser?.companyId, currentUser?.id, firebaseUser]);

  const handleSend = async () => {
    if (!body.trim() || !subject.trim()) return;
    setSending(true);
    setResult(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/email/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyId: currentUser.companyId,
          userId: currentUser.id,
          to: lead.email,
          toName: lead.name || '',
          subject,
          body,
          fromEmail: oauthEmail,
        }),
      });
      const json = await res.json();
      if (res.ok && json.success) {
        setResult({ ok: true, msg: `Email sent via ${json.provider === 'microsoft' ? 'Outlook' : 'Gmail'}` });
        setTimeout(onClose, 2000);
      } else if (json.error === 'no_oauth') {
        // Backend has no tokens — open system mail client as fallback
        window.open(`mailto:${lead.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
        onClose();
      } else {
        setResult({ ok: false, msg: json.error || 'Send failed' });
      }
    } catch {
      setResult({ ok: false, msg: 'Network error — check your connection' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
      <div style={{ width: 560, maxWidth: '95vw', background: 'var(--card-bg, #fff)', borderRadius: 16, boxShadow: '0 24px 60px rgba(0,0,0,0.25)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border, #e2e8f0)', display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 20 }}>📧</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary, #0f172a)' }}>New Email</div>
            <div style={{ fontSize: 11, color: 'var(--text-secondary, #64748b)' }}>to {lead.name || lead.email}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: 'var(--text-secondary, #94a3b8)', lineHeight: 1 }}>✕</button>
        </div>

        {/* Fields */}
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* From */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, borderBottom: '1px solid var(--border, #f1f5f9)', paddingBottom: 8 }}>
            <span style={{ color: 'var(--text-secondary, #64748b)', width: 52, flexShrink: 0 }}>From</span>
            <span style={{ color: 'var(--text-primary, #0f172a)', fontWeight: 500 }}>{oauthEmail || '(your work email)'}</span>
          </div>
          {/* To */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, borderBottom: '1px solid var(--border, #f1f5f9)', paddingBottom: 8 }}>
            <span style={{ color: 'var(--text-secondary, #64748b)', width: 52, flexShrink: 0 }}>To</span>
            <span style={{ color: 'var(--text-primary, #0f172a)' }}>{lead.email}</span>
          </div>
          {/* Subject */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, borderBottom: '1px solid var(--border, #f1f5f9)', paddingBottom: 8 }}>
            <span style={{ color: 'var(--text-secondary, #64748b)', width: 52, flexShrink: 0 }}>Subject</span>
            <input
              value={subject} onChange={e => setSubject(e.target.value)}
              style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 13, color: 'var(--text-primary, #0f172a)' }}
              placeholder="Subject…"
            />
          </div>
          {/* Body */}
          <textarea
            value={body} onChange={e => setBody(e.target.value)}
            rows={8}
            placeholder={`Write your message to ${lead.name || 'this student'}…`}
            style={{ resize: 'vertical', border: 'none', outline: 'none', background: 'transparent', fontSize: 13, lineHeight: 1.7, color: 'var(--text-primary, #0f172a)', fontFamily: 'inherit' }}
          />
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border, #f1f5f9)', display: 'flex', alignItems: 'center', gap: 10 }}>
          {result && (
            <div style={{ flex: 1, fontSize: 12, color: result.ok ? '#10b981' : '#ef4444', fontWeight: 500 }}>
              {result.ok ? '✅' : '⚠️'} {result.msg}
            </div>
          )}
          {!result && <div style={{ flex: 1 }} />}
          <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border, #e2e8f0)', background: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary, #64748b)' }}>Cancel</button>
          <button
            onClick={handleSend} disabled={sending || !body.trim() || !subject.trim() || !lead.email}
            style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#3b82f6,#2563eb)', color: '#fff', fontSize: 13, fontWeight: 700, cursor: (sending || !body.trim()) ? 'default' : 'pointer', opacity: (sending || !body.trim()) ? 0.6 : 1 }}
          >
            {sending ? 'Sending…' : '📤 Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── LEAD DETAIL PANEL ────────────────────────────────────────────────────────
function LeadDetailPanel({ lead, onClose }) {
  const {
    users, getUserById, updateLead, addFollowup,
    getLeadFollowups, pipelineStages, updateFollowup,
    deleteFollowup, currentUser, addAssessment, subscribeAssessmentsForLead,
    logNoteAdded, customSources, customFields, company, priorities,
  } = useCRM();
  const getSourceIcon = useCallback((sourceValue) => {
    const allS = [...ALL_SOURCES, ...(customSources || []).filter(cs => !ALL_SOURCES.find(s => s.value === cs.value))];
    const found = allS.find(s => s.value === sourceValue);
    return found?.icon || SOURCE_ICONS[sourceValue] || '🏷️';
  }, [customSources]);

  const [tab, setTab] = useState('overview');
  // ── inline contact editing
  const [editContact, setEditContact] = useState(false);
  const [editPhone, setEditPhone] = useState(lead.phone || '');
  const [editEmail, setEditEmail] = useState(lead.email || '');
  const [editName,  setEditName]  = useState(getLeadName(lead) || '');
  const [savingContact, setSavingContact] = useState(false);
  const [studentProfile, setStudentProfile] = useState(null);
  const [fupForm, setFupForm] = useState({ type: 'call', title: '', dueDate: '', notes: '', priority: 'medium' });
  const [showEmailComposer, setShowEmailComposer] = useState(false);
  const [notes, setNotes] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [assessments, setAssessments] = useState([]);
  const [sendingAssessment, setSendingAssessment] = useState(false);
  const [assessmentLink, setAssessmentLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);
  const [assessmentError, setAssessmentError] = useState('');

  const assignedUser = useMemo(
    () => getUserById(lead.assignedToId) || users.find(u => u.name === lead.assignedTo) || null,
    [lead.assignedToId, lead.assignedTo, getUserById, users]
  );
  const assignedTeacher = useMemo(
    () => (lead.assignedTeacherId ? getUserById(lead.assignedTeacherId) : null) ||
          (lead.assignedTeacher ? users.find(u => u.name === lead.assignedTeacher) : null) || null,
    [lead.assignedTeacherId, lead.assignedTeacher, getUserById, users]
  );
  const counselorUsers = useMemo(
    () => users.filter(u => u.role === 'counsellor' || u.role === 'counselor' || u.role === 'admin'),
    [users]
  );
  const teacherUsers = useMemo(
    () => users.filter(u => u.role === 'test_prep'),
    [users]
  );

  const leadFollowups = getLeadFollowups(lead.id);
  const currentStage  = pipelineStages.find(s => s.id === lead.status);
  const displayName   = getLeadName(lead);

  useEffect(() => {
    if (tab !== 'notes') return;
    const unsub = subscribeLeadNotes(lead.id, currentUser?.companyId, setNotes);
    return unsub;
  }, [tab, lead.id, currentUser?.companyId]); // eslint-disable-line

  useEffect(() => {
    if (tab !== 'assessment') return;
    const unsub = subscribeAssessmentsForLead(lead.id, setAssessments);
    return unsub;
  }, [tab, lead.id, subscribeAssessmentsForLead]);

  useEffect(() => {
    if (tab !== 'profile') return;
    const companyId = currentUser?.companyId;
    if (!companyId || !lead.id) return;
    const ref = doc(db, 'companies', companyId, 'studentProfiles', lead.id);
    const unsub = onSnapshot(ref, snap => {
      setStudentProfile(snap.exists() ? snap.data() : null);
    });
    return unsub;
  }, [tab, lead.id, currentUser?.companyId]); // eslint-disable-line

  const handleSendAssessment = async () => {
    setSendingAssessment(true);
    setAssessmentError('');
    try {
      const docRef = await addAssessment({
        leadId: lead.id,
        leadName: displayName,
        leadEmail: lead.email || '',
        sentBy: currentUser.name,
      });
      const link = `${window.location.origin}${window.location.pathname}?assessment=${docRef.id}`;
      setAssessmentLink(link);
    } catch (e) {
      console.error('Assessment create failed:', e);
      setAssessmentError(
        e?.code === 'permission-denied'
          ? 'Permission denied — make sure Firestore rules are published.'
          : `Failed to generate link: ${e.message || 'Unknown error'}`
      );
    } finally {
      setSendingAssessment(false);
    }
  };

  const copyAssessmentLink = (link) => {
    navigator.clipboard.writeText(link);
    setLinkCopied(true);
    setTimeout(() => setLinkCopied(false), 2000);
  };

  const displayCountries = () => {
    if (lead.countries?.length) return lead.countries.join(', ');
    return lead.country || '—';
  };

  const handleCall      = () => lead.phone  && window.open(`tel:${lead.phone}`);
  const handleWhatsApp  = () => lead.phone  && window.open(`https://wa.me/${lead.phone.replace(/\D/g, '')}`);
  const handleEmail     = () => lead.email  && setShowEmailComposer(true);

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    try {
      await addLeadNote(currentUser?.companyId || 'default', { leadId: lead.id, content: noteText.trim(), createdBy: currentUser.name || 'Unknown' });
      if (logNoteAdded) logNoteAdded(lead.id, noteText.trim());
      setNoteText('');
    } finally {
      setSavingNote(false);
    }
  };

  const handleAddFollowup = () => {
    if (!fupForm.title || !fupForm.dueDate) return;
    addFollowup({ ...fupForm, leadId: lead.id, assignedTo: assignedUser?.id || lead.assignedToId || '' });
    setFupForm({ type: 'call', title: '', dueDate: '', notes: '', priority: 'medium' });
  };

  const noteTimeDiff = (ts) => {
    const date = getDate(ts);
    if (!date) return '';
    const diff = Math.round((Date.now() - date.getTime()) / 60000);
    if (diff < 1) return 'just now';
    if (diff < 60) return `${diff}m ago`;
    if (diff < 1440) return `${Math.round(diff / 60)}h ago`;
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const testScores = [
    { key: 'ielts', label: 'IELTS' }, { key: 'toefl', label: 'TOEFL' },
    { key: 'pte',   label: 'PTE'   }, { key: 'gre',   label: 'GRE'   },
    { key: 'gmat',  label: 'GMAT'  }, { key: 'sat',   label: 'SAT'   },
    { key: 'act',   label: 'ACT'   }, { key: 'gpa',   label: 'GPA'   },
  ].filter(s => lead[s.key]);

  return (
    <>
    {showEmailComposer && (
      <EmailComposerModal
        lead={lead}
        currentUser={currentUser}
        onClose={() => setShowEmailComposer(false)}
      />
    )}
    <div style={{
      position: 'fixed', inset: 0, zIndex: 500,
      display: 'flex', alignItems: 'stretch', justifyContent: 'flex-end',
      background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)',
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        width: '100%', maxWidth: 540,
        background: '#fff',
        borderLeft: '1px solid rgba(0,0,0,0.08)',
        display: 'flex', flexDirection: 'column',
        animation: 'slideIn 0.25s ease',
        overflowY: 'hidden',
      }}>
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid rgba(0,0,0,0.06)', background: `linear-gradient(135deg, ${currentStage?.color || '#7c3aed'}18, rgba(59,130,246,0.06))`, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <div style={{ width: 46, height: 46, borderRadius: 12, background: `linear-gradient(135deg, ${currentStage?.color || '#7c3aed'}, #3b82f6)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 700, color: '#fff', flexShrink: 0 }}>
                {(displayName || '?')[0].toUpperCase()}
              </div>
              <div>
                <h3 style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>{displayName}</h3>
                <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{lead.email || 'No email'} · {lead.phone}</div>
              </div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 18 }}>✕</button>
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 600, background: `${currentStage?.color || '#7c3aed'}25`, color: currentStage?.color || '#7c3aed', border: `1px solid ${currentStage?.color || '#7c3aed'}40` }}>
              {currentStage?.name || lead.status}
            </span>
            {/* Inline priority picker */}
            {(priorities || [{ value: 'hot', label: 'Hot', emoji: '🔥', color: '#ef4444' }, { value: 'warm', label: 'Warm', emoji: '☀️', color: '#f59e0b' }, { value: 'cold', label: 'Cold', emoji: '❄️', color: '#3b82f6' }]).map(p => ({
              v: p.value, label: `${p.emoji} ${p.label}`, active: p.color,
            })).map(p => (
              <button key={p.v} onClick={() => updateLead(lead.id, { priority: p.v })}
                style={{
                  fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 600, cursor: 'pointer', border: 'none',
                  background: lead.priority === p.v ? p.active : 'rgba(0,0,0,0.06)',
                  color: lead.priority === p.v ? '#fff' : '#64748b',
                  transition: 'all 0.15s',
                }}>
                {p.label}
              </button>
            ))}
            <span style={{ fontSize: 11, color: '#64748b' }}>{getSourceIcon(lead.source)} {lead.source}</span>
          </div>
        </div>

        {/* Tabs */}
        <div style={{ padding: '0 24px', borderBottom: '1px solid rgba(0,0,0,0.05)', flexShrink: 0, overflowX: 'auto' }}>
          <div className="tab-bar" style={{ display: 'inline-flex', whiteSpace: 'nowrap' }}>
            {[
              { id: 'overview',    label: 'Overview' },
              { id: 'profile',     label: '📋 Profile' },
              { id: 'whatsapp',   label: lead.waUnread > 0 ? `💬 WhatsApp (${lead.waUnread})` : '💬 WhatsApp' },
              { id: 'followups',  label: 'Follow-ups' },
              { id: 'meeting',    label: '📅 Meeting' },
              { id: 'notes',      label: 'Notes' },
              { id: 'assessment', label: 'Assessment' },
            ].map(t => (
              <div key={t.id} className={`tab-item${tab === t.id ? ' active' : ''}`} onClick={() => setTab(t.id)}>{t.label}</div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>

          {/* ── OVERVIEW ── */}
          {tab === 'overview' && (
            <div>
              {/* ── Quick Actions ── */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                <button onClick={handleCall} style={{ padding: '10px 6px', borderRadius: 9, border: '1px solid rgba(16,185,129,0.3)', background: 'rgba(16,185,129,0.08)', color: '#10b981', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                  📞 Call
                </button>
                <button onClick={() => setTab('whatsapp')} style={{ padding: '10px 6px', borderRadius: 9, border: '1px solid rgba(37,211,102,0.3)', background: 'rgba(37,211,102,0.08)', color: '#22c55e', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                  💬 WhatsApp
                </button>
                <button onClick={handleEmail} style={{ padding: '10px 6px', borderRadius: 9, border: '1px solid rgba(59,130,246,0.3)', background: 'rgba(59,130,246,0.08)', color: '#3b82f6', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                  📧 Email
                </button>
                <button onClick={() => setTab('meeting')} style={{ padding: '10px 6px', borderRadius: 9, border: '1px solid rgba(124,58,237,0.3)', background: 'rgba(124,58,237,0.08)', color: '#7c3aed', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5 }}>
                  📅 Meeting
                </button>
              </div>

              {/* ── Contact Info (editable) ── */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, letterSpacing: '0.4px' }}>CONTACT INFO</div>
                  {!editContact ? (
                    <button onClick={() => { setEditName(getLeadName(lead) || ''); setEditPhone(lead.phone || ''); setEditEmail(lead.email || ''); setEditContact(true); }}
                      style={{ fontSize: 11, color: '#7c3aed', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>✏️ Edit</button>
                  ) : (
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={async () => {
                          setSavingContact(true);
                          const nameParts = editName.trim().split(/\s+/);
                          const firstName = nameParts[0] || '';
                          const lastName  = nameParts.slice(1).join(' ') || '';
                          await updateLead(lead.id, { phone: editPhone, email: editEmail, name: editName.trim(), firstName, lastName });
                          setSavingContact(false); setEditContact(false);
                        }}
                        disabled={savingContact}
                        style={{ fontSize: 11, color: '#fff', background: '#7c3aed', border: 'none', borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontWeight: 600 }}>
                        {savingContact ? '…' : 'Save'}
                      </button>
                      <button onClick={() => setEditContact(false)}
                        style={{ fontSize: 11, color: '#64748b', background: 'rgba(0,0,0,0.05)', border: 'none', borderRadius: 6, padding: '3px 10px', cursor: 'pointer' }}>Cancel</button>
                    </div>
                  )}
                </div>
                <div style={{ background: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 10, overflow: 'hidden' }}>
                  {[
                    { icon: '👤', label: 'Full Name', key: 'name',  val: editName,  set: setEditName  },
                    { icon: '📞', label: 'Phone',     key: 'phone', val: editPhone, set: setEditPhone },
                    { icon: '📧', label: 'Email',     key: 'email', val: editEmail, set: setEditEmail },
                  ].map((item, i) => (
                    <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderTop: i > 0 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
                      <span style={{ fontSize: 14, flexShrink: 0 }}>{item.icon}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500, marginBottom: 2 }}>{item.label}</div>
                        {editContact ? (
                          <input value={item.val} onChange={e => item.set(e.target.value)}
                            className="crm-input" style={{ fontSize: 13, padding: '4px 8px', width: '100%' }} />
                        ) : (
                          <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 500, wordBreak: 'break-all' }}>
                            {lead[item.key] || <span style={{ color: '#94a3b8' }}>—</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                    <span style={{ fontSize: 14, flexShrink: 0 }}>🏷️</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500, marginBottom: 2 }}>Source</div>
                      <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 500 }}>{getSourceIcon(lead.source)} {lead.source || '—'}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                    <span style={{ fontSize: 14, flexShrink: 0 }}>📅</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500, marginBottom: 2 }}>Added</div>
                      <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 500 }}>
                        {lead.createdAt ? getDate(lead.createdAt)?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Score + Assigned ── */}
              {(() => {
                const liveScore = computeLeadScore(lead, pipelineStages);
                const scoreColor = liveScore >= 75 ? '#10b981' : liveScore >= 50 ? '#f59e0b' : '#ef4444';
                const scoreLabel = liveScore >= 75 ? 'High Potential' : liveScore >= 50 ? 'Good Prospect' : 'Needs Nurturing';
                return (
                  <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
                    <div className="glass-card" style={{ flex: 1, padding: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 50, height: 50, borderRadius: '50%', border: `3px solid ${scoreColor}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: scoreColor + '12' }}>
                        <span style={{ fontSize: 14, fontWeight: 800, color: scoreColor }}>{liveScore}</span>
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: '#64748b', marginBottom: 2 }}>LEAD SCORE</div>
                        <div style={{ fontSize: 12, color: '#0f172a', fontWeight: 600 }}>{scoreLabel}</div>
                      </div>
                    </div>
                    <div className="glass-card" style={{ flex: 1, padding: 14 }}>
                      <div style={{ fontSize: 10, color: '#64748b', marginBottom: 6 }}>COUNSELOR</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div className="avatar" style={{ background: assignedUser?.avatar?.bg || '#7c3aed', color: assignedUser?.avatar?.color || '#fff', width: 28, height: 28, fontSize: 10 }}>
                          {(assignedUser?.name || '?').split(' ').map(n => n[0]).join('')}
                        </div>
                        <span style={{ fontSize: 13, color: '#0f172a', fontWeight: 500 }}>{assignedUser?.name || 'Unassigned'}</span>
                      </div>
                      {teacherUsers.length > 0 && (
                        <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid rgba(0,0,0,0.06)' }}>
                          <div style={{ fontSize: 10, color: '#64748b', marginBottom: 6 }}>TEST PREP TEACHER</div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div className="avatar" style={{ background: assignedTeacher?.avatar?.bg || '#0ea5e9', color: assignedTeacher?.avatar?.color || '#fff', width: 28, height: 28, fontSize: 10 }}>
                              {(assignedTeacher?.name || '—').split(' ').map(n => n[0]).join('')}
                            </div>
                            <span style={{ fontSize: 13, color: '#0f172a', fontWeight: 500 }}>{assignedTeacher?.name || 'Not assigned'}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* ── Quick Facts ── */}
              {(() => {
                const facts = [
                  { icon: '🎓', label: 'Course', val: lead.course },
                  { icon: '🌍', label: 'Countries', val: (lead.countries || []).join(', ') || lead.country },
                  { icon: '💰', label: 'Budget', val: lead.budget },
                  { icon: '📅', label: 'Intake', val: lead.intake },
                  { icon: '📋', label: 'Qualification', val: lead.qualification },
                  { icon: '🔤', label: 'IELTS', val: lead.ielts },
                ].filter(f => f.val);
                if (!facts.length) return null;
                return (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, letterSpacing: '0.4px', marginBottom: 8 }}>QUICK FACTS</div>
                    <div style={{ background: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 10, overflow: 'hidden' }}>
                      {facts.map((f, i) => (
                        <div key={f.label} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', borderTop: i > 0 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
                          <span style={{ fontSize: 14, flexShrink: 0 }}>{f.icon}</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 500 }}>{f.label}</div>
                            <div style={{ fontSize: 12, color: '#0f172a', fontWeight: 600, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.val}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, letterSpacing: '0.4px', marginBottom: 8 }}>UPDATE STAGE</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {pipelineStages.map(s => {
                    const active = lead.status === s.id;
                    return (
                      <button key={s.id} onClick={() => updateLead(lead.id, { status: s.id })}
                        style={{
                          padding: '6px 11px', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                          background: active ? `${s.color}28` : 'rgba(0,0,0,0.04)',
                          border: `2px solid ${active ? s.color : 'transparent'}`,
                          color: active ? s.color : '#64748b',
                          transition: 'all 0.15s',
                        }}>
                        {active ? '✓ ' : ''}{s.name}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, letterSpacing: '0.4px', marginBottom: 10 }}>ACADEMIC PROFILE</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {[
                    { label: 'Countries',     value: displayCountries() },
                    { label: 'Course',        value: lead.course },
                    { label: 'Intake',        value: lead.intake },
                    { label: 'Qualification', value: lead.qualification },
                    { label: 'Budget',        value: lead.budget ? `₹${Number(lead.budget).toLocaleString('en-IN')}` : null },
                  ].map(item => (
                    <div key={item.label} style={{ background: 'rgba(0,0,0,0.03)', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ fontSize: 10, color: '#475569', marginBottom: 3, fontWeight: 500 }}>{item.label}</div>
                      <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 500 }}>{item.value || '—'}</div>
                    </div>
                  ))}
                </div>
              </div>

              {testScores.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, letterSpacing: '0.4px', marginBottom: 10 }}>TEST SCORES</div>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {testScores.map(s => (
                      <div key={s.key} style={{ background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.2)', borderRadius: 8, padding: '8px 14px', textAlign: 'center', minWidth: 64 }}>
                        <div style={{ fontSize: 10, color: '#7c3aed', fontWeight: 600 }}>{s.label}</div>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginTop: 2 }}>{lead[s.key]}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Facebook Ad Analytics (only shown for FB leads) ── */}
              {lead.source === 'facebook' && (lead.fbCampaignName || lead.fbAdsetName || lead.fbAdName || lead.fbPageName) && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <div style={{ width: 16, height: 16, borderRadius: 4, background: 'rgba(24,119,242,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <svg viewBox="0 0 36 36" width="10" height="10" fill="none"><path d="M3 21.5C3 24.5 4.5 26.5 6.5 26.5C8.2 26.5 9.2 25.5 10.7 23.2L13.5 18.8L10.8 14.5C9.2 12.1 8.1 11.5 6.8 11.5C4.6 11.5 3 13.8 3 17.5V21.5Z" fill="#0081FB"/><path d="M13.5 18.8L16.2 14.5C17.8 12.1 19.1 11.5 20.5 11.5C22.7 11.5 24 13.5 24 17C24 20.5 22.5 22.8 20.5 22.8C19.2 22.8 18.3 22 16.8 19.7L13.5 18.8Z" fill="#0064E0"/><path d="M24 17C24 13.5 25.5 11.5 27.5 11.5C29.7 11.5 31 13.5 31 17C31 20.5 29.5 22.5 27.5 22.5C25.5 22.5 24 20.5 24 17Z" fill="#0081FB"/></svg>
                    </div>
                    <div style={{ fontSize: 11, color: '#1877f2', fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase' }}>Facebook Ad Info</div>
                  </div>
                  <div style={{ background: 'rgba(24,119,242,0.04)', border: '1px solid rgba(24,119,242,0.12)', borderRadius: 10, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[
                      { icon: '📢', label: 'Campaign',  value: lead.fbCampaignName },
                      { icon: '🎯', label: 'Ad Set',    value: lead.fbAdsetName },
                      { icon: '🖼️', label: 'Ad',        value: lead.fbAdName },
                      { icon: '📄', label: 'Form',      value: lead.fbFormName },
                      { icon: '📱', label: 'Page',      value: lead.fbPageName },
                      { icon: '🌐', label: 'Platform',  value: lead.fbPlatform },
                    ].filter(item => item.value).map(item => (
                      <div key={item.label} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <span style={{ fontSize: 13, flexShrink: 0 }}>{item.icon}</span>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontSize: 10, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{item.label}</div>
                          <div style={{ fontSize: 12, color: '#0f172a', fontWeight: 500, marginTop: 1, wordBreak: 'break-word' }}>{item.value}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* ── Custom Fields (org-defined, keyed as cf_xxx) ── */}
              {customFields && customFields.length > 0 && customFields.some(f => {
                const val = lead['cf_' + f.id];
                return val !== undefined && val !== null && val !== '';
              }) && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, letterSpacing: '0.4px', marginBottom: 8 }}>CUSTOM FIELDS</div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {customFields.map(field => {
                      const val = lead['cf_' + field.id];
                      if (val === undefined || val === null || val === '') return null;
                      return (
                        <div key={field.id} style={{ background: 'rgba(0,0,0,0.03)', borderRadius: 8, padding: '10px 12px' }}>
                          <div style={{ fontSize: 10, color: '#475569', marginBottom: 3, fontWeight: 500 }}>{field.label}</div>
                          <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 500 }}>
                            {field.type === 'checkbox' ? (val ? '✓ Yes' : '✗ No') : String(val)}
                          </div>
                        </div>
                      );
                    }).filter(Boolean)}
                  </div>
                </div>
              )}

              {/* ── Form Submission Data — all fields stored on the lead doc ── */}
              {(() => {
                // Keys already rendered above or internal system keys
                const SYSTEM_KEYS = new Set([
                  'id','companyId','createdAt','updatedAt','_importedAt',
                  'name','firstName','lastName','phone','email','source',
                  'status','priority','score','assignedTo','assignedToId',
                  'assignedTeacher','assignedTeacherId',
                  'course','country','countries','intake','qualification','budget',
                  'ielts','toefl','pte','gre','gmat','sat','act','gpa',
                  'dob','city','message',
                  'fbLeadId','fbFormId','fbPageId','fbPageName','fbFormName',
                  'fbAdId','fbAdName','fbAdsetId','fbAdsetName',
                  'fbCampaignId','fbCampaignName','fbPlatform',
                  'waUnread','waLastMsg','waLastMsgAt',
                  'formId','assignedAt',
                ]);
                // cf_ keys are already shown in Custom Fields section
                const cfIds = new Set((customFields || []).map(f => 'cf_' + f.id));

                // Pretty label for known field keys
                const FIELD_LABELS = {
                  whatsapp: 'WhatsApp', address: 'Address', state: 'State / Province',
                  gender: 'Gender', nationality: 'Nationality', passport: 'Passport No.',
                  school: 'School / College', grade: 'Grade / Class',
                  board: 'Board / Curriculum', graduationYear: 'Graduation Year',
                  parentName: 'Parent Name', parentPhone: 'Parent Phone',
                  parentEmail: 'Parent Email', parentRelation: 'Relation to Student',
                  gpa: 'GPA', act: 'ACT',
                };

                const extraFields = Object.entries(lead)
                  .filter(([k, v]) => {
                    if (SYSTEM_KEYS.has(k)) return false;
                    if (cfIds.has(k)) return false;
                    if (v === null || v === undefined || v === '') return false;
                    if (Array.isArray(v) && v.length === 0) return false;
                    if (typeof v === 'object' && !Array.isArray(v)) return false; // skip nested objects
                    return true;
                  })
                  .map(([k, v]) => ({
                    key: k,
                    label: FIELD_LABELS[k] || k.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').replace(/^./, s => s.toUpperCase()).trim(),
                    value: Array.isArray(v) ? v.join(', ') : String(v),
                  }));

                if (!extraFields.length) return null;
                return (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, letterSpacing: '0.4px', marginBottom: 8 }}>FORM SUBMISSION DATA</div>
                    <div style={{ background: 'rgba(124,58,237,0.03)', border: '1px solid rgba(124,58,237,0.1)', borderRadius: 10, overflow: 'hidden' }}>
                      {extraFields.map((f, i) => (
                        <div key={f.key} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 14px', borderTop: i > 0 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{f.label}</div>
                            <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 500, marginTop: 2, wordBreak: 'break-word' }}>{f.value}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}

              {/* ── Reassign Counselor ── */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, letterSpacing: '0.4px', marginBottom: 8 }}>ASSIGN COUNSELOR</div>
                <select className="crm-select" style={{ width: '100%' }}
                  value={lead.assignedToId || ''}
                  onChange={e => {
                    const u = counselorUsers.find(x => x.id === e.target.value);
                    updateLead(lead.id, { assignedToId: e.target.value, assignedTo: u?.name || '' });
                  }}>
                  <option value="">Unassigned</option>
                  {counselorUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
              {/* ── Assign Teacher ── */}
              {teacherUsers.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, letterSpacing: '0.4px', marginBottom: 8 }}>ASSIGN TEST PREP TEACHER</div>
                  <select className="crm-select" style={{ width: '100%' }}
                    value={lead.assignedTeacherId || ''}
                    onChange={e => {
                      const u = teacherUsers.find(x => x.id === e.target.value);
                      updateLead(lead.id, { assignedTeacherId: e.target.value, assignedTeacher: u?.name || '' });
                    }}>
                    <option value="">Not assigned</option>
                    {teacherUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* ── PROFILE TAB (UPS form data section-wise) ── */}
          {tab === 'profile' && (() => {
            const p = studentProfile;
            const sp = { fontSize: 12, color: '#0f172a', fontWeight: 500, marginTop: 2, wordBreak: 'break-word' };
            const lp = { fontSize: 10, color: '#94a3b8', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.5 };
            const rowStyle = (i) => ({ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 14px', borderTop: i > 0 ? '1px solid rgba(0,0,0,0.05)' : 'none' });
            const box = { background: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 10, overflow: 'hidden', marginBottom: 16 };
            const sectionTitle = (t) => <div style={{ fontSize: 11, color: '#64748b', fontWeight: 700, letterSpacing: '0.4px', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>{t}</div>;
            const val = (v) => v || <span style={{ color: '#cbd5e1' }}>—</span>;

            if (!p) return (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#0f172a', marginBottom: 6 }}>No Profile Submitted Yet</div>
                <div style={{ fontSize: 12, color: '#94a3b8', lineHeight: 1.7 }}>
                  The student hasn't filled in the UPS profile form yet.<br />
                  Share a form link from the Applications page.
                </div>
              </div>
            );

            return (
              <div>
                {/* Personal */}
                {sectionTitle('👤 Personal Information')}
                <div style={box}>
                  {[
                    { icon: '👤', label: 'Full Name',     v: [p.firstName, p.lastName].filter(Boolean).join(' ') || p.name },
                    { icon: '📅', label: 'Date of Birth', v: p.dob },
                    { icon: '⚧',  label: 'Gender',        v: p.gender },
                    { icon: '🌏', label: 'Nationality',   v: p.nationality },
                    { icon: '🛂', label: 'Passport No.',  v: p.passport },
                    { icon: '📞', label: 'Phone',         v: p.phone },
                    { icon: '🏠', label: 'Address',       v: p.address },
                  ].map((r, i) => r.v ? (
                    <div key={r.label} style={rowStyle(i)}>
                      <span style={{ fontSize: 14, flexShrink: 0 }}>{r.icon}</span>
                      <div><div style={lp}>{r.label}</div><div style={sp}>{val(r.v)}</div></div>
                    </div>
                  ) : null).filter(Boolean)}
                </div>

                {/* Parents */}
                {sectionTitle('👨‍👩‍👧 Parents / Guardians')}
                <div style={box}>
                  {[
                    { icon: '👨', label: "Father's Name",       v: p.fatherName },
                    { icon: '📞', label: "Father's Phone",      v: p.fatherPhone },
                    { icon: '📧', label: "Father's Email",      v: p.fatherEmail },
                    { icon: '💼', label: "Father's Occupation", v: p.fatherOccupation },
                    { icon: '👩', label: "Mother's Name",       v: p.motherName },
                    { icon: '📞', label: "Mother's Phone",      v: p.motherPhone },
                    { icon: '📧', label: "Mother's Email",      v: p.motherEmail },
                    { icon: '💼', label: "Mother's Occupation", v: p.motherOccupation },
                  ].map((r, i) => r.v ? (
                    <div key={r.label} style={rowStyle(i)}>
                      <span style={{ fontSize: 14, flexShrink: 0 }}>{r.icon}</span>
                      <div><div style={lp}>{r.label}</div><div style={sp}>{val(r.v)}</div></div>
                    </div>
                  ) : null).filter(Boolean)}
                </div>

                {/* Education History */}
                {p.eduHistory?.some(r => r.school) && (
                  <>
                    {sectionTitle('🏫 Educational History')}
                    <div style={{ overflowX: 'auto', marginBottom: 16 }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, minWidth: 500 }}>
                        <thead>
                          <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                            {['Level', 'School', 'Location', 'From', 'To', 'Curriculum', '%'].map(h => (
                              <th key={h} style={{ padding: '6px 8px', textAlign: 'left', color: '#475569', fontWeight: 700, whiteSpace: 'nowrap' }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {p.eduHistory.filter(r => r.school).map((r, i) => (
                            <tr key={i} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '7px 8px', fontWeight: 600, color: '#475569', whiteSpace: 'nowrap' }}>{r.level}</td>
                              <td style={{ padding: '7px 8px', color: '#0f172a' }}>{r.school}</td>
                              <td style={{ padding: '7px 8px', color: '#64748b' }}>{r.location || '—'}</td>
                              <td style={{ padding: '7px 8px', color: '#64748b', whiteSpace: 'nowrap' }}>{r.fromDate || '—'}</td>
                              <td style={{ padding: '7px 8px', color: '#64748b', whiteSpace: 'nowrap' }}>{r.toDate || '—'}</td>
                              <td style={{ padding: '7px 8px', color: '#64748b' }}>{r.curriculum || '—'}</td>
                              <td style={{ padding: '7px 8px', color: '#0f172a', fontWeight: 600 }}>{r.marks || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {p.subjectsEnrolled && (
                      <div style={{ background: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.06)', borderRadius: 10, padding: '10px 14px', marginBottom: 16 }}>
                        <div style={lp}>Subjects Currently Enrolled In</div>
                        <div style={{ ...sp, marginTop: 4 }}>{p.subjectsEnrolled}</div>
                      </div>
                    )}
                  </>
                )}

                {/* Test Scores */}
                {sectionTitle('📝 Test Scores')}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
                  {[
                    { label: 'IELTS',    v: p.ieltsScore,         sub: p.ieltsTestDate },
                    { label: 'TOEFL',    v: p.toeflScore,         sub: p.toeflTestDate },
                    { label: 'SAT Att.1',v: p.satAttempt1Total,   sub: `V:${p.satAttempt1Verbal||'—'} M:${p.satAttempt1Math||'—'}` },
                    { label: 'SAT Att.2',v: p.satAttempt2Total,   sub: `V:${p.satAttempt2Verbal||'—'} M:${p.satAttempt2Math||'—'}` },
                    { label: 'GRE',      v: p.greScore,           sub: null },
                    { label: 'GMAT',     v: p.gmatScore,          sub: null },
                    { label: 'ACT',      v: p.actScore,           sub: null },
                  ].filter(s => s.v).map(s => (
                    <div key={s.label} style={{ background: 'rgba(124,58,237,0.06)', border: '1px solid rgba(124,58,237,0.15)', borderRadius: 8, padding: '9px 12px' }}>
                      <div style={{ fontSize: 10, color: '#7c3aed', fontWeight: 700 }}>{s.label}</div>
                      <div style={{ fontSize: 17, fontWeight: 800, color: '#0f172a', margin: '2px 0' }}>{s.v}</div>
                      {s.sub && <div style={{ fontSize: 10, color: '#94a3b8' }}>{s.sub}</div>}
                    </div>
                  ))}
                </div>
                {p.apTests?.some(r => r.subject) && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 10, color: '#64748b', fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>AP Tests</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {p.apTests.filter(r => r.subject).map((r, i) => (
                        <div key={i} style={{ background: 'rgba(59,130,246,0.06)', border: '1px solid rgba(59,130,246,0.15)', borderRadius: 8, padding: '6px 10px' }}>
                          <div style={{ fontSize: 11, fontWeight: 600, color: '#1d4ed8' }}>{r.subject}</div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>Score: {r.score || '—'}</div>
                          {r.dateTaken && <div style={{ fontSize: 10, color: '#94a3b8' }}>{r.dateTaken}</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Educational Objective */}
                {sectionTitle('🎯 Educational Objective')}
                <div style={box}>
                  {[
                    { icon: '🎓', label: 'Degree Types',      v: p.degreeTypes?.join(', ') },
                    { icon: '📚', label: 'Intended Major 1',  v: p.intendedMajor1 },
                    { icon: '📚', label: 'Intended Major 2',  v: p.intendedMajor2 },
                    { icon: '🗓', label: 'Semester',           v: p.semesterPreference },
                    { icon: '📅', label: 'Year',              v: p.yearPreference },
                    { icon: '🌍', label: 'Preferred Locations',v: p.preferredLocations },
                    { icon: '💰', label: 'Budget (US$/year)', v: p.budget ? `$${Number(p.budget).toLocaleString()}` : null },
                    { icon: '🤝', label: 'Financial Support', v: p.financialSupport },
                    { icon: '📧', label: 'App Email',         v: p.appEmail },
                    { icon: '🔑', label: 'Common App Login',  v: p.commonAppLogin },
                    { icon: '🔑', label: 'UC App Login',      v: p.ucAppLogin },
                  ].map((r, i) => r.v ? (
                    <div key={r.label} style={rowStyle(i)}>
                      <span style={{ fontSize: 14, flexShrink: 0 }}>{r.icon}</span>
                      <div><div style={lp}>{r.label}</div><div style={sp}>{val(r.v)}</div></div>
                    </div>
                  ) : null).filter(Boolean)}
                </div>

                {/* Photo */}
                {p.photoUrl && (
                  <div style={{ marginBottom: 16 }}>
                    {sectionTitle('📸 Photo')}
                    <img src={p.photoUrl} alt="Student" style={{ width: 80, height: 100, objectFit: 'cover', borderRadius: 10, border: '2px solid #e2e8f0' }} />
                  </div>
                )}

                {p.testimonialText && (
                  <div style={{ background: 'rgba(124,58,237,0.04)', border: '1px solid rgba(124,58,237,0.15)', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
                    <div style={lp}>Student's Experience</div>
                    <div style={{ ...sp, lineHeight: 1.7, marginTop: 4, fontStyle: 'italic' }}>"{p.testimonialText}"</div>
                  </div>
                )}

                {p.filledAt && (
                  <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', paddingTop: 8 }}>
                    Form submitted: {new Date(p.filledAt.seconds ? p.filledAt.seconds * 1000 : p.filledAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                  </div>
                )}
              </div>
            );
          })()}

          {/* ── WHATSAPP TAB ── */}
          {tab === 'whatsapp' && (
            <WAChatTab lead={lead} currentUser={currentUser} />
          )}

          {/* ── FOLLOWUPS ── */}
          {tab === 'followups' && (
            <div>
              <div className="glass-card" style={{ padding: 16, marginBottom: 20, borderLeft: '2px solid #7c3aed' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 12 }}>Schedule Follow-up</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                  <select className="crm-select" value={fupForm.type} onChange={e => setFupForm(p => ({ ...p, type: e.target.value }))}>
                    <option value="call">📞 Call</option>
                    <option value="email">📧 Email</option>
                    <option value="whatsapp">💬 WhatsApp</option>
                    <option value="meeting">🗓 Meeting</option>
                  </select>
                  <select className="crm-select" value={fupForm.priority} onChange={e => setFupForm(p => ({ ...p, priority: e.target.value }))}>
                    <option value="high">🔴 High</option>
                    <option value="medium">🟡 Medium</option>
                    <option value="low">🟢 Low</option>
                  </select>
                </div>
                <input className="crm-input" placeholder="Follow-up title *" value={fupForm.title} onChange={e => setFupForm(p => ({ ...p, title: e.target.value }))} style={{ marginBottom: 8 }} />
                <input className="crm-input" type="datetime-local" value={fupForm.dueDate} onChange={e => setFupForm(p => ({ ...p, dueDate: e.target.value }))} style={{ marginBottom: 8 }} />
                <textarea className="crm-textarea" placeholder="Notes (optional)" value={fupForm.notes} onChange={e => setFupForm(p => ({ ...p, notes: e.target.value }))} style={{ minHeight: 56, marginBottom: 8 }} />
                <button className="btn-primary" onClick={handleAddFollowup} disabled={!fupForm.title || !fupForm.dueDate} style={{ width: '100%', justifyContent: 'center' }}>
                  + Schedule
                </button>
              </div>

              {leadFollowups.length === 0 && (
                <div style={{ textAlign: 'center', color: '#94a3b8', padding: '30px 0', fontSize: 13 }}>No follow-ups yet</div>
              )}
              {leadFollowups.map(f => (
                <div key={f.id} style={{ marginBottom: 10, padding: '12px 14px', background: f.status === 'overdue' ? 'rgba(239,68,68,0.04)' : 'rgba(0,0,0,0.03)', borderRadius: 10, border: `1px solid ${f.status === 'overdue' ? 'rgba(239,68,68,0.2)' : 'rgba(0,0,0,0.05)'}`, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{ fontSize: 18, marginTop: 2 }}>{f.type === 'call' ? '📞' : f.type === 'email' ? '📧' : f.type === 'whatsapp' ? '💬' : '🗓'}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                      <span style={{ fontSize: 13, color: '#0f172a', fontWeight: 500 }}>{f.title}</span>
                      <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 20, fontWeight: 600, background: f.status === 'completed' ? 'rgba(16,185,129,0.15)' : f.status === 'overdue' ? 'rgba(239,68,68,0.15)' : 'rgba(100,116,139,0.15)', color: f.status === 'completed' ? '#10b981' : f.status === 'overdue' ? '#ef4444' : '#64748b' }}>
                        {f.status}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: '#475569' }}>
                      {f.dueDate ? new Date(f.dueDate).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : 'No date'}
                    </div>
                    {f.notes && <div style={{ fontSize: 11, color: '#64748b', marginTop: 3, fontStyle: 'italic' }}>"{f.notes}"</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 5 }}>
                    {f.status !== 'completed' && (
                      <button onClick={() => updateFollowup(f.id, { status: 'completed' })}
                        style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.3)', color: '#10b981', borderRadius: 6, padding: '4px 9px', fontSize: 11, cursor: 'pointer', fontWeight: 600 }}>✓</button>
                    )}
                    <button onClick={() => deleteFollowup(f.id)}
                      style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', color: '#f87171', borderRadius: 6, padding: '4px 8px', fontSize: 11, cursor: 'pointer' }}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── MEETING ── */}
          {tab === 'meeting' && (
            <LeadMeetingTab lead={lead} currentUser={currentUser} company={company} />
          )}

          {/* ── NOTES ── */}
          {tab === 'notes' && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <textarea className="crm-textarea"
                  placeholder="Write a note about this lead…"
                  value={noteText}
                  onChange={e => setNoteText(e.target.value)}
                  style={{ minHeight: 88, marginBottom: 8 }}
                />
                <button className="btn-primary" onClick={handleAddNote} disabled={savingNote || !noteText.trim()} style={{ width: '100%', justifyContent: 'center' }}>
                  {savingNote ? 'Saving…' : '+ Add Note'}
                </button>
              </div>
              {notes.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '24px 0', color: '#94a3b8', fontSize: 13 }}>No notes yet — add the first one above</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {notes.map(note => (
                    <div key={note.id} style={{ background: 'rgba(0,0,0,0.02)', borderRadius: 10, padding: '12px 14px', border: '1px solid rgba(0,0,0,0.05)', borderLeft: '3px solid #7c3aed' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 7 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                          <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#7c3aed', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, flexShrink: 0 }}>
                            {(note.createdBy || 'U')[0].toUpperCase()}
                          </div>
                          <span style={{ fontSize: 11, fontWeight: 600, color: '#374151' }}>{note.createdBy || 'Unknown'}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontSize: 10, color: '#94a3b8' }}>{noteTimeDiff(note.createdAt)}</span>
                          <button onClick={() => deleteLeadNote(note.id)} style={{ background: 'none', border: 'none', color: '#cbd5e1', cursor: 'pointer', fontSize: 13, padding: 0, lineHeight: 1 }}>✕</button>
                        </div>
                      </div>
                      <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{note.content}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── ASSESSMENT ── */}
          {tab === 'assessment' && (
            <div>
              <div style={{ background: 'linear-gradient(135deg, rgba(124,58,237,0.06), rgba(59,130,246,0.06))', border: '1px solid rgba(124,58,237,0.15)', borderRadius: 12, padding: '14px 16px', marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#4c1d95', marginBottom: 4 }}>🧠 Psychometric Assessment</div>
                <div style={{ fontSize: 12, color: '#5b21b6', lineHeight: 1.5 }}>
                  Send a personalised career assessment to this lead. Based on Holland RIASEC, Big Five personality, and Gardner's Multiple Intelligences.
                </div>
              </div>

              {assessmentLink && (
                <div style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.25)', borderRadius: 10, padding: '12px 14px', marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#065f46', marginBottom: 8 }}>✅ Assessment link ready:</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input readOnly value={assessmentLink} style={{ flex: 1, fontSize: 11, padding: '7px 10px', borderRadius: 7, border: '1px solid rgba(0,0,0,0.12)', background: '#f8fafc', color: '#374151', fontFamily: 'monospace' }} onClick={e => e.target.select()} />
                    <button onClick={() => copyAssessmentLink(assessmentLink)}
                      style={{ padding: '7px 14px', borderRadius: 7, background: linkCopied ? '#10b981' : '#7c3aed', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, whiteSpace: 'nowrap', transition: 'background 0.2s' }}>
                      {linkCopied ? '✓ Copied' : 'Copy'}
                    </button>
                  </div>
                </div>
              )}

              {assessmentError && (
                <div style={{ marginBottom: 14, padding: '10px 14px', background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 9, fontSize: 12, color: '#dc2626' }}>
                  ⚠️ {assessmentError}
                </div>
              )}

              {assessments.length === 0 && !assessmentLink ? (
                <div style={{ textAlign: 'center', padding: '28px 0 20px' }}>
                  <div style={{ fontSize: 40, marginBottom: 10 }}>📋</div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>No assessment sent yet</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 20 }}>Generate a link and send it to the student</div>
                  <button className="btn-primary" onClick={handleSendAssessment} disabled={sendingAssessment} style={{ margin: '0 auto' }}>
                    {sendingAssessment ? 'Generating…' : '🧠 Generate Assessment Link'}
                  </button>
                </div>
              ) : (
                <div>
                  {assessments.map(a => (
                    <AssessmentCard key={a.id} assessment={a} lead={lead} onCopyLink={copyAssessmentLink} linkCopied={linkCopied} />
                  ))}
                  <button className="btn-ghost" onClick={handleSendAssessment} disabled={sendingAssessment} style={{ width: '100%', justifyContent: 'center', marginTop: 12 }}>
                    {sendingAssessment ? 'Generating…' : '+ Send New Assessment'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    </>
  );
}

// ─── ASSESSMENT CARD ──────────────────────────────────────────────────────────
function AssessmentCard({ assessment, lead, onCopyLink, linkCopied }) {
  const link = `${window.location.origin}${window.location.pathname}?assessment=${assessment.id}`;
  const [copied, setCopied] = React.useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const statusColor = assessment.status === 'completed' ? '#10b981' : '#f59e0b';
  const statusLabel = assessment.status === 'completed' ? 'Completed' : 'Pending';
  const statusBg    = assessment.status === 'completed' ? 'rgba(16,185,129,0.1)' : 'rgba(245,158,11,0.1)';

  return (
    <div style={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 12, padding: '14px 16px', marginBottom: 12, background: '#fafafa' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>🧠</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>Career Assessment</div>
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 1 }}>
              Sent {assessment.createdAt ? new Date(assessment.createdAt.toDate?.() || assessment.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'} by {assessment.sentBy || 'Admin'}
            </div>
          </div>
        </div>
        <span style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 600, background: statusBg, color: statusColor }}>
          {statusLabel}
        </span>
      </div>
      {assessment.status === 'completed' && assessment.report ? (
        <CompactReportSummary report={assessment.report} assessmentId={assessment.id} />
      ) : (
        <div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input readOnly value={link} style={{ flex: 1, fontSize: 11, padding: '6px 9px', borderRadius: 7, border: '1px solid rgba(0,0,0,0.1)', background: '#f1f5f9', color: '#475569', fontFamily: 'monospace' }} onClick={e => e.target.select()} />
            <button onClick={handleCopy}
              style={{ padding: '6px 12px', borderRadius: 7, background: copied ? '#10b981' : '#7c3aed', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', transition: 'background 0.2s' }}>
              {copied ? '✓ Copied' : 'Copy'}
            </button>
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>Awaiting student to complete the test</div>
        </div>
      )}
    </div>
  );
}

// ─── COMPACT REPORT SUMMARY ───────────────────────────────────────────────────
function CompactReportSummary({ report, assessmentId }) {
  const [expanded, setExpanded] = React.useState(false);
  if (!report) return null;

  const top3Careers = (report.topCareers || []).slice(0, 3);
  // hollandType is already the two-letter code (e.g. "IR"); fall back to deriving from hollandRanked
  const hollandTop  = report.hollandType ||
    (report.hollandRanked || []).slice(0, 2).map(h => h.code).join('');

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 10 }}>
        <div style={{ background: 'rgba(124,58,237,0.06)', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: '#7c3aed', fontWeight: 600, marginBottom: 2 }}>HOLLAND CODE</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#4c1d95' }}>{hollandTop || '—'}</div>
        </div>
        <div style={{ background: 'rgba(59,130,246,0.06)', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: '#3b82f6', fontWeight: 600, marginBottom: 2 }}>TOP CAREER</div>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#1e3a8a', lineHeight: 1.2 }}>{top3Careers[0]?.title || '—'}</div>
        </div>
        <div style={{ background: 'rgba(16,185,129,0.06)', borderRadius: 8, padding: '8px 10px', textAlign: 'center' }}>
          <div style={{ fontSize: 10, color: '#10b981', fontWeight: 600, marginBottom: 2 }}>MATCHES</div>
          <div style={{ fontSize: 16, fontWeight: 800, color: '#064e3b' }}>{report.topCareers?.length || 0}</div>
        </div>
      </div>
      <div style={{ marginBottom: 8 }}>
        {top3Careers.map((c, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: i < top3Careers.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}>
            <span style={{ fontSize: 18 }}>{c.icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{c.title}</div>
              <div style={{ fontSize: 10, color: '#64748b' }}>{c.description?.slice(0, 60)}…</div>
            </div>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#7c3aed' }}>{c.matchScore}%</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button onClick={() => setExpanded(!expanded)}
          style={{ flex: 1, padding: '7px 12px', borderRadius: 8, background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.2)', color: '#7c3aed', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
          {expanded ? 'Show Less ▲' : 'Full Report ▼'}
        </button>
        <button onClick={() => window.open(`${window.location.origin}${window.location.pathname}?assessment=${assessmentId}`, '_blank')}
          style={{ padding: '7px 12px', borderRadius: 8, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', color: '#3b82f6', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
          Open ↗
        </button>
      </div>
      {expanded && (
        <div style={{ marginTop: 12, borderTop: '1px solid rgba(0,0,0,0.07)', paddingTop: 12 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 8, letterSpacing: '0.4px' }}>HOLLAND RIASEC PROFILE</div>
          {report.hollandScores && Object.entries(report.hollandScores).map(([dim, score]) => {
            const labels = { R: 'Realistic', I: 'Investigative', A: 'Artistic', S: 'Social', E: 'Enterprising', C: 'Conventional' };
            const colors = { R: '#f59e0b', I: '#3b82f6', A: '#ec4899', S: '#10b981', E: '#ef4444', C: '#8b5cf6' };
            return (
              <div key={dim} style={{ marginBottom: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                  <span style={{ fontSize: 11, color: '#374151' }}>{labels[dim] || dim}</span>
                  <span style={{ fontSize: 11, fontWeight: 600, color: colors[dim] || '#7c3aed' }}>{Math.round(score)}%</span>
                </div>
                <div style={{ height: 5, background: 'rgba(0,0,0,0.06)', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${score}%`, background: colors[dim] || '#7c3aed', borderRadius: 3 }} />
                </div>
              </div>
            );
          })}
          {report.bigFiveScores && (
            <div style={{ marginTop: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 8, letterSpacing: '0.4px' }}>BIG FIVE PERSONALITY</div>
              {Object.entries(report.bigFiveScores).map(([dim, score]) => {
                const labels = { O: 'Openness', C: 'Conscientiousness', E: 'Extraversion', A: 'Agreeableness', N: 'Emotional Stability' };
                return (
                  <div key={dim} style={{ marginBottom: 5 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                      <span style={{ fontSize: 11, color: '#374151' }}>{labels[dim] || dim}</span>
                      <span style={{ fontSize: 11, fontWeight: 600, color: '#475569' }}>{Math.round(score)}%</span>
                    </div>
                    <div style={{ height: 4, background: 'rgba(0,0,0,0.06)', borderRadius: 2, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${score}%`, background: 'linear-gradient(90deg,#7c3aed,#3b82f6)', borderRadius: 2 }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', marginBottom: 8, letterSpacing: '0.4px' }}>ALL CAREER MATCHES</div>
            {(report.topCareers || []).map((c, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8, background: i % 2 === 0 ? 'rgba(0,0,0,0.02)' : 'transparent', marginBottom: 2 }}>
                <span style={{ fontSize: 18 }}>{c.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#0f172a' }}>{c.title}</div>
                  <div style={{ fontSize: 10, color: '#94a3b8' }}>{c.salary || ''}</div>
                </div>
                <div style={{ fontSize: 13, fontWeight: 700, color: '#7c3aed' }}>{c.matchScore}%</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── MULTI-SELECT DROPDOWN ────────────────────────────────────────────────────
// selected = string[] (empty = "all"). options = [{ value, label }].
function MultiSelectDropdown({ allLabel, options, selected, onChange, style = {} }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const isAll    = selected.length === 0;
  const active   = !isAll;
  const btnLabel = isAll
    ? allLabel
    : selected.length === 1
      ? (options.find(o => o.value === selected[0])?.label || selected[0])
      : `${selected.length} selected`;

  const toggle = (val) => {
    if (selected.includes(val)) onChange(selected.filter(v => v !== val));
    else onChange([...selected, val]);
  };

  return (
    <div ref={ref} style={{ position: 'relative', ...style }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          height: 36, display: 'flex', alignItems: 'center', gap: 6,
          padding: '0 30px 0 11px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
          border: `1px solid ${active ? 'rgba(124,58,237,0.45)' : 'rgba(0,0,0,0.12)'}`,
          background: active ? 'rgba(124,58,237,0.07)' : '#fff',
          color: active ? '#7c3aed' : '#374151', fontWeight: active ? 600 : 400,
          position: 'relative', whiteSpace: 'nowrap', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis',
          fontFamily: 'inherit',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{btnLabel}</span>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
          style={{ position: 'absolute', right: 9, top: '50%', transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`, transition: 'transform 0.15s', flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 5px)', left: 0, zIndex: 500,
          background: '#fff', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 12,
          boxShadow: '0 8px 28px rgba(0,0,0,0.13)', minWidth: 200, maxHeight: 320,
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
        }}>
          {/* Quick actions */}
          <div style={{ display: 'flex', gap: 6, padding: '8px 10px', borderBottom: '1px solid rgba(0,0,0,0.06)', flexShrink: 0 }}>
            <button onClick={() => onChange([])}
              style={{ flex: 1, fontSize: 11, padding: '4px 0', borderRadius: 6, border: '1px solid rgba(0,0,0,0.1)', background: isAll ? 'rgba(124,58,237,0.08)' : 'transparent', color: isAll ? '#7c3aed' : '#64748b', cursor: 'pointer', fontWeight: isAll ? 600 : 400 }}>
              All
            </button>
            <button onClick={() => onChange(options.map(o => o.value))}
              style={{ flex: 1, fontSize: 11, padding: '4px 0', borderRadius: 6, border: '1px solid rgba(124,58,237,0.25)', background: 'rgba(124,58,237,0.06)', color: '#7c3aed', cursor: 'pointer' }}>
              Select All
            </button>
          </div>
          {/* Options */}
          <div style={{ overflowY: 'auto' }}>
            {options.map(opt => {
              const checked = selected.includes(opt.value);
              return (
                <label key={opt.value}
                  style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 12px', cursor: 'pointer', background: checked ? 'rgba(124,58,237,0.05)' : 'transparent', userSelect: 'none' }}>
                  <input type="checkbox" checked={checked} onChange={() => toggle(opt.value)} style={{ accentColor: '#7c3aed', width: 14, height: 14, flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: '#0f172a', flex: 1 }}>{opt.label}</span>
                </label>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── BULK EDIT MODAL ──────────────────────────────────────────────────────────
function BulkEditModal({ count, sources, priorities, onApply, onClose }) {
  const EDITABLE_FIELDS = [
    { key: 'source',    label: 'Source',           type: 'select' },
    { key: 'priority',  label: 'Priority',          type: 'select' },
    { key: 'countries', label: 'Countries Interested', type: 'text' },
    { key: 'course',    label: 'Course Interested',  type: 'text' },
    { key: 'intake',    label: 'Intake',             type: 'text' },
    { key: 'city',      label: 'City',               type: 'text' },
    { key: 'qualification', label: 'Qualification',  type: 'text' },
    { key: 'budget',    label: 'Budget',             type: 'text' },
    { key: 'fbCampaignName', label: 'Campaign Name', type: 'text' },
  ];

  const [field,  setField]  = useState(EDITABLE_FIELDS[0].key);
  const [value,  setValue]  = useState('');
  const [saving, setSaving] = useState(false);

  const def = EDITABLE_FIELDS.find(f => f.key === field);

  const handleApply = async () => {
    if (!value.trim()) return;
    setSaving(true);
    await onApply(field, value.trim());
    setSaving(false);
    onClose();
  };

  const overlayStyle = {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 9999, padding: 20,
  };
  const modalStyle = {
    background: '#fff', borderRadius: 16, padding: '28px 28px 24px',
    width: '100%', maxWidth: 420, boxShadow: '0 24px 60px rgba(0,0,0,0.18)',
  };

  return (
    <div style={overlayStyle} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={modalStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a' }}>Edit Fields</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>Updating {count} lead{count !== 1 ? 's' : ''}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#94a3b8', lineHeight: 1 }}>✕</button>
        </div>

        {/* Field picker */}
        <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 6 }}>Field to update</label>
        <select value={field} onChange={e => { setField(e.target.value); setValue(''); }}
          style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, color: '#1e293b', marginBottom: 16, background: '#fff' }}>
          {EDITABLE_FIELDS.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>

        {/* Value input */}
        <label style={{ fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 6 }}>New value</label>
        {def?.type === 'select' && field === 'source' ? (
          <select value={value} onChange={e => setValue(e.target.value)}
            style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, color: '#1e293b', marginBottom: 20, background: '#fff' }}>
            <option value="">— Select source —</option>
            {sources.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        ) : def?.type === 'select' && field === 'priority' ? (
          <select value={value} onChange={e => setValue(e.target.value)}
            style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, color: '#1e293b', marginBottom: 20, background: '#fff' }}>
            <option value="">— Select priority —</option>
            {(priorities || []).map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        ) : (
          <input value={value} onChange={e => setValue(e.target.value)}
            placeholder={`Enter ${def?.label?.toLowerCase() || 'value'}…`}
            style={{ width: '100%', padding: '9px 12px', borderRadius: 8, border: '1.5px solid #e2e8f0', fontSize: 13, color: '#1e293b', marginBottom: 20, outline: 'none', boxSizing: 'border-box' }}
            onKeyDown={e => { if (e.key === 'Enter') handleApply(); }}
          />
        )}

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px', borderRadius: 10, border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button onClick={handleApply} disabled={!value.trim() || saving}
            style={{ flex: 2, padding: '10px', borderRadius: 10, border: 'none', background: value.trim() && !saving ? 'linear-gradient(135deg,#7c3aed,#6366f1)' : '#e2e8f0', color: value.trim() && !saving ? '#fff' : '#94a3b8', fontSize: 13, fontWeight: 700, cursor: value.trim() && !saving ? 'pointer' : 'not-allowed', transition: 'all 0.15s' }}>
            {saving ? 'Updating…' : `Update ${count} lead${count !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── SAVE VIEW DIALOG ─────────────────────────────────────────────────────────
function SaveViewDialog({ onSave, onClose, users = [] }) {
  const [name,     setName]     = useState('');
  const [visMode,  setVisMode]  = useState('all');      // 'all' | 'specific'
  const [visUsers, setVisUsers] = useState([]);

  const counselors = users.filter(u => u.role !== 'test_prep' && u.role !== 'digital_media');

  const toggleUser = (uid) =>
    setVisUsers(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]);

  const handleSave = () => {
    if (!name.trim()) return;
    if (visMode === 'specific' && visUsers.length === 0) return;
    onSave(name.trim(), visMode === 'all' ? 'all' : visUsers);
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 400 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', marginBottom: 14 }}>Save as New View</h3>

        {/* Name */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>View Name</label>
          <input
            className="crm-input"
            placeholder="e.g. Hot UK Leads"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSave()}
            autoFocus
          />
        </div>

        {/* Visibility */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 8 }}>Visible to</label>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
            {[['all', '🌐 Everyone'], ['specific', '👤 Specific Counselors']].map(([val, lbl]) => (
              <button key={val} onClick={() => setVisMode(val)} style={{
                flex: 1, padding: '8px 6px', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', fontWeight: visMode === val ? 600 : 400,
                border: `1.5px solid ${visMode === val ? '#7c3aed' : 'rgba(0,0,0,0.12)'}`,
                background: visMode === val ? 'rgba(124,58,237,0.08)' : '#fff',
                color: visMode === val ? '#7c3aed' : '#64748b',
              }}>{lbl}</button>
            ))}
          </div>

          {visMode === 'specific' && (
            <div style={{ border: '1px solid rgba(0,0,0,0.1)', borderRadius: 9, maxHeight: 180, overflowY: 'auto', padding: '4px 0' }}>
              {counselors.length === 0 && (
                <div style={{ padding: '12px', fontSize: 12, color: '#94a3b8', textAlign: 'center' }}>No counselors found</div>
              )}
              {counselors.map(u => (
                <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '7px 12px', cursor: 'pointer', background: visUsers.includes(u.id) ? 'rgba(124,58,237,0.05)' : 'transparent', userSelect: 'none' }}>
                  <input type="checkbox" checked={visUsers.includes(u.id)} onChange={() => toggleUser(u.id)} style={{ accentColor: '#7c3aed', width: 14, height: 14, flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: '#0f172a', flex: 1 }}>{u.name}</span>
                  <span style={{ fontSize: 11, color: '#94a3b8' }}>{u.role}</span>
                </label>
              ))}
            </div>
          )}
          {visMode === 'specific' && visUsers.length > 0 && (
            <div style={{ fontSize: 11, color: '#7c3aed', marginTop: 6 }}>
              ✓ {visUsers.length} counselor{visUsers.length !== 1 ? 's' : ''} selected — you will also see this view
            </div>
          )}
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={handleSave} disabled={!name.trim() || (visMode === 'specific' && visUsers.length === 0)}>
            Save View
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── KANBAN BOARD VIEW ───────────────────────────────────────────────────────
const PRIORITY_COLORS = { hot: '#ef4444', warm: '#f59e0b', cold: '#3b82f6' };

// ─── MAIN LEADS PAGE ──────────────────────────────────────────────────────────
export default function Leads() {
  const { leads: allLeads, users, getUserById, pipelineStages, addLeadModalOpen, setAddLeadModalOpen, addLead, updateLead, deleteLead, bulkDeleteLeads, customFields, openLeadId, setOpenLeadId, customSources, saveCustomSources, currentUser: cu, dataScope, companyId, priorities } = useCRM();
  const currentUser = cu; // alias so JSX below can use currentUser
  // Build a value→priority lookup map from live priorities (falls back to defaults)
  const priorityMap = useMemo(() => {
    const arr = priorities || [
      { value: 'hot',  label: 'Hot',  emoji: '🔥', color: '#ef4444' },
      { value: 'warm', label: 'Warm', emoji: '☀️', color: '#f59e0b' },
      { value: 'cold', label: 'Cold', emoji: '❄️', color: '#3b82f6' },
    ];
    return Object.fromEntries(arr.map(p => [p.value, p]));
  }, [priorities]);

  // Scope leads: counsellors see only their own when admin has set counsellorLeads='own'
  const isAdmin = cu?.role === 'admin' || cu?.role === 'superadmin';
  const leads = useMemo(() => {
    if (isAdmin || dataScope?.counsellorLeads !== 'own') return allLeads;
    return allLeads.filter(l => l.assignedTo === cu?.id || l.assignedToId === cu?.id || l.assignedTo === cu?.name);
  }, [allLeads, isAdmin, dataScope?.counsellorLeads, cu?.id, cu?.name]);

  // Merge built-in + custom sources into one unified list
  const mergedSources = useMemo(() => {
    const custom = (customSources || []).filter(cs => !ALL_SOURCES.find(s => s.value === cs.value));
    return [...ALL_SOURCES, ...custom];
  }, [customSources]);

  // Get icon for any source (built-in or custom)
  const getSourceIcon = useCallback((sourceValue) => {
    const found = mergedSources.find(s => s.value === sourceValue);
    return found?.icon || SOURCE_ICONS[sourceValue] || '🏷️';
  }, [mergedSources]);

  // ── Restore filters + view mode from localStorage on mount ──────────────
  const _af = loadActiveFilters();
  const [search,         setSearch]         = useState(_af.search   || '');
  const [filterStatus,   setFilterStatus]   = useState(_af.status   || []);
  const [filterSource,   setFilterSource]   = useState(_af.source   || []);
  const [filterUser,     setFilterUser]     = useState(_af.user     || []);
  const [filterPriority, setFilterPriority] = useState(_af.priority || []);
  const [filterCampaign, setFilterCampaign] = useState(_af.campaign || []);
  const [sort,           setSort]           = useState(_af.sort     || 'newest');
  const [pageSize,       setPageSize]       = useState(50);
  const [currentPage,    setCurrentPage]    = useState(1);
  const [showAdd,         setShowAdd]         = useState(false);
  const [showBulk,        setShowBulk]        = useState(false);
  const [showSaveView,    setShowSaveView]    = useState(false);
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  const [selectedLeadId,  setSelectedLeadId]  = useState(null);
  const [selected,        setSelected]        = useState([]);
  const DEFAULT_VIEW = { id: 'default', name: 'All Leads', filters: defaultFilters(), columns: DEFAULT_COLUMNS };
  const [savedViews,      setSavedViews]      = useState(() => {
    try {
      const local = JSON.parse(localStorage.getItem('crm_saved_views') || '[]');
      const localViews = local.filter(v => v.id !== 'default');
      if (localViews.length > 0) return [DEFAULT_VIEW, ...localViews];
    } catch {}
    return [DEFAULT_VIEW];
  });
  const [activeViewId,    setActiveViewId]    = useState(loadActiveViewId);
  const [hoveredViewId,   setHoveredViewId]   = useState(null);
  const filtersInitRef = useRef(false); // ensure we apply active view filters only once on first Firestore load
  const [visibleColumns,  setVisibleColumns]  = useState(() => {
    const views = loadSavedViews();
    const id    = loadActiveViewId();
    const v     = views.find(x => x.id === id);
    return v?.columns || DEFAULT_COLUMNS;
  });
  const [bulkImport,     setBulkImport]     = useState({ active: false, done: 0, total: 0, errors: [], finished: false });
  const [viewMode,        setViewMode]        = useState(loadViewMode); // 'table' | 'cards'
  const [colSort,         setColSort]         = useState({ col: null, dir: 'asc' });
  const [hoveredRow,      setHoveredRow]      = useState(null);
  const [showBulkStage,   setShowBulkStage]   = useState(false);
  const [showBulkAssign,  setShowBulkAssign]  = useState(false);
  const [showBulkEdit,    setShowBulkEdit]    = useState(false);
  const [inlineEdit,      setInlineEdit]      = useState(null); // { leadId, field }
  const bulkAbortRef   = useRef(false);
  const columnsMenuRef = useRef(null);
  const [intakeLinkMsg, setIntakeLinkMsg] = useState('');

  useEffect(() => {
    if (addLeadModalOpen) { setShowAdd(true); setAddLeadModalOpen(false); }
  }, [addLeadModalOpen, setAddLeadModalOpen]);

  // ── Subscribe to Firestore saved views for this company ──────────────────────
  // Architecture: localStorage is the canonical source for THIS user's own views.
  // onSnapshot only adds views from OTHER users that are shared with this user.
  // This eliminates duplicate-creation bugs and prevents onSnapshot overwriting local edits.
  useEffect(() => {
    if (!companyId) return;
    const colRef = collection(db, 'companies', companyId, 'savedLeadViews');
    const unsub = onSnapshot(colRef, (snap) => {
      const raw = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const uid = cu?.id;

      // Own views from localStorage — canonical source (prevents edit-overwrite bugs)
      const localViews = (() => {
        try { return JSON.parse(localStorage.getItem('crm_saved_views') || '[]').filter(v => v.id !== 'default'); }
        catch { return []; }
      })();
      const localIds   = new Set(localViews.map(v => v.id));
      const localNames = new Set(localViews.map(v => v.name));

      // Own views from Firestore — only import ones not already in localStorage
      // (handles first login on a new device / cleared cache without duplicating
      //  views currently being written via saveCurrentView)
      const ownFromFS = uid ? raw.filter(v => v.createdBy === uid) : [];
      const newOwnFromFS = ownFromFS.filter(v =>
        !localIds.has(v.id) && !localNames.has(v.name)
      );
      if (newOwnFromFS.length > 0) {
        try {
          localStorage.setItem('crm_saved_views', JSON.stringify([...localViews, ...newOwnFromFS]));
        } catch {}
      }
      const ownViews = [...localViews, ...newOwnFromFS];

      // Shared views from OTHER users visible to this user
      const shared = raw.filter(v => {
        if (uid && v.createdBy === uid) return false;
        if (!v.visibleTo || v.visibleTo === 'all') return true;
        if (uid && Array.isArray(v.visibleTo) && v.visibleTo.includes(uid)) return true;
        return false;
      });
      shared.sort((a, b) => (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0));

      setSavedViews([DEFAULT_VIEW, ...ownViews, ...shared]);
      // Apply active view filters on first load only (from localStorage)
      if (!filtersInitRef.current) {
        filtersInitRef.current = true;
        const savedActiveId = loadActiveViewId();
        let toActivate = null;
        try {
          const localViews = JSON.parse(localStorage.getItem('crm_saved_views') || '[]');
          toActivate = [...localViews, ...shared].find(v => v.id === savedActiveId);
        } catch {}
        if (toActivate && toActivate.id !== 'default') {
          const f = toActivate.filters || {};
          setSearch(f.search || '');
          setFilterStatus(toArr(f.status));
          setFilterSource(toArr(f.source));
          setFilterUser(toArr(f.user));
          setFilterPriority(toArr(f.priority));
          setFilterCampaign(toArr(f.campaign));
          setSort(f.sort || 'newest');
          setVisibleColumns(toActivate.columns || DEFAULT_COLUMNS);
        }
      }
    }, (err) => {
      console.error('[SavedViews] Firestore error:', err.code, err.message);
      if (err.code === 'permission-denied') {
        console.warn('[SavedViews] Run: firebase deploy --only firestore:rules  to enable cross-user view sharing');
      }
      // On Firestore error: local views stay intact — no state wipe
    });
    return () => unsub();
  }, [companyId, cu?.id]); // eslint-disable-line

  const handleCopyIntakeLink = async () => {
    if (!companyId) return;
    try {
      const compRef = doc(db, 'companies', companyId);
      const compSnap = await getDoc(compRef);
      let intakeToken = compSnap.exists() ? compSnap.data()?.intakeFormToken : null;
      if (!intakeToken) {
        intakeToken = crypto.randomUUID();
        await setDoc(compRef, { intakeFormToken: intakeToken }, { merge: true });
      }
      const url = `${window.location.origin}/student-form/${companyId}/intake/${intakeToken}`;
      await navigator.clipboard.writeText(url);
      setIntakeLinkMsg('✅ Intake form link copied!');
      setTimeout(() => setIntakeLinkMsg(''), 3500);
    } catch (err) {
      setIntakeLinkMsg('⚠️ ' + err.message);
      setTimeout(() => setIntakeLinkMsg(''), 4000);
    }
  };

  useEffect(() => {
    try { localStorage.setItem('crm_active_view_id', activeViewId); } catch {}
  }, [activeViewId]);

  useEffect(() => {
    try { localStorage.setItem('crm_view_mode', viewMode); } catch {}
  }, [viewMode]);

  // Close columns menu on outside click
  useEffect(() => {
    if (!showColumnsMenu) return;
    const handler = (e) => {
      if (columnsMenuRef.current && !columnsMenuRef.current.contains(e.target)) {
        setShowColumnsMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showColumnsMenu]);

  // Close inline cell editors on outside click
  useEffect(() => {
    if (!inlineEdit) return;
    const handler = () => setInlineEdit(null);
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [inlineEdit]);

  const selectedLead = useMemo(
    () => leads.find(l => l.id === selectedLeadId) || null,
    [leads, selectedLeadId]
  );

  // Auto-open lead when navigated from Followups page
  useEffect(() => {
    if (openLeadId && leads.length > 0) {
      setSelectedLeadId(openLeadId);
      setOpenLeadId(null);
    }
  }, [openLeadId, leads]); // eslint-disable-line

  const filtered = useMemo(() => {
    let arr = [...leads];
    if (search) {
      const q = search.toLowerCase();
      arr = arr.filter(l =>
        (l.firstName || '').toLowerCase().includes(q) ||
        (l.lastName || '').toLowerCase().includes(q) ||
        (l.name || '').toLowerCase().includes(q) ||
        (l.phone || '').includes(q) ||
        (l.email || '').toLowerCase().includes(q) ||
        (l.country || '').toLowerCase().includes(q) ||
        (l.countries || []).some(c => c.toLowerCase().includes(q))
      );
    }
    if (filterStatus.length   > 0) arr = arr.filter(l => filterStatus.includes(l.status));
    if (filterSource.length   > 0) arr = arr.filter(l => filterSource.includes(l.source));
    if (filterPriority.length > 0) arr = arr.filter(l => filterPriority.includes(l.priority));
    if (filterUser.length     > 0) arr = arr.filter(l => filterUser.includes(l.assignedToId) || filterUser.includes(l.assignedTo));
    if (filterCampaign.length > 0) arr = arr.filter(l => filterCampaign.includes(l.fbCampaignName) || filterCampaign.includes(l.fbCampaignId));
    if (sort === 'newest') arr.sort((a, b) => (getDate(b.createdAt) || 0) - (getDate(a.createdAt) || 0));
    if (sort === 'oldest') arr.sort((a, b) => (getDate(a.createdAt) || 0) - (getDate(b.createdAt) || 0));
    if (sort === 'score')  arr.sort((a, b) => (b.score || 0) - (a.score || 0));
    if (sort === 'name')   arr.sort((a, b) => getLeadName(a).localeCompare(getLeadName(b)));
    // Column-header sort overrides toolbar sort
    if (colSort.col) {
      const PRIO = Object.fromEntries((priorities || [{ value: 'hot' }, { value: 'warm' }, { value: 'cold' }]).map((p, i) => [p.value, i]));
      arr.sort((a, b) => {
        let av, bv;
        switch (colSort.col) {
          case 'firstName':  av = (a.firstName || a.name || '').toLowerCase(); bv = (b.firstName || b.name || '').toLowerCase(); break;
          case 'lastName':   av = (a.lastName  || '').toLowerCase();           bv = (b.lastName  || '').toLowerCase();           break;
          case 'email':      av = (a.email     || '').toLowerCase();           bv = (b.email     || '').toLowerCase();           break;
          case 'phone':      av = (a.phone     || '');                         bv = (b.phone     || '');                         break;
          case 'stage':      av = (pipelineStages.find(s=>s.id===a.status)?.name||'').toLowerCase(); bv = (pipelineStages.find(s=>s.id===b.status)?.name||'').toLowerCase(); break;
          case 'source':     av = (a.source    || '').toLowerCase();           bv = (b.source    || '').toLowerCase();           break;
          case 'countries':  av = (a.countries?.[0] || a.country || '').toLowerCase(); bv = (b.countries?.[0] || b.country || '').toLowerCase(); break;
          case 'course':     av = (a.course    || '').toLowerCase();           bv = (b.course    || '').toLowerCase();           break;
          case 'intake':     av = (a.intake    || '');                         bv = (b.intake    || '');                         break;
          case 'budget':     av = parseFloat(a.budget) || 0;                   bv = parseFloat(b.budget) || 0;                   break;
          case 'assigned':   av = (getUserById(a.assignedToId)?.name||'').toLowerCase(); bv = (getUserById(b.assignedToId)?.name||'').toLowerCase(); break;
          case 'score':      av = a.score  || 0;  bv = b.score  || 0;  break;
          case 'priority':   av = PRIO[a.priority]??1; bv = PRIO[b.priority]??1; break;
          case 'leadAge':    av = getLeadAge(a)||0; bv = getLeadAge(b)||0; break;
          case 'added':      av = getDate(a.createdAt)?.getTime()||0; bv = getDate(b.createdAt)?.getTime()||0; break;
          default:           return 0;
        }
        if (typeof av === 'string') { const c = av.localeCompare(bv); return colSort.dir==='asc' ? c : -c; }
        return colSort.dir === 'asc' ? av - bv : bv - av;
      });
    }
    return arr;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads, search, filterStatus, filterSource, filterUser, filterPriority, filterCampaign, sort, colSort]);

  useEffect(() => { setCurrentPage(1); }, [search, filterStatus, filterSource, filterUser, filterPriority, filterCampaign, sort, pageSize]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage   = Math.min(currentPage, totalPages);
  const pagedLeads = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  // Use a Set for O(1) lookups — prevents O(n²) freeze with 47K leads
  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const pageLeadIds      = useMemo(() => pagedLeads.map(l => l.id), [pagedLeads]);
  const allPageSelected  = pageLeadIds.length > 0 && pageLeadIds.every(id => selectedSet.has(id));
  const somePageSelected = pageLeadIds.some(id => selectedSet.has(id)) && !allPageSelected;
  const allViewSelected  = filtered.length > 0 && filtered.length === selected.length && filtered.every(l => selectedSet.has(l.id));

  const handleHeaderCheckbox = () => {
    if (allPageSelected) {
      setSelected(prev => prev.filter(id => !pageLeadIds.includes(id)));
    } else {
      setSelected(prev => [...new Set([...prev, ...pageLeadIds])]);
    }
  };

  const toggleSelect = (id) =>
    setSelected(prev => selectedSet.has(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const toggleColumn = (key) =>
    setVisibleColumns(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );

  const col = (key) => visibleColumns.includes(key);

  const currentViewState = () => ({
    filters: { search, status: filterStatus, source: filterSource, user: filterUser, priority: filterPriority, campaign: filterCampaign, sort },
    columns: visibleColumns,
  });

  const applyView = (view) => {
    setActiveViewId(view.id);
    try { localStorage.setItem('crm_active_view_id', view.id); } catch {}
    const f = view.filters || {};
    setSearch(f.search || '');
    setFilterStatus(toArr(f.status));
    setFilterSource(toArr(f.source));
    setFilterUser(toArr(f.user));
    setFilterPriority(toArr(f.priority));
    setFilterCampaign(toArr(f.campaign));
    setSort(f.sort || 'newest');
    setVisibleColumns(view.columns || DEFAULT_COLUMNS);
  };

  const saveCurrentView = async (name, visibleTo = 'all') => {
    const { filters, columns } = currentViewState();
    // Optimistic update — add to local state immediately so the tab appears at once
    const tempId = 'local_' + Date.now();
    const newView = { id: tempId, name, filters, columns, visibleTo, createdBy: cu?.id || '' };
    setSavedViews(prev => [...prev, newView]);
    setActiveViewId(tempId);
    try { localStorage.setItem('crm_active_view_id', tempId); } catch {}
    // Also save to localStorage so it survives refresh even if Firestore isn't reachable
    try {
      const local = JSON.parse(localStorage.getItem('crm_saved_views') || '[]');
      localStorage.setItem('crm_saved_views', JSON.stringify([...local.filter(v => v.id !== 'default'), newView]));
    } catch {}
    setShowSaveView(false);
    // Background Firestore write for cross-user sharing
    try {
      const docRef = await addDoc(collection(db, 'companies', companyId, 'savedLeadViews'), {
        name, filters, columns, visibleTo,
        createdBy: cu?.id || '',
        createdAt: serverTimestamp(),
      });
      // Replace temp ID with real Firestore ID
      setSavedViews(prev => prev.map(v => v.id === tempId ? { ...v, id: docRef.id } : v));
      setActiveViewId(docRef.id);
      try { localStorage.setItem('crm_active_view_id', docRef.id); } catch {}
      // Update localStorage with real ID too
      try {
        const local = JSON.parse(localStorage.getItem('crm_saved_views') || '[]');
        localStorage.setItem('crm_saved_views', JSON.stringify(local.map(v => v.id === tempId ? { ...v, id: docRef.id } : v)));
      } catch {}
    } catch (e) {
      console.error('[SaveView] Firestore sync failed (deploy firestore:rules to enable cross-user sharing):', e.message);
      // Local-only view stays visible — no further action needed
    }
  };

  const updateCurrentView = async () => {
    if (!activeViewId || activeViewId === 'default') return;
    const { filters, columns } = currentViewState();
    // Update local state immediately
    setSavedViews(prev => prev.map(v => v.id === activeViewId ? { ...v, filters, columns } : v));
    // Update localStorage
    try {
      const local = JSON.parse(localStorage.getItem('crm_saved_views') || '[]');
      localStorage.setItem('crm_saved_views', JSON.stringify(local.map(v => v.id === activeViewId ? { ...v, filters, columns } : v)));
    } catch {}
    // Update Firestore if not a local-only view
    if (!activeViewId.startsWith('local_')) {
      try {
        await updateDoc(doc(db, 'companies', companyId, 'savedLeadViews', activeViewId), {
          filters, columns, updatedAt: serverTimestamp(),
        });
      } catch (e) { console.error('[UpdateView]', e.message); }
    }
  };

  const deleteView = async (id) => {
    if (!id || id === 'default') return;
    // Remove from local state immediately
    setSavedViews(prev => prev.filter(v => v.id !== id));
    // Remove from localStorage
    try {
      const local = JSON.parse(localStorage.getItem('crm_saved_views') || '[]');
      localStorage.setItem('crm_saved_views', JSON.stringify(local.filter(v => v.id !== id)));
    } catch {}
    // Delete from Firestore if not a local-only view
    if (!id.startsWith('local_')) {
      try { await deleteDoc(doc(db, 'companies', companyId, 'savedLeadViews', id)); } catch (e) { console.error('[DeleteView]', e.message); }
    }
    if (activeViewId === id) {
      const fallback = savedViews.find(v => v.id !== id) || DEFAULT_VIEW;
      applyView(fallback);
    }
  };

  // Detect if current state differs from the active saved view
  const activeView = savedViews.find(v => v.id === activeViewId);
  const hasUnsavedChanges = useMemo(() => {
    if (!activeView) return false;
    const f = activeView.filters || {};
    const arrEq = (a, b) => JSON.stringify([...(a||[])].sort()) === JSON.stringify([...(b||[])].sort());
    const filtersChanged = (
      (f.search || '') !== search ||
      !arrEq(toArr(f.status),   filterStatus)   ||
      !arrEq(toArr(f.source),   filterSource)   ||
      !arrEq(toArr(f.user),     filterUser)     ||
      !arrEq(toArr(f.priority), filterPriority) ||
      !arrEq(toArr(f.campaign), filterCampaign) ||
      (f.sort || 'newest') !== sort
    );
    const savedCols = JSON.stringify([...(activeView.columns || DEFAULT_COLUMNS)].sort());
    const currCols  = JSON.stringify([...visibleColumns].sort());
    return filtersChanged || savedCols !== currCols;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeView, search, filterStatus, filterSource, filterUser, filterPriority, filterCampaign, sort, visibleColumns]);

  // Counselors cannot delete. Admins can only delete when inside a named saved view (not default).
  const isCounselor = cu?.role === 'counsellor' || cu?.role === 'counselor';
  const isDefaultView = activeViewId === 'default';
  const canDelete = !isCounselor && !isDefaultView;

  const handleBulkDelete = async () => {
    if (!canDelete) return;
    if (!window.confirm('Delete ' + selected.length + ' lead(s)? This cannot be undone.')) return;
    // Use batch delete — single state update + batched Firestore writes
    // Never freezes the browser even with thousands of leads
    await bulkDeleteLeads(selected);
    setSelected([]);
  };

  const handleBulkChangeStage = async (stageId) => {
    await Promise.all(selected.map(id => updateLead(id, { status: stageId })));
    setSelected([]);
    setShowBulkStage(false);
  };

  const handleBulkReassign = async (userId) => {
    const user = users.find(u => u.id === userId);
    await Promise.all(selected.map(id => updateLead(id, { assignedToId: userId, assignedTo: user?.name || '' })));
    setSelected([]);
    setShowBulkAssign(false);
  };

  const handleBulkEditApply = async (field, value) => {
    await Promise.all(selected.map(id => updateLead(id, { [field]: value })));
    setSelected([]);
  };

  const handleExport = (leadsToExport) => {
    exportToCSV(leadsToExport, pipelineStages, getUserById);
  };

  // Chunk size — each HTTP request carries this many leads to the server
  const IMPORT_CHUNK = 3000;

  const handleStartImport = useCallback(async (rows) => {
    bulkAbortRef.current = false;
    const jobId = `job_${companyId}_${Date.now()}`;
    setBulkImport({ active: true, done: 0, total: rows.length, errors: 0, finished: false, jobId, serverMode: true });

    // ── Subscribe to Firestore job progress ──────────────────────────────────
    // The progress floater will update live even if the browser was closed and reopened.
    const unsubJob = onSnapshot(doc(db, 'bulkImportJobs', jobId), snap => {
      if (!snap.exists()) return;
      const d = snap.data();
      setBulkImport(prev => ({
        ...prev,
        done:     d.done     || 0,
        errors:   d.errors   || 0,
        total:    d.total    || prev.total,
        finished: d.status === 'done' || d.status === 'error',
      }));
      if (d.status === 'done' || d.status === 'error') unsubJob();
    });

    // ── Send leads to backend in chunks ──────────────────────────────────────
    // Once each chunk is received by the server, it processes in background.
    // Closing or sleeping the laptop does NOT stop already-received chunks.
    try {
      for (let i = 0; i < rows.length; i += IMPORT_CHUNK) {
        if (bulkAbortRef.current) break;
        const chunk = rows.slice(i, i + IMPORT_CHUNK);
        const isFirst = i === 0;

        const res = await fetch(`${BACKEND_URL}/api/leads/bulk-import`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            companyId,
            leads:       chunk,
            jobId,
            chunkIndex:  Math.floor(i / IMPORT_CHUNK),
            totalLeads:  rows.length,  // only used by first chunk to set job.total
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          console.error('[BulkImport] Chunk failed:', err.error || res.status);
          break;
        }
        if (isFirst) {
          console.log(`[BulkImport] Job started: ${jobId} — ${rows.length} leads in ${Math.ceil(rows.length / IMPORT_CHUNK)} chunks`);
        }
        // Small pause between chunks to avoid overwhelming the server
        if (i + IMPORT_CHUNK < rows.length) {
          await new Promise(r => setTimeout(r, 300));
        }
      }
    } catch (err) {
      console.error('[BulkImport] Network error:', err.message);
      // Fall back to client-side import if backend is unreachable
      setBulkImport({ active: true, done: 0, total: rows.length, errors: 0, finished: false, jobId: null, serverMode: false });
      unsubJob();
      for (let i = 0; i < rows.length; i++) {
        if (bulkAbortRef.current) break;
        try { await addLead(rows[i]); } catch {}
        setBulkImport(prev => ({ ...prev, done: i + 1 }));
        if (i % 5 === 4) await new Promise(r => setTimeout(r, 0));
      }
      setBulkImport(prev => ({ ...prev, finished: true }));
    }
  }, [addLead, companyId]); // eslint-disable-line

  const dismissBulkImport = () => {
    bulkAbortRef.current = true;
    setBulkImport({ active: false, done: 0, total: 0, errors: 0, finished: false, jobId: null, serverMode: false });
  };

  const displayCountries = (lead) => {
    if (lead.countries?.length) return lead.countries.join(', ');
    return lead.country || '—';
  };

  const clearFilters = () => {
    setFilterStatus([]); setFilterSource([]);
    setFilterUser([]); setFilterPriority([]); setFilterCampaign([]); setSearch('');
  };

  const hasFilters = filterStatus.length > 0 || filterSource.length > 0 || filterUser.length > 0 || filterPriority.length > 0 || filterCampaign.length > 0 || search;

  // Unique FB campaigns from all leads (for filter dropdown)
  const fbCampaigns = useMemo(() => {
    const seen = new Set();
    const campaigns = [];
    leads.forEach(l => {
      if (l.fbCampaignName && !seen.has(l.fbCampaignName)) {
        seen.add(l.fbCampaignName);
        campaigns.push({ id: l.fbCampaignId || l.fbCampaignName, name: l.fbCampaignName });
      }
    });
    return campaigns.sort((a, b) => a.name.localeCompare(b.name));
  }, [leads]);

  return (
    <div className="page-enter" style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>

      {/* ── Saved Views Bar ── */}
      <div style={{ padding: '0 20px', borderBottom: '1px solid rgba(0,0,0,0.07)', background: 'linear-gradient(to bottom, #faf9ff, #f8f7ff)', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 3, overflowX: 'auto', minHeight: 50 }}>
        {savedViews.map(view => {
          const isActive = view.id === activeViewId;
          const showUnsaved = isActive && hasUnsavedChanges && view.id !== 'default';
          const isHov = hoveredViewId === view.id;
          return (
            <div key={view.id}
              onMouseEnter={() => setHoveredViewId(view.id)}
              onMouseLeave={() => setHoveredViewId(null)}
              style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
              {/* View pill */}
              <button
                onClick={() => applyView(view)}
                style={{
                  padding: '5px 13px',
                  borderRadius: 20,
                  border: isActive ? '1.5px solid #7c3aed' : `1.5px solid ${isHov ? 'rgba(124,58,237,0.25)' : 'transparent'}`,
                  background: isActive
                    ? 'linear-gradient(135deg, #7c3aed 0%, #6366f1 100%)'
                    : isHov ? 'rgba(124,58,237,0.07)' : 'transparent',
                  color: isActive ? '#fff' : isHov ? '#7c3aed' : '#64748b',
                  fontWeight: isActive ? 600 : 400,
                  fontSize: 12.5,
                  cursor: 'pointer',
                  transition: 'all 0.18s',
                  display: 'flex', alignItems: 'center', gap: 6,
                  whiteSpace: 'nowrap',
                  boxShadow: isActive ? '0 2px 8px rgba(124,58,237,0.25)' : 'none',
                }}
              >
                {view.name}
                {/* Restricted badge */}
                {view.id !== 'default' && Array.isArray(view.visibleTo) && (
                  <span title={`Restricted to ${view.visibleTo.length} counselor${view.visibleTo.length !== 1 ? 's' : ''}`}
                    style={{ fontSize: 9, opacity: isActive ? 0.85 : 0.5, lineHeight: 1 }}>🔒</span>
                )}
                {/* Unsaved dot */}
                {showUnsaved && (
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: isActive ? 'rgba(255,220,80,0.95)' : '#f59e0b', display: 'inline-block', flexShrink: 0 }} title="Unsaved changes" />
                )}
                {/* Delete X — visible on hover for non-default, non-unsaved views */}
                {view.id !== 'default' && (isHov || isActive) && !showUnsaved && (
                  <span
                    onClick={e => { e.stopPropagation(); deleteView(view.id); }}
                    style={{ fontSize: 10, color: isActive ? 'rgba(255,255,255,0.65)' : '#94a3b8', cursor: 'pointer', lineHeight: 1, marginLeft: -2 }}
                    title="Delete view">✕</span>
                )}
              </button>

              {/* Save button — appears when active view has unsaved changes */}
              {showUnsaved && (
                <button
                  onClick={updateCurrentView}
                  title="Save current filters & columns to this view"
                  style={{
                    padding: '4px 11px',
                    borderRadius: 20,
                    border: '1.5px solid #7c3aed',
                    background: '#fff',
                    color: '#7c3aed',
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: 4,
                    fontSize: 11, fontWeight: 700,
                    transition: 'all 0.15s',
                    flexShrink: 0,
                    boxShadow: '0 1px 4px rgba(124,58,237,0.15)',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f5f3ff'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Save
                </button>
              )}
            </div>
          );
        })}

        {/* Divider */}
        <div style={{ width: 1, height: 18, background: 'rgba(0,0,0,0.1)', margin: '0 4px', flexShrink: 0 }} />

        {/* New View button */}
        <button onClick={() => setShowSaveView(true)}
          style={{
            padding: '5px 12px',
            borderRadius: 20,
            border: '1.5px dashed rgba(124,58,237,0.4)',
            background: 'transparent',
            color: '#7c3aed',
            fontSize: 12, fontWeight: 500,
            cursor: 'pointer', flexShrink: 0,
            display: 'flex', alignItems: 'center', gap: 5,
            transition: 'all 0.15s',
            whiteSpace: 'nowrap',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(124,58,237,0.06)'; e.currentTarget.style.borderStyle = 'solid'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderStyle = 'dashed'; }}>
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          New View
        </button>
      </div>

      {/* ── Toolbar ── */}
      <div style={{ padding: '12px 24px', borderBottom: '1px solid rgba(0,0,0,0.05)', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#475569" strokeWidth="2" style={{ position: 'absolute', left: 11, top: '50%', transform: 'translateY(-50%)' }}>
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input className="crm-input" placeholder="Search by name, phone, email…" value={search} onChange={e => setSearch(e.target.value)} style={{ paddingLeft: 34, fontSize: 13, height: 36 }} />
        </div>
        <MultiSelectDropdown
          allLabel="All Stages"
          options={pipelineStages.map(s => ({ value: s.id, label: s.name }))}
          selected={filterStatus}
          onChange={setFilterStatus}
        />
        <MultiSelectDropdown
          allLabel="All Sources"
          options={mergedSources.map(s => ({ value: s.value, label: `${SOURCE_ICONS[s.value] || ''} ${s.label}`.trim() }))}
          selected={filterSource}
          onChange={setFilterSource}
        />
        <MultiSelectDropdown
          allLabel="All Priorities"
          options={(priorities || [{ value: 'hot', label: 'Hot', emoji: '🔥' }, { value: 'warm', label: 'Warm', emoji: '☀️' }, { value: 'cold', label: 'Cold', emoji: '❄️' }]).map(p => ({ value: p.value, label: `${p.emoji || ''} ${p.label}`.trim() }))}
          selected={filterPriority}
          onChange={setFilterPriority}
        />
        <MultiSelectDropdown
          allLabel="All Counsellors"
          options={users.filter(u => u.role !== 'test_prep' && u.role !== 'digital_media').map(u => ({ value: u.id, label: u.name }))}
          selected={filterUser}
          onChange={setFilterUser}
        />
        {fbCampaigns.length > 0 && (
          <MultiSelectDropdown
            allLabel="📢 All Campaigns"
            options={fbCampaigns.map(c => ({ value: c.name, label: c.name }))}
            selected={filterCampaign}
            onChange={setFilterCampaign}
          />
        )}
        <select className="crm-select" value={sort} onChange={e => setSort(e.target.value)} style={{ height: 36, fontSize: 13 }}>
          <option value="newest">Newest First</option>
          <option value="oldest">Oldest First</option>
          <option value="score">By Score</option>
          <option value="name">By Name</option>
        </select>
        {/* Columns picker */}
        <div style={{ position: 'relative' }} ref={columnsMenuRef}>
          <button
            onClick={() => setShowColumnsMenu(v => !v)}
            className="btn-ghost"
            style={{ height: 36, padding: '0 12px', fontSize: 13, gap: 5, background: showColumnsMenu ? 'rgba(124,58,237,0.08)' : undefined, color: showColumnsMenu ? '#7c3aed' : undefined }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/></svg>
            Columns {visibleColumns.length < ALL_COLUMNS.length ? `(${visibleColumns.length}/${ALL_COLUMNS.length})` : ''}
          </button>
          {showColumnsMenu && (
            <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, background: '#fff', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 300, padding: '10px 4px', minWidth: 200, maxHeight: 420, overflowY: 'auto' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.5px', padding: '0 12px 6px', textTransform: 'uppercase' }}>
                Visible Columns
              </div>
              {/* Lead column is always visible */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', opacity: 0.45 }}>
                <input type="checkbox" checked readOnly style={{ accentColor: '#7c3aed' }} />
                <span style={{ fontSize: 12, color: '#374151' }}>Lead (always visible)</span>
              </div>
              {/* Grouped columns */}
              {['contact','pipeline','academic','activity'].map(grp => {
                const grpCols = ALL_COLUMNS.filter(c => c.group === grp);
                const grpLabels = { contact: 'Contact Info', pipeline: 'Pipeline', academic: 'Academic', activity: 'Activity' };
                return (
                  <div key={grp}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.5px', padding: '6px 12px 3px', textTransform: 'uppercase' }}>{grpLabels[grp]}</div>
                    {grpCols.map(colDef => (
                      <label key={colDef.key}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', cursor: 'pointer', borderRadius: 7, margin: '0 4px',
                          background: visibleColumns.includes(colDef.key) ? 'rgba(124,58,237,0.05)' : 'transparent' }}>
                        <input type="checkbox" checked={visibleColumns.includes(colDef.key)} onChange={() => toggleColumn(colDef.key)} style={{ accentColor: '#7c3aed' }} />
                        <span style={{ fontSize: 12.5, color: '#374151', fontWeight: visibleColumns.includes(colDef.key) ? 500 : 400 }}>{colDef.label}</span>
                      </label>
                    ))}
                  </div>
                );
              })}
              {/* Custom fields section */}
              {customFields.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', letterSpacing: '0.5px', padding: '6px 12px 3px', textTransform: 'uppercase' }}>Custom Fields</div>
                  {customFields.map(cf => {
                    const key = 'cf_' + cf.id;
                    return (
                      <label key={cf.id}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', cursor: 'pointer', borderRadius: 7, margin: '0 4px',
                          background: visibleColumns.includes(key) ? 'rgba(124,58,237,0.05)' : 'transparent' }}>
                        <input type="checkbox" checked={visibleColumns.includes(key)} onChange={() => toggleColumn(key)} style={{ accentColor: '#7c3aed' }} />
                        <span style={{ fontSize: 12.5, color: '#374151', fontWeight: visibleColumns.includes(key) ? 500 : 400 }}>{cf.label}</span>
                      </label>
                    );
                  })}
                </div>
              )}
              <div style={{ borderTop: '1px solid rgba(0,0,0,0.06)', padding: '6px 8px 2px', display: 'flex', gap: 6 }}>
                <button onClick={() => setVisibleColumns(DEFAULT_COLUMNS)} style={{ flex: 1, fontSize: 11, padding: '5px 0', borderRadius: 6, border: '1px solid rgba(0,0,0,0.1)', background: 'transparent', color: '#64748b', cursor: 'pointer' }}>
                  Reset
                </button>
                <button onClick={() => setVisibleColumns(ALL_COLUMNS.map(c => c.key))} style={{ flex: 1, fontSize: 11, padding: '5px 0', borderRadius: 6, border: '1px solid rgba(124,58,237,0.25)', background: 'rgba(124,58,237,0.06)', color: '#7c3aed', cursor: 'pointer' }}>
                  Show All
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Table / Kanban toggle */}
        <div style={{ display: 'flex', borderRadius: 8, border: '1px solid rgba(0,0,0,0.1)', overflow: 'hidden', flexShrink: 0 }}>
          <button onClick={() => setViewMode('table')}
            style={{ height: 36, padding: '0 12px', fontSize: 12, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
              background: viewMode === 'table' ? 'rgba(124,58,237,0.1)' : 'transparent',
              color: viewMode === 'table' ? '#7c3aed' : '#64748b', fontWeight: viewMode === 'table' ? 600 : 400 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/></svg>
            Table
          </button>
          <button onClick={() => setViewMode('cards')}
            style={{ height: 36, padding: '0 12px', fontSize: 12, border: 'none', borderLeft: '1px solid rgba(0,0,0,0.1)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5,
              background: viewMode === 'cards' ? 'rgba(124,58,237,0.1)' : 'transparent',
              color: viewMode === 'cards' ? '#7c3aed' : '#64748b', fontWeight: viewMode === 'cards' ? 600 : 400 }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></svg>
            Cards
          </button>
        </div>
        <button onClick={() => handleExport(selected.length > 0 ? leads.filter(l => selectedSet.has(l.id)) : filtered)}
          className="btn-ghost" style={{ height: 36, padding: '0 12px', fontSize: 13, gap: 5 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          Export CSV
        </button>
        <button onClick={() => setShowBulk(true)} className="btn-ghost" style={{ height: 36, padding: '0 12px', fontSize: 13, gap: 5 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
          Bulk Upload
        </button>
        <button className="btn-primary" onClick={() => setShowAdd(true)} style={{ height: 36, padding: '0 16px', fontSize: 13, gap: 5, flexShrink: 0 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Add Lead
        </button>
        <button onClick={handleCopyIntakeLink} title="Copy intake form link — share with new students to self-register"
          className="btn-ghost" style={{ height: 36, padding: '0 14px', fontSize: 13, gap: 5, flexShrink: 0, whiteSpace: 'nowrap' }}>
          📋 Student Form
        </button>
        {intakeLinkMsg && (
          <span style={{ fontSize: 11, fontWeight: 700, color: intakeLinkMsg.startsWith('✅') ? '#10b981' : '#ef4444', whiteSpace: 'nowrap' }}>{intakeLinkMsg}</span>
        )}
      </div>

      {/* ── Bulk action bar ── */}
      {selected.length > 0 && (
        <div style={{ padding: '8px 24px', background: 'rgba(124,58,237,0.08)', borderBottom: '1px solid rgba(124,58,237,0.18)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap', position: 'relative', zIndex: 50 }}>
          <span style={{ fontSize: 13, color: '#7c3aed', fontWeight: 700 }}>{selected.length} selected</span>
          {allPageSelected && !allViewSelected && filtered.length > pagedLeads.length && (
            <button onClick={() => {
              // Chunk the ID extraction to avoid blocking the main thread
              const ids = filtered.map(l => l.id);
              setSelected(ids);
            }}
              style={{ fontSize: 12, padding: '4px 12px', borderRadius: 6, background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.3)', color: '#7c3aed', cursor: 'pointer', fontWeight: 600 }}>
              Select all {filtered.length.toLocaleString()} in view
            </button>
          )}
          {allViewSelected && <span style={{ fontSize: 12, color: '#7c3aed', fontWeight: 500 }}>All {filtered.length.toLocaleString()} in view selected</span>}

          {/* Change Stage */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => { setShowBulkStage(v => !v); setShowBulkAssign(false); }}
              className="btn-ghost" style={{ fontSize: 12, padding: '4px 10px', gap: 4 }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="12" cy="12" r="10"/><polyline points="12 8 16 12 12 16"/><line x1="8" y1="12" x2="16" y2="12"/></svg>
              Change Stage ▾
            </button>
            {showBulkStage && (
              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, background: '#fff', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 200, padding: '6px 0', minWidth: 170, maxHeight: 240, overflowY: 'auto' }}>
                {pipelineStages.map(s => (
                  <button key={s.id} onClick={() => handleBulkChangeStage(s.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 12.5, color: '#1e293b', textAlign: 'left' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: s.color || '#94a3b8', flexShrink: 0 }} />
                    {s.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Reassign */}
          <div style={{ position: 'relative' }}>
            <button onClick={() => { setShowBulkAssign(v => !v); setShowBulkStage(false); }}
              className="btn-ghost" style={{ fontSize: 12, padding: '4px 10px', gap: 4 }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              Reassign ▾
            </button>
            {showBulkAssign && (
              <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, background: '#fff', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 200, padding: '6px 0', minWidth: 180, maxHeight: 240, overflowY: 'auto' }}>
                {users.map(u => (
                  <button key={u.id} onClick={() => handleBulkReassign(u.id)}
                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 12.5, color: '#1e293b', textAlign: 'left' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}>
                    <div className="avatar" style={{ background: u.avatar?.bg || '#7c3aed', color: '#fff', width: 20, height: 20, fontSize: 9, flexShrink: 0 }}>
                      {(u.name||'?').split(' ').map(n=>n[0]).join('').slice(0,2)}
                    </div>
                    {u.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Bulk Edit Fields */}
          <button onClick={() => { setShowBulkEdit(true); setShowBulkStage(false); setShowBulkAssign(false); }}
            className="btn-ghost" style={{ fontSize: 12, padding: '4px 10px', gap: 4 }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
            Edit Fields
          </button>

          <button onClick={() => handleExport(leads.filter(l => selectedSet.has(l.id)))}
            className="btn-ghost" style={{ fontSize: 12, padding: '4px 10px', gap: 4 }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Export CSV
          </button>
          {canDelete && (
            <button onClick={handleBulkDelete}
              style={{ fontSize: 12, padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.06)', color: '#ef4444', cursor: 'pointer', gap: 4, display: 'flex', alignItems: 'center' }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>
              Delete
            </button>
          )}
          {isAdmin && isDefaultView && (
            <span style={{ fontSize: 11, color: '#f59e0b', padding: '4px 8px', background: 'rgba(245,158,11,0.08)', borderRadius: 6, border: '1px solid rgba(245,158,11,0.2)' }}>
              Switch to a saved view to delete
            </span>
          )}
          <button onClick={() => setSelected([])} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', marginLeft: 'auto', fontSize: 12 }}>✕ Clear</button>
        </div>
      )}

      {/* ── Count bar + per page ── */}
      <div style={{ padding: '6px 24px', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, borderBottom: '1px solid rgba(0,0,0,0.04)', background: '#fafafa' }}>
        <span style={{ fontSize: 12, color: '#64748b' }}>
          {filtered.length} lead{filtered.length !== 1 ? 's' : ''}{leads.length !== filtered.length ? ` (of ${leads.length})` : ''}
        </span>
        {hasFilters && (
          <button onClick={clearFilters} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 6, border: '1px solid rgba(239,68,68,0.25)', background: 'rgba(239,68,68,0.06)', color: '#ef4444', cursor: 'pointer' }}>
            ✕ Clear filters
          </button>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>Show</span>
          <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))}
            style={{ fontSize: 12, padding: '2px 6px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.1)', background: '#fff', color: '#374151', cursor: 'pointer' }}>
            {[25, 50, 100, 250].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <span style={{ fontSize: 12, color: '#94a3b8' }}>per page</span>
        </div>
        {showSaveView && (
          <SaveViewDialog
            users={users}
            onSave={(name, visibleTo) => saveCurrentView(name, visibleTo)}
            onClose={() => setShowSaveView(false)}
          />
        )}
      </div>

      {/* ── Bulk Edit Modal ── */}
      {showBulkEdit && (
        <BulkEditModal
          count={selected.length}
          sources={mergedSources}
          priorities={priorities || [
            { value: 'hot', label: '🔥 Hot' },
            { value: 'warm', label: '☀️ Warm' },
            { value: 'cold', label: '❄️ Cold' },
          ]}
          onApply={handleBulkEditApply}
          onClose={() => setShowBulkEdit(false)}
        />
      )}

      {/* ── Main scrollable area ── */}
      <div style={{ flex: 1, overflow: 'auto', padding: '0 24px 24px' }}>

        {/* Cards view */}
        {viewMode === 'cards' && (
          <div style={{ paddingTop: 16 }}>
            {filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '80px 0' }}>
                <div style={{ fontSize: 40, marginBottom: 10 }}>&#128269;</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>No leads found</div>
                <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 16 }}>Try adjusting your filters</div>
                <button className="btn-primary" onClick={() => setShowAdd(true)}>+ Add Lead</button>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
                {pagedLeads.map(lead => {
                  const stage = pipelineStages.find(s => s.id === lead.status);
                  const user  = getUserById(lead.assignedToId) || users.find(u => u.name === lead.assignedTo);
                  const name  = getLeadName(lead);
                  return (
                    <div key={lead.id} onClick={() => setSelectedLeadId(lead.id)}
                      style={{ background: '#fff', borderRadius: 12, border: '1px solid rgba(0,0,0,0.07)', padding: 16, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', transition: 'box-shadow 0.15s' }}
                      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)'; }}
                      onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.06)'; }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                        <div style={{ width: 36, height: 36, borderRadius: 10, background: 'linear-gradient(135deg,rgba(124,58,237,0.25),rgba(59,130,246,0.25))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#a78bfa', flexShrink: 0 }}>
                          {(name || '?')[0].toUpperCase()}
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                          <div style={{ fontSize: 11, color: '#94a3b8' }}>{lead.phone || lead.email || '—'}</div>
                        </div>
                        <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 5, background: (priorityMap[lead.priority]?.color || '#94a3b8') + '20', color: priorityMap[lead.priority]?.color || '#94a3b8', fontWeight: 600, flexShrink: 0 }}>
                          {priorityMap[lead.priority] ? `${priorityMap[lead.priority].emoji} ${priorityMap[lead.priority].label}` : (lead.priority || 'warm')}
                        </span>
                      </div>
                      {stage && (
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 9px', borderRadius: 6, background: (stage.color || '#7c3aed') + '18', marginBottom: 8 }}>
                          <div style={{ width: 6, height: 6, borderRadius: '50%', background: stage.color || '#7c3aed' }} />
                          <span style={{ fontSize: 11, fontWeight: 600, color: stage.color || '#7c3aed' }}>{stage.name}</span>
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                        <span>{displayCountries(lead) !== '—' ? displayCountries(lead) : ''}</span>
                        <span>{user ? user.name : ''}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Table view */}
        {viewMode === 'table' && (
          <>
            {filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '80px 0' }}>
                <div style={{ fontSize: 40, marginBottom: 10 }}>&#128269;</div>
                <div style={{ fontSize: 15, fontWeight: 600, color: '#64748b', marginBottom: 6 }}>No leads found</div>
                <div style={{ fontSize: 13, color: '#94a3b8', marginBottom: 16 }}>Try adjusting your filters or add a new lead</div>
                <button className="btn-primary" onClick={() => setShowAdd(true)}>+ Add Lead</button>
              </div>
            ) : (
              <table className="crm-table">
                <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                  <tr>
                    <th style={{ width: 36 }}>
                      <input type="checkbox" className="crm-checkbox"
                        checked={allPageSelected}
                        ref={el => { if (el) el.indeterminate = somePageSelected; }}
                        onChange={handleHeaderCheckbox} />
                    </th>
                    {(() => {
                      const SortTh = ({ k, children, style: s }) => {
                        const active = colSort.col === k;
                        return (
                          <th onClick={() => setColSort(p => p.col === k ? { col: k, dir: p.dir === 'asc' ? 'desc' : 'asc' } : { col: k, dir: 'asc' })}
                            style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...s }}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                              {children}
                              {active ? <span style={{ fontSize: 10, color: '#7c3aed' }}>{colSort.dir === 'asc' ? '↑' : '↓'}</span>
                                      : <span style={{ fontSize: 10, color: '#cbd5e1' }}>↕</span>}
                            </span>
                          </th>
                        );
                      };
                      return (
                        <>
                          <SortTh k="firstName">Lead</SortTh>
                          {col('firstName')    && <SortTh k="firstName">First Name</SortTh>}
                          {col('lastName')     && <SortTh k="lastName">Last Name</SortTh>}
                          {col('email')        && <SortTh k="email">Email</SortTh>}
                          {col('phone')        && <SortTh k="phone">Phone</SortTh>}
                          {col('stage')        && <SortTh k="stage">Stage</SortTh>}
                          {col('source')       && <SortTh k="source">Source</SortTh>}
                          {col('countries')    && <SortTh k="countries">Countries</SortTh>}
                          {col('whatsapp')       && <th>WhatsApp</th>}
                          {col('city')           && <SortTh k="city">City</SortTh>}
                          {col('state')          && <th>State</th>}
                          {col('address')        && <th>Address</th>}
                          {col('course')         && <SortTh k="course">Course</SortTh>}
                          {col('intake')         && <SortTh k="intake">Intake</SortTh>}
                          {col('budget')         && <SortTh k="budget">Budget</SortTh>}
                          {col('qualification')  && <th>Qualification</th>}
                          {col('school')         && <th>School</th>}
                          {col('grade')          && <th>Grade</th>}
                          {col('board')          && <th>Board</th>}
                          {col('graduationYear') && <th>Grad Year</th>}
                          {col('dob')            && <th>Date of Birth</th>}
                          {col('gender')         && <th>Gender</th>}
                          {col('nationality')    && <th>Nationality</th>}
                          {col('passport')       && <th>Passport No.</th>}
                          {col('ielts')          && <th>IELTS</th>}
                          {col('toefl')          && <th>TOEFL</th>}
                          {col('pte')            && <th>PTE</th>}
                          {col('gre')            && <th>GRE</th>}
                          {col('gmat')           && <th>GMAT</th>}
                          {col('sat')            && <th>SAT</th>}
                          {col('act')            && <th>ACT</th>}
                          {col('gpa')            && <th>GPA</th>}
                          {col('parentName')     && <th>Parent Name</th>}
                          {col('parentPhone')    && <th>Parent Phone</th>}
                          {col('parentEmail')    && <th>Parent Email</th>}
                          {col('parentRelation') && <th>Parent Relation</th>}
                          {col('message')        && <th>Message</th>}
                          {col('assigned')       && <SortTh k="assigned">Assigned</SortTh>}
                          {col('score')        && <SortTh k="score">Score</SortTh>}
                          {col('priority')     && <SortTh k="priority">Priority</SortTh>}
                          {col('leadAge')      && <SortTh k="leadAge">Lead Age</SortTh>}
                          {col('lastActivity') && <th>Last Activity</th>}
                          {col('added')        && <SortTh k="added">Date Added</SortTh>}
                          {customFields.map(cf => col('cf_' + cf.id) && <th key={cf.id}>{cf.label}</th>)}
                        </>
                      );
                    })()}
                  </tr>
                </thead>
                <tbody>
                  {pagedLeads.map(lead => {
                    const user        = getUserById(lead.assignedToId) || users.find(u => u.name === lead.assignedTo) || null;
                    const score       = lead.score;
                    const scoreColor  = score >= 80 ? '#10b981' : score >= 60 ? '#f59e0b' : '#ef4444';
                    const stage       = pipelineStages.find(s => s.id === lead.status);
                    const name        = getLeadName(lead);
                    const age         = getLeadAge(lead);
                    return (
                      <tr key={lead.id}
                        onClick={() => setSelectedLeadId(lead.id)}
                        onMouseEnter={() => setHoveredRow(lead.id)}
                        onMouseLeave={() => setHoveredRow(null)}
                        style={{ cursor: 'pointer', background: hoveredRow === lead.id ? 'rgba(124,58,237,0.03)' : undefined }}>
                        <td onClick={e => e.stopPropagation()}>
                          <input type="checkbox" className="crm-checkbox" checked={selectedSet.has(lead.id)} onChange={() => toggleSelect(lead.id)} />
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg,rgba(124,58,237,0.28),rgba(59,130,246,0.28))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, color: '#a78bfa', flexShrink: 0 }}>
                              {(name || '?')[0].toUpperCase()}
                            </div>
                            <div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{name}</span>
                                {lead.source && <span style={{ fontSize: 11 }} title={lead.source}>{getSourceIcon(lead.source)}</span>}
                              </div>
                              <div style={{ fontSize: 11, color: '#94a3b8' }}>{lead.phone || lead.email || '—'}</div>
                            </div>
                          </div>
                        </td>
                        {col('firstName')    && <td style={{ fontSize: 13 }}>{lead.firstName || lead.name || '—'}</td>}
                        {col('lastName')     && <td style={{ fontSize: 13 }}>{lead.lastName || '—'}</td>}
                        {col('email')        && <td style={{ fontSize: 12, color: '#475569' }}>{lead.email || '—'}</td>}
                        {col('phone')        && <td style={{ fontSize: 12, color: '#475569' }}>{lead.phone || '—'}</td>}
                        {col('stage')        && (
                          <td>
                            {stage ? (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px', borderRadius: 6, background: (stage.color || '#7c3aed') + '18', fontSize: 11, fontWeight: 600, color: stage.color || '#7c3aed' }}>
                                <span style={{ width: 5, height: 5, borderRadius: '50%', background: stage.color || '#7c3aed', display: 'inline-block' }} />
                                {stage.name}
                              </span>
                            ) : '—'}
                          </td>
                        )}
                        {col('source') && (
                          <td style={{ fontSize: 12, cursor: 'pointer', position: 'relative' }}
                            onClick={e => { e.stopPropagation(); setInlineEdit(inlineEdit?.leadId === lead.id && inlineEdit?.field === 'source' ? null : { leadId: lead.id, field: 'source' }); }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 6px', borderRadius: 6, background: inlineEdit?.leadId === lead.id && inlineEdit?.field === 'source' ? 'rgba(124,58,237,0.08)' : 'transparent' }}>
                              {getSourceIcon(lead.source)} {lead.source || '—'}
                              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" style={{ opacity: hoveredRow === lead.id ? 1 : 0, transition: 'opacity 0.15s', flexShrink: 0 }}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            </span>
                            {inlineEdit?.leadId === lead.id && inlineEdit?.field === 'source' && (
                              <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 200, background: '#fff', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: '6px 0', minWidth: 160, maxHeight: 220, overflowY: 'auto' }}
                                onClick={e => e.stopPropagation()}>
                                {mergedSources.map(s => (
                                  <button key={s.value} onClick={() => { updateLead(lead.id, { source: s.value }); setInlineEdit(null); }}
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 12px', border: 'none', background: lead.source === s.value ? 'rgba(124,58,237,0.08)' : 'none', cursor: 'pointer', fontSize: 12.5, color: '#1e293b', textAlign: 'left', fontWeight: lead.source === s.value ? 600 : 400 }}
                                    onMouseEnter={e => { if (lead.source !== s.value) e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
                                    onMouseLeave={e => { if (lead.source !== s.value) e.currentTarget.style.background = 'none'; }}>
                                    {s.icon || SOURCE_ICONS[s.value] || '🏷️'} {s.label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </td>
                        )}
                        {col('countries') && (
                          <td style={{ fontSize: 12, color: '#475569', cursor: 'pointer', position: 'relative' }}
                            onClick={e => { e.stopPropagation(); setInlineEdit(inlineEdit?.leadId === lead.id && inlineEdit?.field === 'countries' ? null : { leadId: lead.id, field: 'countries', val: displayCountries(lead) }); }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 6px', borderRadius: 6, background: inlineEdit?.leadId === lead.id && inlineEdit?.field === 'countries' ? 'rgba(124,58,237,0.08)' : 'transparent' }}>
                              {displayCountries(lead) || '—'}
                              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" style={{ opacity: hoveredRow === lead.id ? 1 : 0, transition: 'opacity 0.15s', flexShrink: 0 }}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            </span>
                            {inlineEdit?.leadId === lead.id && inlineEdit?.field === 'countries' && (
                              <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 200, background: '#fff', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: 10, minWidth: 220 }}
                                onClick={e => e.stopPropagation()}>
                                <input autoFocus defaultValue={displayCountries(lead)}
                                  placeholder="e.g. UK, Canada, Australia"
                                  style={{ width: '100%', padding: '7px 10px', borderRadius: 7, border: '1.5px solid #e2e8f0', fontSize: 12.5, outline: 'none', boxSizing: 'border-box' }}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') { updateLead(lead.id, { countries: e.target.value }); setInlineEdit(null); }
                                    if (e.key === 'Escape') setInlineEdit(null);
                                  }}
                                />
                                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>Press Enter to save · Esc to cancel</div>
                              </div>
                            )}
                          </td>
                        )}
                        {col('whatsapp')       && <td style={{ fontSize: 12, color: '#475569' }}>{lead.whatsapp || '—'}</td>}
                        {col('city')           && <td style={{ fontSize: 12, color: '#475569' }}>{lead.city || '—'}</td>}
                        {col('state')          && <td style={{ fontSize: 12, color: '#475569' }}>{lead.state || '—'}</td>}
                        {col('address')        && <td style={{ fontSize: 12, color: '#475569', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.address || '—'}</td>}
                        {col('course') && (
                          <td style={{ fontSize: 12, color: '#475569', cursor: 'pointer', position: 'relative' }}
                            onClick={e => { e.stopPropagation(); setInlineEdit(inlineEdit?.leadId === lead.id && inlineEdit?.field === 'course' ? null : { leadId: lead.id, field: 'course' }); }}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 6px', borderRadius: 6, background: inlineEdit?.leadId === lead.id && inlineEdit?.field === 'course' ? 'rgba(124,58,237,0.08)' : 'transparent' }}>
                              {lead.course || '—'}
                              <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" style={{ opacity: hoveredRow === lead.id ? 1 : 0, transition: 'opacity 0.15s', flexShrink: 0 }}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            </span>
                            {inlineEdit?.leadId === lead.id && inlineEdit?.field === 'course' && (
                              <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 200, background: '#fff', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: 10, minWidth: 220 }}
                                onClick={e => e.stopPropagation()}>
                                <input autoFocus defaultValue={lead.course || ''}
                                  placeholder="e.g. BSc Computer Science"
                                  style={{ width: '100%', padding: '7px 10px', borderRadius: 7, border: '1.5px solid #e2e8f0', fontSize: 12.5, outline: 'none', boxSizing: 'border-box' }}
                                  onKeyDown={e => {
                                    if (e.key === 'Enter') { updateLead(lead.id, { course: e.target.value }); setInlineEdit(null); }
                                    if (e.key === 'Escape') setInlineEdit(null);
                                  }}
                                />
                                <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4 }}>Press Enter to save · Esc to cancel</div>
                              </div>
                            )}
                          </td>
                        )}
                        {col('intake')         && <td style={{ fontSize: 12, color: '#475569' }}>{lead.intake || '—'}</td>}
                        {col('budget')         && <td style={{ fontSize: 12, color: '#475569' }}>{lead.budget ? '₹' + Number(lead.budget).toLocaleString('en-IN') : '—'}</td>}
                        {col('qualification')  && <td style={{ fontSize: 12, color: '#475569' }}>{lead.qualification || '—'}</td>}
                        {col('school')         && <td style={{ fontSize: 12, color: '#475569' }}>{lead.school || '—'}</td>}
                        {col('grade')          && <td style={{ fontSize: 12, color: '#475569' }}>{lead.grade || '—'}</td>}
                        {col('board')          && <td style={{ fontSize: 12, color: '#475569' }}>{lead.board || '—'}</td>}
                        {col('graduationYear') && <td style={{ fontSize: 12, color: '#475569' }}>{lead.graduationYear || '—'}</td>}
                        {col('dob')            && <td style={{ fontSize: 12, color: '#475569' }}>{lead.dob || '—'}</td>}
                        {col('gender')         && <td style={{ fontSize: 12, color: '#475569' }}>{lead.gender || '—'}</td>}
                        {col('nationality')    && <td style={{ fontSize: 12, color: '#475569' }}>{lead.nationality || '—'}</td>}
                        {col('passport')       && <td style={{ fontSize: 12, color: '#475569' }}>{lead.passport || '—'}</td>}
                        {col('ielts')          && <td style={{ fontSize: 12, color: '#475569' }}>{lead.ielts || '—'}</td>}
                        {col('toefl')          && <td style={{ fontSize: 12, color: '#475569' }}>{lead.toefl || '—'}</td>}
                        {col('pte')            && <td style={{ fontSize: 12, color: '#475569' }}>{lead.pte || '—'}</td>}
                        {col('gre')            && <td style={{ fontSize: 12, color: '#475569' }}>{lead.gre || '—'}</td>}
                        {col('gmat')           && <td style={{ fontSize: 12, color: '#475569' }}>{lead.gmat || '—'}</td>}
                        {col('sat')            && <td style={{ fontSize: 12, color: '#475569' }}>{lead.sat || '—'}</td>}
                        {col('act')            && <td style={{ fontSize: 12, color: '#475569' }}>{lead.act || '—'}</td>}
                        {col('gpa')            && <td style={{ fontSize: 12, color: '#475569' }}>{lead.gpa || '—'}</td>}
                        {col('parentName')     && <td style={{ fontSize: 12, color: '#475569' }}>{lead.parentName || '—'}</td>}
                        {col('parentPhone')    && <td style={{ fontSize: 12, color: '#475569' }}>{lead.parentPhone || '—'}</td>}
                        {col('parentEmail')    && <td style={{ fontSize: 12, color: '#475569' }}>{lead.parentEmail || '—'}</td>}
                        {col('parentRelation') && <td style={{ fontSize: 12, color: '#475569' }}>{lead.parentRelation || '—'}</td>}
                        {col('message')        && <td style={{ fontSize: 12, color: '#475569', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{lead.message || '—'}</td>}
                        {col('assigned')     && (
                          <td>
                            {user ? (
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                <div className="avatar" style={{ background: user.avatar?.bg || '#7c3aed', color: '#fff', width: 22, height: 22, fontSize: 9 }}>
                                  {(user.name || '?').split(' ').map(n => n[0]).join('').slice(0, 2)}
                                </div>
                                <span style={{ fontSize: 12 }}>{user.name}</span>
                              </div>
                            ) : <span style={{ fontSize: 12, color: '#94a3b8' }}>Unassigned</span>}
                          </td>
                        )}
                        {col('score')        && (
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={{ width: 28, height: 5, borderRadius: 3, background: 'rgba(0,0,0,0.06)', overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: (score || 0) + '%', background: scoreColor, borderRadius: 3 }} />
                              </div>
                              <span style={{ fontSize: 12, fontWeight: 600, color: scoreColor }}>{score || 0}</span>
                            </div>
                          </td>
                        )}
                        {col('priority') && (
                          <td style={{ cursor: 'pointer', position: 'relative' }}
                            onClick={e => { e.stopPropagation(); setInlineEdit(inlineEdit?.leadId === lead.id && inlineEdit?.field === 'priority' ? null : { leadId: lead.id, field: 'priority' }); }}>
                            <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 5, background: (PRIORITY_COLORS[lead.priority] || '#94a3b8') + '20', color: PRIORITY_COLORS[lead.priority] || '#94a3b8', fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                              {lead.priority === 'hot' ? '🔥' : lead.priority === 'cold' ? '❄️' : '☀️'} {lead.priority || 'warm'}
                              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ opacity: hoveredRow === lead.id ? 0.6 : 0, transition: 'opacity 0.15s' }}><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                            </span>
                            {inlineEdit?.leadId === lead.id && inlineEdit?.field === 'priority' && (
                              <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 200, background: '#fff', border: '1px solid rgba(0,0,0,0.1)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: '6px 0', minWidth: 130 }}
                                onClick={e => e.stopPropagation()}>
                                {[{ value: 'hot', label: '🔥 Hot' }, { value: 'warm', label: '☀️ Warm' }, { value: 'cold', label: '❄️ Cold' }].map(p => (
                                  <button key={p.value} onClick={() => { updateLead(lead.id, { priority: p.value }); setInlineEdit(null); }}
                                    style={{ display: 'block', width: '100%', padding: '7px 14px', border: 'none', background: lead.priority === p.value ? 'rgba(124,58,237,0.08)' : 'none', cursor: 'pointer', fontSize: 12.5, color: '#1e293b', textAlign: 'left', fontWeight: lead.priority === p.value ? 600 : 400 }}
                                    onMouseEnter={e => { if (lead.priority !== p.value) e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
                                    onMouseLeave={e => { if (lead.priority !== p.value) e.currentTarget.style.background = 'none'; }}>
                                    {p.label}
                                  </button>
                                ))}
                              </div>
                            )}
                          </td>
                        )}
                        {col('leadAge')      && <td style={{ fontSize: 12, color: '#64748b' }}>{age !== null ? age + 'd' : '—'}</td>}
                        {col('lastActivity') && <td style={{ fontSize: 12, color: '#94a3b8' }}>—</td>}
                        {col('added')        && (
                          <td style={{ fontSize: 12, color: '#64748b', whiteSpace: 'nowrap' }}>
                            {getDate(lead.createdAt)?.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: '2-digit' }) || '—'}
                          </td>
                        )}
                        {customFields.map(cf => col('cf_' + cf.id) && (
                          <td key={cf.id} style={{ fontSize: 12, color: '#475569' }}>{lead['cf_' + cf.id] ?? '—'}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, padding: '16px 0', flexWrap: 'wrap' }}>
            <button onClick={() => setCurrentPage(1)} disabled={safePage === 1}
              style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.1)', background: 'transparent', color: safePage === 1 ? '#cbd5e1' : '#374151', cursor: safePage === 1 ? 'default' : 'pointer', fontSize: 12 }}>«</button>
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={safePage === 1}
              style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.1)', background: 'transparent', color: safePage === 1 ? '#cbd5e1' : '#374151', cursor: safePage === 1 ? 'default' : 'pointer', fontSize: 12 }}>‹</button>
            {Array.from({ length: Math.min(7, totalPages) }, (_, i) => {
              let p;
              if (totalPages <= 7) p = i + 1;
              else if (safePage <= 4) p = i + 1;
              else if (safePage >= totalPages - 3) p = totalPages - 6 + i;
              else p = safePage - 3 + i;
              return (
                <button key={p} onClick={() => setCurrentPage(p)}
                  style={{ width: 32, height: 32, borderRadius: 6, border: '1px solid ' + (safePage === p ? 'rgba(124,58,237,0.4)' : 'rgba(0,0,0,0.1)'), background: safePage === p ? 'rgba(124,58,237,0.1)' : 'transparent', color: safePage === p ? '#7c3aed' : '#374151', fontWeight: safePage === p ? 700 : 400, cursor: 'pointer', fontSize: 12 }}>
                  {p}
                </button>
              );
            })}
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages}
              style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.1)', background: 'transparent', color: safePage === totalPages ? '#cbd5e1' : '#374151', cursor: safePage === totalPages ? 'default' : 'pointer', fontSize: 12 }}>›</button>
            <button onClick={() => setCurrentPage(totalPages)} disabled={safePage === totalPages}
              style={{ padding: '5px 10px', borderRadius: 6, border: '1px solid rgba(0,0,0,0.1)', background: 'transparent', color: safePage === totalPages ? '#cbd5e1' : '#374151', cursor: safePage === totalPages ? 'default' : 'pointer', fontSize: 12 }}>»</button>
            <span style={{ fontSize: 12, color: '#64748b', marginLeft: 6 }}>Page {safePage} of {totalPages}</span>
          </div>
        )}
      </div>

      {/* Add Lead Modal */}
      {showAdd && <AddLeadModal onClose={() => setShowAdd(false)} />}

      {/* Bulk Upload Modal */}
      {showBulk && (
        <BulkUploadModal
          onClose={() => setShowBulk(false)}
          onStartImport={(rows) => { setShowBulk(false); handleStartImport(rows); }}
          customSources={customSources}
          saveCustomSources={saveCustomSources}
        />
      )}

      {/* Bulk import progress floater */}
      {bulkImport.active && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, zIndex: 9000, background: '#fff', borderRadius: 14, boxShadow: '0 8px 32px rgba(0,0,0,0.18)', border: `1px solid ${bulkImport.finished && bulkImport.errors > 0 ? 'rgba(239,68,68,0.2)' : 'rgba(124,58,237,0.2)'}`, padding: '16px 20px', minWidth: 300, maxWidth: 360 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 3 }}>
                {!bulkImport.finished && (
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: '#7c3aed', animation: 'pulse 1.2s infinite' }} />
                )}
                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                  {bulkImport.finished
                    ? (bulkImport.errors > 0 ? '⚠️ Import done with errors' : '✅ Import complete!')
                    : '⚡ Importing on server…'}
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#64748b' }}>
                {bulkImport.done.toLocaleString()} / {bulkImport.total.toLocaleString()} leads added
                {bulkImport.errors > 0 ? ` · ${bulkImport.errors} skipped` : ''}
              </div>
              {bulkImport.serverMode && !bulkImport.finished && (
                <div style={{ marginTop: 6, fontSize: 10, color: '#10b981', fontWeight: 600, background: 'rgba(16,185,129,0.08)', padding: '3px 8px', borderRadius: 20, display: 'inline-block' }}>
                  🖥️ Running on server — safe to close this tab
                </div>
              )}
            </div>
            {bulkImport.finished && (
              <button onClick={dismissBulkImport} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 18, padding: 0, lineHeight: 1, flexShrink: 0 }}>✕</button>
            )}
          </div>
          <div style={{ background: 'rgba(0,0,0,0.06)', borderRadius: 6, height: 7, overflow: 'hidden' }}>
            <div style={{
              height: '100%',
              width: (bulkImport.total > 0 ? Math.min((bulkImport.done / bulkImport.total) * 100, 100) : 0) + '%',
              background: bulkImport.finished && bulkImport.errors > 0 ? 'linear-gradient(90deg,#f59e0b,#ef4444)' : 'linear-gradient(90deg,#7c3aed,#3b82f6)',
              borderRadius: 6, transition: 'width 0.4s ease',
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 5, fontSize: 10, color: '#94a3b8' }}>
            <span>{bulkImport.total > 0 ? Math.round((bulkImport.done / bulkImport.total) * 100) : 0}%</span>
            {bulkImport.total > 0 && <span>{(bulkImport.total - bulkImport.done).toLocaleString()} remaining</span>}
          </div>
        </div>
      )}

      {/* Lead Detail Slide-over */}
      {selectedLead && (
        <>
          <div onClick={() => setSelectedLeadId(null)}
            style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(15,23,42,0.35)', backdropFilter: 'blur(2px)' }} />
          <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, zIndex: 1101, width: 'min(560px, 96vw)', background: '#fff', boxShadow: '-8px 0 40px rgba(0,0,0,0.18)', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            <LeadDetailPanel lead={selectedLead} onClose={() => setSelectedLeadId(null)} />
          </div>
        </>
      )}

    </div>
  );
}
